import type { NodeKind, TriggerSource } from './apiTypes.tsx';
import type { CredentialKind, DeployTargetKind, AgentIoRow } from './apiTypes.tsx';

/**
 * Single source of truth for every user-facing string in the console (지시21).
 * Code comments stay Korean (repo convention, 03 §0) — only rendered text lives here.
 * No i18n library: the product has exactly one locale (English) today, so a plain
 * object keeps this simple instead of speculatively supporting locale switching.
 *
 * ## ⚠ 이 파일을 고치면 **무는 자들** 〔실측 2026-09-03, 결정6443〕
 *
 * 수입자가 **206곳**이고 그중 **여섯이 gui-canvas 파일**이다. 남의 축이 문구를 더하다
 * 붉으면 까닭을 모른 채 갇힌다 — 오늘 그 부류로 커밋 열하나가 갇혔다(gui-canvas 7953·7960).
 * 그래서 여기 적는다:
 *
 * ```
 * 문구를 **더했는데 화면이 안 쓰면**      `lib/everyWordingReachesAScreen` 이 문다
 *   → 배선과 **같은 회차**에 넣어라. 먼저 넣으면 붉는다
 * 문구에 **한글이 들어가면**              `i18nResidualKorean` 이 문다(주석은 한글이 맞다)
 * 문구를 **고치거나 이름을 바꾸면**        검사 **132개**가 이 파일을 참조한다 —
 *   그중 여덟은 **원문을 읽어** 규칙으로 잰다(`statusReportMarker`·`sectionHomes` 등)
 * ```
 * ▶ 이 파일은 **gui 소유**다. 문구가 필요하면 옮기지 말고 **gui 에 브리프로 청하라** —
 *   장부가 두 벌이 되면 한쪽이 낡는다(결정595 「정본과 사본」).
 */
export const strings = {
  common: {
    loading: 'Loading…',
    /**
     * ★★ **고를 수 없는 자격증명에 붙는 말** (gui 결정9447).
     *
     * 〔도는 판 실측 2026-09-07 KST〕 자격증명 **26행** 중 `verdict.usable === false` 가 **9행**인데,
     * 고르개 **넷**이 그것을 **그대로 내밀고** 있었다. 고르면 쓰는 순간 실패한다.
     *
     * ⚠ **감추지 않고 잠근다.** 감추면 *«내가 등록한 자격증명이 왜 안 보이나»* 가 되고,
     *   그건 「가려진 것은 없는 것이다」를 내가 만드는 일이다
     *   (`ProvisionNodeAgentDialog` 이 결정546 에서 같은 손을 골랐다).
     * ⚠ **까닭은 안 적는다** — 폐기인지 만료인지 상류 문제인지는 **서버 판정**이고,
     *   여기서 뭉개면 그럴듯하게 틀린 말이 된다. 사실 하나만 말한다.
     *   (같은 손: `modelCostCapBlind` 옆의 «cannot be called now».)
     */
    credentialUnusable: 'cannot be used now',
    /**
     * **이 쓰임에는 안 맞는 종류** (지시162, 2026-09-09).
     *
     * ⚠ 위 `credentialUnusable` 과 **다른 말**이다. 저쪽은 «지금 못 쓴다»(폐기·만료)이고
     *   이쪽은 «쓸 수는 있는데 **여기 것이 아니다**» 다. 뭉치면 사람이 멀쩡한 자격을 버린다.
     * 🔴 오늘 실물로 그 차이가 났다: 목록이 폐기된 것만 흐리게 하고 `ssh_password` 는
     *   멀쩡히 고르게 둬서, 운영자가 그것을 골랐고 노드에서 `curl 401` 로 죽었다.
     * ⚠ 여기서는 **짧게만** 말한다 — 긴 사유는 서버가 낸다(`credentialKindRejection`).
     *   `<option>` 한 줄에 문단을 넣으면 아무도 안 읽는다.
     */
    /**
     * **재지 않았다** — `0` 이 아니다 (결정488·490).
     *
     * 두 화면이 같은 사실을 말한다: 세션의 토큰·비용(값이 안 온다)과 실행의 비용
     * (`usageUnknown` 이 실린다). **한 곳에 둔다** — 두 벌로 적으면 같은 것을 두
     * 낱말로 부르게 되고, 사람은 그 둘이 다른 뜻인 줄 안다.
     *
     * `0` 은 *"재서 0 이었다"* 이고 이것은 *"재지 않았다"* 다. 온프렘 실행은 진짜로
     * 0 원이라(결정194) 그 둘을 뭉치면 어느 쪽으로든 거짓말이 된다.
     */
    notMeasured: 'not measured',
    /**
     * ★ **세션은 «부재» 밖에 못 본다 — 그래서 «아직 안 쟀다» 고 말하지 않는다** (결정5471).
     *
     * 런은 서버 판정을 읽는다(`usageUnknown`) — 그때는 «아직 안 쟀다» 가 **사실**이다.
     * 세션에는 그 판정이 없다〔실측: 계약 `PaseoInspect` 에 `tokens`·`costUsd` 가
     * **선택**이고 «못 쟀다» 를 말하는 칸이 없다〕. 게다가 클라이언트는
     * **숫자가 아니면 그 칸을 뺀다**〔`integrations/paseo/http-client.ts`〕.
     *
     * 즉 부재는 셋을 덮는다:
     * ```
     * 데몬이 아직 안 쟀다      ← «아직 안 쟀다» 가 맞는 경우
     * 숫자가 아닌 것이 왔다     ← 이상이 있는 경우
     * 칸 이름이 바뀌었다        ← 계약이 갈린 경우
     * ```
     * **화면이 그중 하나를 골라 말하면 나머지 둘에서 거짓이 된다.**
     * 그래서 아는 것만 말한다 — «사용량이 안 왔다».
     *
     * ⚠ 런 쪽 문구는 **안 바꿨다.** 그쪽은 판정을 읽으므로 «아직 안 쟀다» 가 참이다.
     * 낱말이 갈리는 것이 이 경우엔 옳다 — **근거가 다르면 말도 달라야 한다.**
     */
    noUsageReported: 'No usage reported',
  },

  nav: {
    pipelines: '① Pipeline Builder',
    runs: '② Runs',
    agentIo: '③ Agent I/O',
    approvals: '④ Approvals',
    fleet: '⑤ Fleet',
    sessions: '⑥ Sessions',
    settings: '⑦ Settings',
    resources: '⑧ Resources',
    access: '⑨ Access',
    /** 지시160 — 도는 사슬을 컨베이어 벨트처럼 보는 자리. */
    operator: '⑩ Pipeline Operator',
    /** 지시188 — 로그인 뒤 첫 화면. Builder·Operator 의 기술 통계. */
    dashboard: '⑪ Dashboard',
    /** 지시220 — 플릿의 데이터베이스를 들여다보는 자리(읽기만). */
    databases: '⑫ Database',
  },

  /**
   * **Dashboard — Pipeline Builder·Operator 의 기술 통계** (지시188).
   * 날은 KST 달력일이다 — 축과 표에 그 이름표를 단다(시각을 적는 자리는 시계를 말한다).
   */
  dashboard: {
    heading: 'Dashboard',
    lede: 'How the pipelines have been doing. Counted on the server, by calendar day in KST.',
    windowLabel: 'Window',
    windowOption: (days: number) => `Last ${days} days`,
    includeSelfCheck: 'Include self-checks',
    /** 창 길이·시작·날을 가르는 시계를 **서버가 준 값으로** 적는다 — 버튼 상태로 짐작하지 않는다. */
    since: (days: number, when: string, zone: string) => `${days} days since ${when} (${zone}).`,
    selfCheckExcluded: (names: string) => `Self-check runs are left out (${names}).`,
    selfCheckNone: 'Self-check runs are included.',
    loading: 'Loading the numbers…',
    unreadable: 'Could not read the dashboard numbers — this does not mean there are none. ',
    emptyWindow: 'No runs in this window.',
    axisDay: 'Day (KST)',
    showTable: 'Show as table',
    total: 'Total',
    noValue: '—',
    point: (day: string, parts: string) => `${day} KST: ${parts}`,
    operator: {
      heading: 'Pipeline Operator',
      runs: 'Runs',
      successRate: 'Success rate',
      successRateSub: (success: number, failed: number) => `${success} succeeded · ${failed} failed`,
      medianDuration: 'Median duration',
      durationSub: (p95: string, measured: number) => `p95 ${p95} · from ${measured} timed runs`,
      tokens: 'Tokens',
      tokensSub: (input: string, output: string) => `${input} in · ${output} out`,
      cost: 'Cost',
      usageUnknown: (n: number) => `${n} runs did not report usage — totals may be short`,
      runsPerDay: 'Runs per day',
      durationPerDay: 'Median duration per day',
      tokensPerDay: 'Tokens per day',
      busiest: 'Busiest pipelines',
    },
    builder: {
      heading: 'Pipeline Builder',
      pipelines: 'Pipelines',
      pipelinesSub: (generated: number, code: number) => `${generated} generated · ${code} ejected to code`,
      published: 'Published',
      versionsAll: 'Saved versions',
      versionsInWindow: 'Saved in window',
      versionsPerDay: 'Versions saved per day',
      mostRevised: 'Most revised pipelines',
    },
    series: {
      success: 'Succeeded',
      other: 'Other',
      failed: 'Failed',
      seconds: 'Median',
      tokens: 'Tokens',
      versions: 'Versions',
    },
    table: {
      day: 'Day (KST)',
      pipeline: 'Pipeline',
      runs: 'Runs',
      success: 'Succeeded',
      failed: 'Failed',
      median: 'Median',
      lastRun: 'Last run',
      source: 'Source',
      versions: 'Versions',
      published: 'Published',
      lastSaved: 'Last saved',
      yes: 'yes',
      no: 'no',
    },
  },

  /**
   * **Pipeline Operator — 도는 사슬을 컨베이어 벨트처럼** (지시160).
   *
   * 🔴 운영자가 요구한 다섯: ①지금 구동 중인 사슬 ②들어오는 장치 ③나가는 장치
   *   ④메시지 큐(누르면 대기 중인 run) ⑤도는 run 을 모듈 상자 안에서.
   * ⚠ **자국이 이미 다 있다** — `inputReceivedAt`(들어옴) · `queued`/`running`/
   *   `needs_approval` · `endedAt`(나감) · 큐 보고. **새로 재는 자를 짓지 않는다.**
   */
  operator: {
    heading: 'Pipeline Operator',
    lede: 'What is moving right now — one small board per pipeline. Finished work lives in Runs.',
    /*
     * 지시190 — 칸은 **상태**로 가른다. 한 런은 한 칸에만 있다(종전 «Came in» 은 들어온 자국이 있는 것
     * 전부를 실어 «Went out» 과 같은 런이 두 번 나왔다). 들어온 곳·기다린 시간은 **카드의 글**로 옮겼다.
     */
    columns: {
      queued: 'Waiting to start',
      working: 'Running',
      approval: 'Waiting for a person',
      out: 'Went out',
    },
    columnLabel: (pipeline: string, column: string) => `${pipeline}: ${column}`,
    emptyColumn: 'None',
    boardSummary: (moving: number, recent: number) => `${moving} moving · ${recent} recent`,
    more: (n: number) => `+${n} more in Runs`,
    window: (n: number) => `Boards are built from the last ${n} runs, with self-checks left out.`,
    noRuns: 'Nothing has moved recently.',
    statusWord: (status: string) => (status === 'success' ? 'succeeded' : status),
    waited: (seconds: number) => `waited ${seconds}s before starting`,
    openRun: (runId: string) => `Open run ${runId}`,
    cameFrom: (source: string) => `from ${source}`,
    wentTo: (channel: string) => `to ${channel}`,
    atStep: (step: string, node: string) => `at ${step} on ${node}`,
    atStepUnknownNode: (step: string) => `at ${step}`,
    stepsUnreadable: 'Could not read the steps of this run — this does not mean it has none. ',
    queuesTitle: 'Message queues',
    queuesNone: 'No queue has anything in it.',
    /* 지시183 — 아직 안 온 것을 «0» 이나 «비었다» 로 그리지 않는다. */
    boardsLoading: 'Loading the pipeline boards…',
    /** 지시195 — 첫 진입 대기. 셋은 따로 오므로 따로 말하고, 얼마나 기다렸는지 센다. */
    loading: {
      part: (what: string, state: string) => `${what} — ${state}`,
      runs: 'Recent runs',
      ops: 'How each pipeline runs',
      queues: 'Message queues',
      reading: 'reading…',
      ready: 'ready',
      /* ⚠ 까닭을 싣는다(결정4462) — 권한·프록시·서버는 할 일이 서로 다르다. */
      failed: (why: string) => `could not read (${why})`,
      elapsed: (seconds: number) => `Waiting ${seconds}s so far.`,
    },
    queuesLoading: 'Reading the queues…',
    queuesUnreadable: 'Could not read the queues — this does not mean they are empty. ',
    queueLine: (queue: string, waiting: number, active: number) =>
      `${queue} — ${waiting} waiting, ${active} active`,
    queuedRunsCaveat:
      'These are the runs waiting to start. The platform does not record which queue a run is waiting on, so this list is not filtered by the queue you picked.',
    runsUnreadable: 'Could not read the runs — this does not mean nothing is moving. ',
    /*
     * 지시191 — 보드마다 운영 사항. ⚠ «언제부터 켜져 있나» 는 **기록이 없다**(노드 신호에 uptime 이 없다) —
     * 그래서 마지막 신호만 보이고, 그렇다고 화면에 한 줄 적는다. 못 잰 GPU 는 노드가 보낸 까닭을 그대로 싣는다.
     */
    ops: {
      loading: 'Reading how this pipeline runs…',
      unreadable: 'Could not read how the pipelines run — this does not mean there is nothing to show. ',
      noStats: (days: number) => `No runs in the last ${days} days.`,
      uptimeNote: 'Uptime is not recorded, so each server shows its last signal instead.',
      runs: (days: number) => `Runs (${days} days)`,
      successRate: 'Success',
      duration: 'Median · p95',
      lastSuccess: 'Last success',
      lastFailure: 'Last failure',
      live: 'Live version',
      liveValue: (version: string, savedAt: string) => `${version} · saved ${savedAt}`,
      notPublished: 'not published',
      codeOnly: 'code pipeline',
      none: '—',
      unknown: 'unknown',
      ranOn: 'Ran on',
      notInFleet: 'not in the fleet',
      heartbeat: (age: string) => `signal ${age}`,
      noHeartbeat: 'no signal yet',
      gpuSpec: (model: string, count: number, vramGb: number | null) =>
        `${model} ×${count}${vramGb === null ? '' : ` · ${vramGb} GB`}`,
      noGpu: 'no GPU registered',
      gpuUse: (util: string, age: string) => `GPU use ${util} (${age})`,
      gpuUnmeasured: (reason: string) => `GPU use not measured: ${reason}`,
      steps: (n: number) => `${n} steps`,
      model: 'Model',
      modelServer: (model: string, node: string) => `${model} on ${node}`,
      modelServerUnknown: (model: string) => `${model} (model server not declared)`,
    },
  },


  layout: {
    brand: 'summary.ai',
    product: 'Zero-Touch Robot Orchestration',
    loggedInAs: (ownerId: string) => `Logged in as ${ownerId}`,
    logout: 'Logout',
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
  },

  app: {
    loading: 'Loading…',
  },

  main: {
    rootElementNotFound: '#root element not found',
  },

  errorBoundary: {
    /* 오류 경계 (결정2403). 짧게 — 아래 원문이 진짜 내용이다. */
    title: 'This screen failed to render',
    hint: 'The console kept running. Reload to try again; the detail below is the raw error.',
    reload: 'Reload',
  },

  authStore: {
    useAuthOutsideProvider: 'useAuth must be used within an AuthProvider',
  },

  login: {
    subtitle: 'Sign in to continue.',
    /*
     * 세션이 도중에 끊겨 쫓겨났을 때만 (결정2406). ⚠ **사과하거나 오류라고 하지 않는다** —
     * 만료는 정상 동작이다. 사람이 알아야 할 것은 *"왜 여기 와 있나"* 와
     * *"하던 일이 저장 안 됐을 수 있다"* 둘뿐이다.
     */
    sessionExpired: 'Your session ended. Sign in again — unsaved changes may have been lost.',
    username: 'Username',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    errors: {
      badCredentials: 'Incorrect username or password.',
      tooManyAttempts: 'Too many attempts. Please try again shortly.',
      serverError: (status: number) => `Login failed due to a server error (HTTP ${status}).`,
      generic: (status: number) => `Login failed (HTTP ${status}).`,
      network: 'Could not connect to the server. Check the address and your network connection.',
    },
  },

  /**
   * 지시54 — 권한으로 잠긴 자리.
   *
   * **감추는 것은 편의이지 보안이 아니다.** 실제 차단은 서버가 한다. 문구는 그래서
   * "못 한다" 가 아니라 **"네가 못 한다, 관리자에게 요청하라"** 로 적는다 — 기능이
   * 없는 것과 내게 권한이 없는 것은 다르다.
   */
  permissions: {
    noPermission: 'Your account cannot do this. Ask an organisation admin for the permission.',
    lockedTitle: 'Not permitted',
  },

  /**
   * Error-code → English label map for describeError.tsx.
   *
   * The server (out of this task's scope) returns `{code, message}` and `message` is
   * still Korean in places (e.g. dangling-ref graph validation text, 결정35). We can't
   * translate content we don't own, so we don't touch `message` — but the console *can*
   * own its own label for the well-known `code` values from the contract
   * (validation_error · unauthorized · not_found · …), and prefix the raw message with
   * that English label. Unknown codes still get a readable label (code text
   * humanized) instead of being dropped, and codeless errors fall back to the raw
   * message untouched — we never discard the only diagnostic text a user has.
   */
  errors: {
    codeLabels: {
      validation_error: 'Validation error',
      unauthorized: 'Unauthorized',
      not_found: 'Not found',
      forbidden: 'Forbidden',
      invalid_credentials: 'Invalid credentials',
      conflict: 'Conflict',
      // 서버가 CREDENTIALS_KEY 없이 비밀을 저장하기를 거부할 때(503). 서버 message 가
      // 무엇을 채워야 하는지까지 말해 주므로 여기서는 라벨만 사람 말로 바꾼다.
      credentials_key_missing: 'Server cannot store secrets',
      credential_store_failed: 'Could not store the credential',
    } as Record<string, string>,
  },

  statusBadge: {
    /*
     * ★ **기계 낱말로 읽히는 상태만 사람 말로 옮긴다** (결정5421).
     *
     * 〔글로 읽어 알았다〕 세션 목록이 이렇게 읽혔다:
     * *"impl-gui · idle … paseo-2 · **waiting_permission** … cli-launched · running"*
     * `running`·`idle` 은 그냥 낱말인데 밑줄 든 것만 **기계가 뱉은 것**처럼 튄다.
     *
     * ⚠ **다 옮기지 않는다.** `running`·`queued` 같은 것은 이미 사람 말이고,
     * 옮기면 서버 낱말과 화면 낱말이 갈려 로그와 못 잇는다. **밑줄 든 것만** 옮긴다.
     * ⚠ `Partial` 이고 **모르는 값은 그대로** 낸다 — 서버가 상태를 늘려도 빈칸이
     * 안 되고(지시107), 이 표를 안 고쳐도 화면이 산다.
     * ⚠ 옮긴 자리에는 **서버 낱말을 툴팁으로** 남긴다 — 로그·CLI 에서 그 낱말로 찾는다
     * (결정5416 함대 표에서 같은 판단을 했다).
     */
    label: {
      needs_approval: 'needs approval',
      waiting_permission: 'waiting for permission',
      rolled_back: 'rolled back',
      not_installable: 'cannot install',
      needs_start: 'needs start',
      daemon_detected: 'daemon detected',
      not_measured: 'not measured',
      /*
       * ★ **«도는 중» 이 아니라 «여기서 죽었다»** (env 결정3712·3721 → gui 결정5423).
       *
       * env 가 알려 줬다 — 이 상태가 목록에 보이는 경우는 사실상 하나다:
       * **실행이 그 단계에서 끊겼다.** 정상 흐름에서는 같은 행이 곧 닫힌다.
       * 그래서 «진행 중…» 으로 달면 **거짓**이 된다 — 아무도 안 돌고 있다.
       *
       * ⚠ env 가 이름을 `running` 에서 이것으로 **바꿔 줬다.** 그대로 뒀으면 내
       * `StatusBadge` 의 `running: 'success'` 에 걸려 **죽은 단계가 초록**이 될 뻔했다.
       * 남의 축이 내 표를 읽고 막아 준 자리다.
       */
      /*
       * ⚠ **«아무도 안 돌고 있다» 고 단정하던 것을 물린다** (지시182 ㉡, 실측 2026-09-10).
       *   이 상태가 나는 갈래가 하나 더 생겼다: **결과를 못 받은 것**(연결이 죽어 종료
       *   코드를 못 받았다). 그때 그 명령은 **그 기계에서 끝났을 수도 있다** — 실물이
       *   그랬다(`tailscale logout` 이 실제로 됐는데 우리는 못 받았다).
       *   그러니 «아무것도 안 돌고 있다» 는 우리가 할 수 없는 말이다. 걸음의 `output`
       *   이 어느 갈래인지 말하므로, 배지는 **더 말하지 않는다.**
       */
      unfinished: 'stopped here — the result never came back',
    } as Record<string, string>,
    // Toolbar 전용 drift·eject 배지 문구 — 그 외 상태값(running·failed 등)은 서버 상태값을 그대로 보여줌.
    cleanGuiOwned: 'Clean · GUI-owned',
    handEditDrift: 'Hand-edit drift',
    codeOwnedEjected: 'Code-owned (ejected)',
    /**
     * 아직 한 번도 발행되지 않은 초안(지시33 §1). 만들자마자 이 상태이며,
     * 이걸 보여 주지 않으면 사용자는 **왜 실행이 안 되는지** 알 수 없다.
     */
    draftNotPublished: 'Draft · not published',
    /*
     * **발행했다고 도는 것은 아니다** (결정1400).
     *
     * `Clean · GUI-owned` 는 *"구운 것과 그래프가 같다"* 는 뜻인데, 사람은 그것을
     * *"지금 이게 돈다"* 로 읽는다. 오늘 실제로 그렇게 읽혀서 시간을 썼다 —
     * 발행은 됐고 `drift` 도 clean 인데 **엔진은 8시간 전 판을 들고 있었다.**
     *
     * 그래서 **확실할 때만** 한 줄을 더 보여 준다. 문장을 *"안 돈다"* 가 아니라
     * *"도는 판에 아직 없다"* 로 적는 이유는, 우리가 아는 것이 딱 거기까지이기
     * 때문이다(`runningBuild.tsx` 참고).
     */
    notInRunningBuild: 'Published — but the running server was built before this version',
    notInRunningBuildHint:
      'Publishing generates the pipeline source. The running server still has an older build, so it does not have this version yet — it needs a rebuild and restart.',
  },

  nodeKindLabel: {
    trigger: 'Trigger',
    agent: 'Agent',
    transform: 'Transform',
    branch: 'Branch',
    output: 'Output',
    tool: 'Tool',
    datasource: 'Datasource',
    docker: 'Container',
    observation: 'Observation',
    ollama: 'Ollama',
  } satisfies Record<NodeKind, string>,

  /**
   * 종류 이름만으로는 처음 쓰는 사람이 무엇을 고르는지 알 수 없다(지시22 §7).
   * 팔레트 버튼·우클릭 메뉴 항목의 툴팁으로 그대로 쓴다.
   */
  nodeKindHint: {
    trigger: 'Starts the pipeline when something happens outside it — an inbound email, a Slack message, a schedule, a webhook.',
    agent: 'Calls an LLM with a prompt (or a registered agent definition) and passes its answer downstream.',
    transform: 'Reshapes data with a plain expression — no LLM call, no cost, deterministic.',
    branch: 'Sends the run down one path or another depending on a condition you write.',
    output: 'The end of a path — delivers the result somewhere (a Slack channel, a file, an API).',
    tool: 'Calls one tool exposed by a registered MCP server — real side effects (create an issue, send a message).',
    datasource: 'Reads rows from, or writes rows to, a registered connection (Postgres, HTTP API, or run history).',
    /*
     * 지시59 — 이것만 **담는 것**이고 나머지는 도는 것이다. 툴팁이 그 차이를 먼저
     * 말해야 사용자가 안에 무엇을 넣는지 알 수 있다.
     */
    /*
     * 지시56 §1 — **실행되지 않는다.** 그것이 이 종류의 전부라 힌트가 먼저 말한다.
     * 이 꼭짓점이 하나라도 있으면 그래프 발행이 거부된다(계약으로, `checkObservation`).
     */
    observation:
      'A window onto a live Paseo session — for watching, not running. A graph containing one cannot be published, because a live session\u2019s prompt changes every turn and cannot be compiled.',
    /* Docker 와 형제 (지시66). 담는 것이라 같은 어법으로 말한다. */
    ollama:
      'A local model server that other steps run against. Put it on a compute node, then drop steps into it — they call this server instead of a cloud provider.',
    docker: 'A container that other steps run inside. Put it on a compute node, then drop steps into it — they run in the image you picked instead of on the host.',
  } satisfies Record<NodeKind, string>,

  triggerSourceLabel: {
    imap: 'Email (IMAP)',
    slack: 'Slack',
    cron: 'Schedule (cron)',
    github: 'GitHub',
    api: 'API call',
    webhook: 'Webhook',
  } satisfies Record<TriggerSource, string>,

  agentIoRoleLabel: {
    prompt: 'Prompt',
    response: 'Response',
    tool: 'Tool call',
  } satisfies Record<AgentIoRow['role'], string>,

  graphNodeCard: {
    fanIn: (count: number) => `${count} upstream merged`,
    startCap: 'Start — no input',
    endCap: 'End — no output',
    needsConfiguration: 'Needs configuration',
    /*
     * ★ **이 꼭짓점은 등록 정의를 안 쓴다 — 설정이 그 자리에 적혀 있다**
     * (지시96 ④ · gui-canvas 요청 · 출처 llm 실측).
     *
     * 〔실측〕 데모의 `slack-echo` 는 LLM 칸이 **등록 정의를 안 쓴다** — `ref` 가 정의
     * 이름이 아니라 **프롬프트 글자 그대로**다. 카드는 그 둘을 **같은 낯으로** 그려서
     * 사람이 «등록해 둔 그것이 도는 중» 이라고 짐작한다. `llm` 이 오늘 그렇게 헛짚었다.
     *
     * 그 짐작이 실제로 무는 자리가 있다: 그 인라인 프롬프트에는 «한국어로» 가 없어
     * **한자 24/24** 가 나왔다〔llm 실측 n=24〕. 등록 정의를 봤다면 안 나올 결론이다.
     *
     * ⚠ **«틀렸다» 가 아니다.** 인라인도 정상 경로다. 가르는 것은 옳고 그름이 아니라
     * **«장부에 있는 그것인가»** 이고, 색조도 경고가 아니라 사실 표시여야 한다.
     * ⚠ 카드가 좁다 — **한 줄을 넘기면 안 들어간다.**
     */
    inlineConfig: 'inline — not a registered definition',
    toolSubtitle: (ref: string) => `Tool: ${ref}`,
    /**
     * **부제가 제목을 되풀이하면 아무 말도 안 한 것이다** (결정3422).
     *
     * 〔실측 08-30, 실물 브라우저〕 출력 꼭짓점 카드가 **«n3 / n3»** 이라고 떴다.
     * 제목도 부제도 `data.ref` 인데, 트리거·출력은 `ref` 가 없어 `GraphCanvas` 가
     * **노드 id 를 채워 넘기기** 때문이다. 운영자에게 그 카드는 «n3» 두 번이고,
     * **그것이 무엇인지는 한 글자도 안 말한다** — 지시101 이 짚은 부류 그대로다.
     *
     * 종류 단서(`vertexKindLabel`)는 **등록 정의**를 찾는 것이라 이 자리를 못 메운다.
     * 그래서 꼭짓점 종류의 사람 이름을 여기 둔다 — 카드가 id 를 되풀이하는 대신
     * «무엇인가» 를 말한다.
     */
    vertexKindName: (kind: string): string | undefined =>
      ({
        output: 'Output',
        branch: 'Branch',
        transform: 'Transform',
      })[kind],
    datasourceModeRead: 'read',
    datasourceModeWrite: 'write',
  },

  pipelineBuilder: {
    /* 지시184 — 머리 줄 두 칸의 보이는 이름. */
    selectPipelineLabel: 'Pipeline',
    newPipelineNameLabel: 'New pipeline',
    /**
     * ⚠ **목록을 못 읽은 것과 파이프라인이 없는 것은 다르다** (결정3473).
     *
     * 고르는 상자는 실패하면 **자리표시자만** 남는다 — «고를 것이 없다» 와 똑같이 보이고,
     * 데모 중이면 사람은 *"파이프라인이 아직 없구나"* 로 읽는다. 사실은 못 읽은 것이고
     * **할 일이 다르다**(다시 부르거나 로그인을 본다). 결정1401 과 같은 잣대다.
     */
    graphsLoadError: 'Could not load the pipeline list',
    selectPipeline: 'Select a pipeline',
    selectPipelineHint: 'Which saved pipeline this canvas is editing. Switching discards nothing — unsaved edits stay until you leave the page.',
    newPipelineNamePlaceholder: 'New pipeline name',
    newPipelineNameHint: 'Creates an empty pipeline with this name. The name is the identifier used to run it, so keep it short and stable.',
    /**
     * **여러 절이 함께 쓰는 «못 읽었다»** (gui 결정9434).
     *
     * ⚠ 종전에는 `inspector.loadFailed` 였고 **세 자리가 그 절에서 빌려 썼다**
     * (인스펙터 `editors/shared` · 기계 상자 · 캔버스 카드 — 그쪽이 스스로 신고했다).
     * 문장은 «Could not load: …» 로 **온전히 일반**이다 — 인스펙터 이야기가 없다.
     * ```
     * ㈎ 그대로 둔다        ✗ 한 열쇠에 **주인이 셋.** 그 절을 고치면 남의 화면이 조용히 바뀐다
     * ㈏ 절마다 제 열쇠     ✗ **같은 글자가 셋** — 하나만 고쳐지는 부류가 된다(결정5456 의 그 병)
     * ㈐ **한 칸 위로**     ✔ 아무도 남의 절을 안 빌리고, 글자는 하나다
     * ```
     * ★ 가르는 물음: **«이 문장을 고칠 까닭이 생기면 세 자리가 다 같이 바뀌어야 하나»**.
     *   그렇다 → 한 열쇠(여기). 아니다 → 절마다 제 열쇠(축 낱말이 그 경우였다 — 결정9430).
     */
    loadFailed: (message?: string) => `Could not load${message ? `: ${message}` : ''}`,
    /**
     * **두 출처가 서로 다른 말을 할 때 — 그 사이를 비워 두지 않는다** (gui-canvas 청 → gui 결정9435).
     *
     * 〔gui-canvas 실측 2026-09-07 KST, 상태를 걸고 읽었다〕 한 카드가 이렇게 떴다:
     * ```
     * "running now · qwen2.5:7b · **Inactive** · ⇢ lab-4090-1 · **running** · Ran on lab-4090-1"
     *   함대 줄  `status: 'offline'`      → «Inactive»
     *   실행 줄  단계 `status: 'running'` → «running»
     * ```
     * **낱말은 둘 다 제 출처에서 옳다.** 그런데 이어 읽으면 사람은 **«화면이 깨졌다» 로 읽는다** —
     * 그리고 그게 제일 비싼 상태다(자기가 본 것을 못 믿게 된다).
     *
     * ## 갈래 셋 중 왜 이것인가
     * ```
     * ㈎ 실행을 믿는다(회색을 걷는다)  ✗ **화면이 판정을 새로 만든다.** «함대 줄이 낡았다» 는
     *                                   판정이고 서버가 그 답을 낸 적이 없다(결정423)
     * ㈐ 그대로 둔다                    ✗ 두 사실 사이가 비어 있으면 사람이 화면을 못 믿는다
     * ㈏ **긴장을 적는다**              ✔ 이 축이 있는 까닭 그대로 — «어긋나면 화면이 말한다»
     * ```
     *
     * ## 이 문장이 지키는 것 셋
     * ```
     * ㉠ **누가 틀렸다고 안 한다** — 둘 다 제 출처에서 참이다. 화면은 **둘을 나란히 놓을 뿐**
     * ㉡ **경고가 아니다** — 할 일이 **없을 수도** 있다(보고가 곧 따라잡는다). 붉히면 사람이
     *    멀쩡한 실행을 멈춘다
     * ㉢ **사람에게 시키지 않는다** — 무엇을 해야 하는지 우리가 모른다
     * ```
     * ⚠ **한 칸 위에 둔다**: 같은 물음을 기계 상자도 한다(결정9434 의 그 손).
     * ⚠ 얼마나 자주 나는지 **아직 못 쟀다** — 그리고 **아무 0 이나 쓰면 안 된다** 〔결정9436〕.
     * ```
     * gui-canvas 의 `[값만 본다]`  = **0**  ← 하지만 **목 판에 그 상태가 아예 없다**.
     *                                 곧 «안 났다» 가 아니라 **«이 판에 그 짝이 없다»** 다
     * 쓸 수 있는 0                  = **실행 자료를 든 축**(engine·env)의
     *                                 «offline 로 보고된 노드에서 `running` 인 단계» 수
     * ```
     * ▶ 처음에 여기 *«0 으로 나오면 이 문구를 지우는 것이 답»* 이라고만 적었다. 그쪽이
     *   **그 0 은 그 판단의 재료가 못 된다**고 되짚어 줬다 — 「세어서 0 에도 세 뜻이 있다」.
     *   **지우려면 «그 짝이 날 수 있는 판» 에서 잰 0 이어야 한다.**
     * ⚠ 그때까지 이 문구를 **먼저 두는 것은 해가 없다**: 위 ㉡㉢ 대로 경고도 아니고 시키지도
     *   않으므로, 드물게 뜨면 사실 한 줄이 늘 뿐이다.
     */
    reportedOfflineButRunning: 'Reported offline, but this step is running there.',
    create: 'Create',
    pickOrCreate: 'Select a pipeline above or create a new one.',
    loading: 'Loading…',
    loadError: 'Could not load graph',
    /*
     * ★ **«decision 3» 은 사용자 말이 아니다** (지시101 ④).
     *
     * 종전 문구가 *"… or eject (decision 3)."* 였다. **내부 결정 번호가 화면으로 샜다** —
     * 사용자는 그 번호로 아무것도 못 찾는다. 그리고 무엇이 어긋났는지 안 보여 준 채
     * 고르라고만 해서, 운영자 화면에서 **아무도 안 고른 채 배너가 상주했다.**
     *
     * 여기서 고칠 수 있는 것은 **문구뿐**이다: 무엇이 일어났는지·안 고르면 어떻게
     * 되는지를 말하고, 내부 번호를 뺀다. ⚠ **«무엇이 어긋났는지 보여 주는 것»** 과
     * **배너가 상주하지 않게 하는 것**은 gui-canvas 몫이다(지시101 담당표).
     *
     * ⚠ 앞머리 «Hand-edit drift detected» 를 **일부러 남겼다.** 그쪽
     * `PipelineBuilderScreen.test.tsx` 가 그 글자를 그대로 찾는다 — 남의 파일을 고치면
     * 같은 줄에서 충돌한다(오늘 한 번 났다). **내 파일에서 풀 수 있으면 내 파일에서
     * 푼다.** 뜻은 그대로고 «(decision 3)» 만 빠졌다.
     */
    /**
     * **«지금 발행된 상태인가» 를 한 자리에서 읽는다** (지시159).
     *
     * 🔴 종전에는 흩어져 있었다〔운영자 실측 2026-09-09〕: 도구막대의 «Editing v3 ·
     *   Published v2» 배지 하나, `Hand-edit drift` 배지 하나, 노란 배너 하나 —
     *   사람이 셋을 맞춰 봐야 «발행됨/안 됨» 을 알았다.
     * ⚠ **«발행됨» 이 «구운 적 있음» 이면 안 된다.** 여기서 말하는 것은 **이 판본이
     *   발행본인가** 다(`PipelineGraph.published` 의 뜻 그대로). 이 방의 «발행 ≠ 굽기 ≠
     *   배포» 함정을 문구가 스스로 피해야 한다.
     */
    publishState: {
      title: 'Publish state',
      /** ① 이 판본이 발행본이다. «구운 적 있음» 이 아니다. */
      published: 'This version is the published one.',
      /** ① 발행본은 따로 있고 지금 보는 것은 초안이다 — **어느 판본이 발행본인지** 댄다. */
      draftWithPublished: (published: string) =>
        `You are editing a draft. The published version is v${published}.`,
      /** ① 한 번도 발행된 적이 없다. «초안» 과 «발행본이 딴 데 있다» 를 뭉치지 않는다. */
      neverPublished: 'This pipeline has never been published.',
      /** ② 판본 대조. 같으면 굳이 두 수를 늘어놓지 않는다. */
      versions: (editing: string, published: string) => `Editing v${editing} · published v${published}`,
      editingOnly: (editing: string) => `Editing v${editing}`,
      /** ③ 발행된 것과 지금이 같은가. **판정은 서버 것**이고 화면은 낱말로 옮긴다. */
      matches: 'The generated code matches this graph.',
      differs: 'The generated code and this graph differ — what differs is below.',
      ejected: 'This pipeline was ejected — the code is maintained by hand from here on.',
    },
    driftDetected:
      'Hand-edit drift detected — the generated code has edits the GUI did not make. ' +
      'Nothing is overwritten until you choose: regenerate from the GUI to discard those ' +
      'edits, or eject to keep the code as the source of truth.',
    /**
     * **무엇이 다른지** (지시96 ②, engine 결정4313 · gui-canvas 청 → gui 결정8400).
     *
     * 위 `driftDetected` 는 「다르다」까지만 말한다. 서버가 이미 대조 결과를 실어 보내는데
     * 〔gui-canvas 실측: 화면 소비 **0건**〕 아무도 안 그리고 있었다.
     *
     * ⚠ 앞머리 `Hand-edit drift detected` 는 **안 건드린다** — gui-canvas 의
     * `PipelineBuilderScreen.test.tsx` 가 그 글자를 그대로 찾는다. **덧붙이는 자리**로만 쓴다.
     *
     * ## 계약이 가르는 대로 넷이다 — **할 일이 서로 다르다**
     * ```
     * axis 'file'  · fileMissing 아님  손으로 고쳤다. **해시 비교라 칸을 못 센다**
     * axis 'file'  · fileMissing       ★ **파일이 사라졌다** — 위 배너의 두 길이 다 헛말이 된다
     * axis 'graph'                     재발행을 안 했다. 이때만 `changed` 가 온다
     * more > 0                         목록이 **잘렸다**(서버 상한 12칸)
     * ```
     */
    /*
     * ★ ①에도 한 마디가 필요하다 — gui-canvas 가 «필요 없을 수도 있다» 며 물었고, 재 보니 필요하다.
     *   나머지 셋이 «어느 칸이 갈렸다» 를 말하는데 이 갈래만 아무 말이 없으면,
     *   사람은 «못 만든 기능» 으로 읽는다. **못 대는 것과 안 대는 것은 다르다** —
     *   왜 못 대는지(해시 비교) 한 마디가 그 둘을 가른다.
     */
    driftFileEdited:
      'The generated file has edits the console did not make. That file is compared by hash, ' +
      'so which parts differ cannot be listed here.',
    /*
     * ★★ **넷 중 가장 급하다**(gui-canvas 판단, 동의한다). 위 배너는 «regenerate 하거나
     *   eject 하라» 고만 하는데 **둘 다 파일이 있다고 가정하는 말**이다. 그대로 두면
     *   사람이 없는 파일을 지키려고 eject 를 고른다.
     */
    driftFileMissing:
      'The generated file is gone. Neither choice above reads as written: regenerating writes it ' +
      'again from this graph, and ejecting cannot keep code that is not there.',
    driftGraphChanged: (n: number) =>
      n === 1
        ? 'The graph changed since it was last published. 1 field differs:'
        : `The graph changed since it was last published. ${n} fields differ:`,
    /* ⚠ 안 그리면 «이만큼만 갈렸다» 로 읽힌다 — 계약 주석이 그것을 경고한다. */
    /**
     * ★ **갈린 마디 중 아직 아무 데도 안 붙은 것** (engine 짝, 결정8456).
     *
     * ⚠ 「괜찮다」로 읽히면 안 된다 — 판정은 그대로 drift 다. **사실만** 말한다.
     *   그 마디는 간선 하나면 살아나고, 그때는 갈린 채로 돈다(engine 실측).
     */
    driftUnwired: (n: number) =>
      n === 1
        ? '1 of those steps is not connected to anything yet.'
        : `${n} of those steps are not connected to anything yet.`,
    driftMore: (n: number) =>
      n === 1 ? 'and 1 more field, which the server did not list' : `and ${n} more fields, which the server did not list`,
    readOnlyNote: 'Ejected pipelines are source-of-truth in code — read-only in the console.',

    /**
     * **추론이 어디로 나가는가** (지시96 ③ · `axis-gui-canvas` 결정1482 요청, 결정1421).
     *
     * 그쪽 캔버스 카드가 **색과 기호로만** 말하고 있었다 — 붉은 줄이 *왜* 붉은지 화면에
     * 없었다. **색만으로는 색을 구별 못 하는 사람에게 안 읽히고**, 이유를 모르면 고칠
     * 수도 없다. 그래서 산문을 여기 둔다(`strings` 는 이 축 소유, 결정1417 의 요청 경로).
     *
     * 넷을 가르는 이유: *"어디서 도는가"* 의 답이 **네 가지로 다르고 할 일도 다르다.**
     */
    /**
     * 꼭짓점 하나를 **설계로 등록**한다 (지시47 ⑤ · 대장 65, `§15.1` 의 `scope:'node'`).
     *
     * 메뉴 문구(`contextMenu.registerPreset`)는 이미 있고 아직 아무도 안 쓴다 —
     * 이 둘이 그 줄을 살린다.
     */
    registerPreset: {
      title: (vertex: string) => `Register “${vertex}” as a preset`,
      /** 무엇이 딸려 가는지 **미리** 말한다 — 등록하고 나서 알면 늦다. */
      contains: (kind: string) => `Saves this ${kind} vertex: its model, prompt, and ports.`,
    },
    inference: {
      /**
       * **추론 줄도 «어느 축인가» 를 낱말로 말한다** (gui-canvas 청 → gui 결정9430).
       *
       * 〔gui-canvas 실측 2026-09-07 KST, 목 판 `fan-out-two-outputs`〕 같은 판의 두 카드가
       * **한 글리프로 두 축**을 냈다:
       * ```
       * summarize   … claude-sonnet-5 · **⇢ Anthropic**       ← Anthropic 은 **공급자**
       * extract …   … qwen2.5:7b     · **⇢ ws-draining-1**    ← 이건 **기계 이름**
       * ```
       * 코드도 그렇다 — `targetLabel` 이 `atNode` 면 기계, `cloud`·`providerNodeUnknown` 이면
       * 공급자를 돌려준다. 툴팁은 갈라 말하는데 **줄만 읽으면 못 가른다.**
       * ★ 어긋남 줄에서 고친 그 병(결정9421)과 같고, **여기가 더 자주 보인다**
       *   (어긋남은 드물고 추론 줄은 늘 뜬다).
       *
       * ## ⚠ 왜 `placementDrift.axisNode` 를 **빌려 쓰지 않나** (그쪽이 ㈎ 로 물었다)
       *
       * 낱말은 같지만 **자리는 제자리여야** 한다. 남의 절 낱말을 빌리면 그 절을 고칠 때
       * **이쪽이 조용히 바뀐다** — 문구표에서 그건 «주인이 둘» 이다.
       * ▶ 글자는 **일부러 같게** 둔다: 사람이 두 줄에서 같은 낱말을 봐야 같은 축으로 읽는다.
       */
      /*
       * ⚠ 이름을 `axisNode`/`axisProvider` 와 **일부러 다르게** 지었다.
       * 문구 도달 검사는 열쇠를 **이름으로만** 세므로(그 파일이 적어 둔 한계),
       * 같은 이름이면 **저쪽 면제가 이쪽을 조용히 덮는다** — 그러면 이쪽이 안 이어져도 안 운다.
       * 보이는 글자는 같게 두되(사람이 두 줄을 같은 축으로 읽어야 한다) **열쇠는 갈랐다.**
       */
      inferenceAxisMachine: 'machine',
      inferenceAxisProvider: 'provider',
      /** 클라우드로 나간다 — 노드 밖인 것이 확실하다. */
      cloud: (provider: string) => `Inference → ${provider} (cloud, off-fleet)`,
      /**
       * 온프렘인데 **어느 기계인지 서버가 아직 안 알려준다.**
       * 지어내지 않고 **모른다고 말한다** — 97 ① 이 *"유도가 아니라 저장"* 으로 못박은 자리다.
       */
      unknownNode: (provider: string) => `Inference → ${provider} (host unknown)`,
      /**
       * **우리 tailnet 안인데 등록된 노드와 안 맞는다** (engine 결정2349, gui-canvas 요청).
       *
       * `unknownNode` 와 **할 일이 다르다** — 이쪽은 «우리 기계인데 이름을 모른다» 라
       * 등록하면 풀리고, 저쪽은 «어느 기계인지 서버가 안 알려준다» 다.
       * 둘을 같은 글자로 그리면 사람이 등록할 생각을 못 한다.
       */
      unresolvedNode: (provider: string) =>
        `Inference → ${provider} (on the fleet, node not registered)`,
      /**
       * ★ **모델을 카탈로그에서 못 찾았다** (지시96 ③, gui-canvas 결정6497).
       *
       * 〔그쪽 실측〕 이 갈래는 지금 **아무것도 안 그린다** — `targetLabel` 이
       * `undefined` 를 주고 `InferenceLine` 이 `null` 을 낸다. 흐릿한 것이 아니라
       * **DOM 에 줄이 없다.** 그러면 «추론을 안 하는 꼭짓점» 과 구별이 안 된다.
       *
       * ⚠ **«없다» 가 아니라 «못 찾았다»** 라고 쓴다. 모델은 그 기계에서 잘 돌 수
       * 있고, 못 찾은 것은 **카탈로그**다. 방금 선 기계라면 아직 안 올라왔을 뿐이다.
       */
      unknownModel: (model: string) => `Inference → “${model}” is not in the model catalog`,
      /** 다른 기계로 나간다 — 확정된 사실. */
      otherNode: (node: string) => `Inference → ${node} (another node)`,
      /** 같은 기계다. **침묵과 다르다** — 침묵은 "안 봤다" 로도 읽힌다. */
      sameNode: (node: string) => `Inference → ${node} (this node)`,
      /**
       * ★ **그 자리는 추측이다** (지시97 §1 · gui-canvas 결정3488 요청 → 결정1480).
       *
       * `declared` 면 사람이 적은 **사실**이고, `derived` 면 서버가 **주소로 추측**한
       * 것이다〔실측: `hostname === node.tailscaleAddr`〕. 주소가 바뀌면 딴 기계를 가리킨다.
       *
       * 〔지금 운영〕 `llm_providers.node_id` 가 채워진 줄 **0/5**(LLM 결정2537) —
       * **뜨는 자리가 전부 추측인데 전부 사실처럼** 그려지고 있었다.
       *
       * ⚠ **`declared` 쪽에는 문구를 안 붙인다.** 확정이 기본이고 **추측일 때만** 단서를
       * 단다 — 늘 붙는 표시는 안 읽힌다. 위 `otherNode`·`sameNode` 가 그 기본이다.
       *
       * 〔남겨 두는 물음〕 `derived` 는 *"다른 기계"* 와 *"같은 기계"* 를 안 가른다 —
       * 청한 대로 **한 갈래**로 준다. 가를 필요가 생기면 말해 달라.
       */
      derivedNode: (node: string) => `Inference → ${node} (inferred from address)`,
      /**
       * ★ **이 꼭짓점은 돌지 않는다.** 모델 이름이 여러 공급자에 있는데 공급자를
       * 안 골랐다 — 러너가 `AmbiguousAgentModelError` 를 던진다.
       *
       * 오늘 실행 실패 한 건이 정확히 이것이었고(`run_d8999c73`), **그 사실이 실행 전
       * 화면에 없었다.** 그래서 문구가 *"안 돈다"* 로 시작하고 **할 일로 끝난다.**
       */
      ambiguous: (model: string, providers: string) =>
        `Will not run — “${model}” exists on ${providers}. Pick a provider.`,
    },

    /**
     * **설계와 실제가 어긋났다** (지시96 ④ · 결정1483 요청, 결정1421).
     *
     * 둘을 가른다 — **기계**와 **공급자**는 다른 물음이고, 오늘 실물에서 실제로 달랐다
     * (꼭짓점 `ec2-control-1`, 추론 `onprem-s-ai-pro6000-37`).
     */
    placementDrift: {
      /** 꼭짓점이 설계와 다른 기계에서 돌았다. */
      node: (designed: string, actual: string) => `Ran on ${actual}, not ${designed}`,
      /** 추론이 설계와 다른 공급자로 갔다. */
      provider: (designed: string, actual: string) => `Inference went to ${actual}, not ${designed}`,
      /*
       * ★★★ **«바꾸고 아직 안 돌았다» 는 어긋남이 아니다** (지시181, 운영자 실물 2026-09-09).
       *
       * 🔴 `carewell-form3-extract` 가 붉게 «provider 43 ≠ 37» 을 냈다. **판정은 옳았다** —
       *    설계는 그날 43 으로 옮겼고 마지막 실제는 09-08 21:13 KST · 7b · 37 이었다.
       *    그런데 그 빨강은 **할 일이 없는** 빨강이다(다음 회차에 저절로 풀린다).
       * ⚠ **침묵하지 않는다** — 침묵하면 «맞다» 로 읽힌다. 낯만 낮추고 사실을 말한다.
       */
      notRunYet: (designed: string) => `Changed to ${designed} — has not run since`,
      notRunYetTitle:
        'The design changed after the last run, so what you see under “Ran on” is older than the design. This is not a mismatch — it clears itself on the next run.',
      /**
       * **카드의 짧은 줄에 «어느 축인가» 를 붙인다** (gui-canvas 청 → gui 결정9421).
       *
       * ## 왜 — 두 줄이 **서로를 부정하는 것처럼** 읽혔다
       *
       * 〔gui-canvas 실측 2026-09-07 KST, 목 판 5197 의 `code-task`/`implement`〕 카드가 이랬다:
       * ```
       * ⇢ Anthropic
       * Ran on ws-h200-1 (**no designed node to compare**)   ← 기계 축: 견줄 설계가 없다
       * Anthropic ≠ onprem-s-ai-pro6000-37                   ← 공급자 축: 견줘서 다르다
       * ```
       * 낱말은 **둘 다 옳다**(축이 다르다). 그런데 이어 읽으면 *«견줄 게 없다더니 왜 ≠ 가 있나»* 다.
       * ⚠ 값이 함정을 더한다: `onprem-s-ai-pro6000-37` 은 **공급자 id 인데 기계 이름처럼 생겼다**
       *   (운영자가 부르는 기계가 `s-ai-pro6000-37`) → 아랫줄을 **기계 얘기로** 읽는다.
       *
       * ## 왜 글리프가 아니라 낱말인가 — **이 저장소가 이미 정한 것**이다
       *
       * 지시137 ① 이 못박았다: *«색으로만 가르지 않는다 — **낱말이 나른다**»*.
       * `⇢` 는 색과 같은 부류다 — **규약을 아는 사람에게만** 뜻이 있고, 이 줄을 처음 보는
       *  운영자가 정확히 그 사람이 아니다. 그렇다고 툴팁의 온문장(`node`·`provider`)을 카드에
       *  올리면 **좁은 카드에서 잘린다** — 잘린 안내는 없는 것보다 나쁘다.
       * ▶ 그래서 **한 낱말**이다. 축을 말하고, 안 잘리고, 규약을 몰라도 읽힌다.
       *
       * ⚠ **번역하지 마라.** 이 낱말은 그 축의 이름이지 판정이 아니다 — 뒤에 붙는 것은
       *   여전히 **서버가 준 id·이름 그대로**다(결정7555·7556 이 그 자리를 정했다).
       */
      axisNode: 'machine',
      /** ⚠ «공급자» 다 — **기계가 아니다.** 그 둘이 헷갈려서 이 낱말이 생겼다. */
      axisProvider: 'provider',
    },

    /**
     * **꺼진 기계 안의 카드** (지시97 §2 · gui-canvas 결정2499 요청 → 결정1480).
     *
     * 운영자가 **둘**을 적었다 — *"greyout 으로 표시하고 **inactive 라고 빨갛게 표기**"*.
     * 회색은 섰는데 **글자가 없었다.** 굳이 둘을 적은 이유가 있다 —
     * **회색은 글자가 아니다.** 명암 대비가 낮은 화면이나 색각 차이가 있는 사람에게는
     * *"조금 흐린 카드"* 와 구별되지 않는다.
     *
     * ⚠ **`Offline` 이 아니라 `Inactive`.** 판정이 `offline` 을 `inactive` 로 옮기는 것은
     * **더 넓은 말이기 때문**이다 — 꺼진 기계 **안의** 모델도 같은 표시를 받는데 그
     * 모델 자체는 offline 인 적이 없다. `Offline` 이면 **그 카드에 틀린 말**이다.
     */
    nodeActivity: {
      inactive: 'Inactive',
    },

    /**
     * **도커 판정·설치 문구 일곱** (지시64 · gui-canvas 결정3489 요청 → 결정1425).
     *
     * `dockerPlacement` 모듈은 문구를 **파라미터로 받는다** — 그래서 그 모듈은 언어를
     * 모르고, 갈래를 하나 늘려도 판정과 문구가 **따로 낡지 않는다.** 주는 자리가 여기다.
     *
     * ⚠ **`reason` 은 여기 없다.** 서버가 사람이 읽을 문장으로 실어 보내고 모듈이 그대로
     * 얹는다 — `nodeIdDerivedMismatch` 와 같은 잣대다. 여기서 다시 지으면 **서버가 아는
     * 것보다 더/덜** 말하게 된다.
     *
     * ⚠ 일곱이 **할 일로 끝난다**(상시 규칙 ④). *"안 된다"* 만 적으면 사람이 다음에
     * 무엇을 눌러야 하는지 모른다.
     */
    dockerPlacement: {
      /** 아예 없다 — 깔면 된다. */
      absent: 'Docker is not installed on this machine. Install it to place containers here.',
      /** 깔려 있는데 꺼져 있다 — **깔라고 하면 안 된다.** 켜는 일이다. */
      notRunning: 'Docker is installed but not running. Start the service, then try again.',
      installed: 'Docker is ready. You can place containers on this machine.',
      /** 이미 있었다 — **아무것도 안 바꿨다.** 그 사실이 사람에게 중요하다. */
      alreadyPresent: 'Docker was already running — nothing was changed on this machine.',
      /** 남의 기계에 손대는 일이라, 막힌 이유와 **풀 방법**을 함께 말한다. */
      sudoBlocked: 'Installation needs sudo and none was available. Attach a sudo password credential, then try again.',
      installFailed: 'The install did not finish. Check the failed step, then try again or place this module elsewhere.',
      /** 깔 수는 있어도 **이 모듈은 못 돈다** — 다른 기계를 권한다. */
      gpuUnsupported: 'This machine cannot run the GPU runtime this module needs. Place it on a GPU node instead.',

    },

    /**
     * **도커 창 크롬 열** (지시64 · gui-canvas 결정4489 요청 → 결정2411).
     *
     * ⚠ **위 `dockerPlacement` 와 일부러 갈라 뒀다.** 청해 온 원안은 한 덩어리였고
     * 나도 처음엔 그렇게 붙였는데, `dockerStringsMatchModule` 걸쇠가 잡았다:
     *
     * ```
     * dockerPlacement        모듈이 **파라미터로 통째로 받는** 가방 — 정확히 일곱이어야 한다
     * dockerPlacementDialog  다이얼로그가 **직접 읽는** 문구 — 개수 약속이 없다
     * ```
     *
     * 섞으면 그 걸쇠가 *"일곱이다"* 를 더는 말할 수 없게 된다(17 로 늘려 통과시키면
     * 남는 것은 **아무 뜻 없는 수**다). 게다가 `title` 은 함수라 `string` 가방에
     * 들어갈 것도 아니었다 — **내 주석이 이미 둘을 갈라 적고 있었는데 코드만 안 갈랐다.**
     */
    /** 꼭짓점 편집 창에서 고른 정의의 구체적인 설정을 보여 주는 창 (운영자 지시 2026-10-02). */
    agentDefSettings: {
      open: 'View settings',
      title: (name: string) => `Settings — ${name}`,
      kind: 'Kind',
      model: 'Provider · model',
      executionNode: 'Runs on',
      instruction: 'Instruction',
      unset: 'not set',
      noSettings: 'This definition has no settings.',
      extraHeading: 'Stored on the definition but not declared by this kind:',
      kindsUnread: 'The kind catalog could not be read, so settings are shown by their raw keys.',
      readOnly: 'Read-only here. To change these, edit the definition from the palette — other pipelines may use it too.',
      close: 'Close',
      popOut: 'Open in separate window',
      notFound: (id: string) => `No agent definition with id ${id}.`,
    },

    /** Database Module 을 기계 상자에 놓기 전에 묻는 창 (지시200). */
    databasePlacementDialog: {
      title: (machine: string) => `PostgreSQL on ${machine}`,
      checking: 'Checking this machine…',
      ready: 'PostgreSQL is running on this machine. You can place the module here.',
      foreign: 'A PostgreSQL we did not install is already listening on this machine.',
      absent: 'PostgreSQL is not installed on this machine. Install it to place the module here.',
      notRunning: 'PostgreSQL is installed here but not running.',
      notInstallable: 'PostgreSQL cannot be installed on this machine by the platform.',
      installed: 'PostgreSQL is installed and running. You can place the module here.',
      alreadyPresent: 'PostgreSQL was already running — nothing was changed on this machine.',
      installFailed: 'The install did not finish, so the module was not placed.',
      reverted: 'What was installed has been rolled back.',
      couldNotCheck:
        'This machine could not be checked. Place the module only if a PostgreSQL server is already running there.',
      place: 'Place here',
      placeAnyway: 'Place anyway',
      install: 'Install PostgreSQL',
      installing: 'Installing… this can take a few minutes',
      cancel: 'Cancel',
      close: 'Close',
    },

    dockerPlacementDialog: {
      /** 창 제목. 어느 기계인지 이름이 들어간다. */
      title: (machine: string) => `Docker on ${machine}`,
      /** 판정을 기다리는 동안. **빈 창을 보여 주지 않는다.** */
      checking: 'Checking this machine…',
      /** 준비됐을 때의 확인 단추 — 이 창의 목적이다. */
      place: 'Place here',
      /** 없을 때. 누르면 깐다. */
      install: 'Install Docker',
      /** 까는 중 — 단추가 이 글로 바뀌고 잠긴다. */
      installing: 'Installing…',
      /** 깔려 있는데 꺼져 있을 때. **깔기와 다른 일이라 단추도 다르다.** */
      start: 'Start Docker',
      cancel: 'Cancel',
      close: 'Close',
      /** sudo 가 필요하다는 **경고**지 실패가 아니다 — 누를 수는 있다. */
      needsAdmin: 'This needs administrator rights on the machine.',
      /** GPU 를 못 쓰는 기계에서 대안을 권한다(§0-9 는 막지 않고 알린다). */
      recommendOllama:
        'This machine has no GPU runtime — an Ollama provider on another node may fit better.',
    },

    /**
     * **이 꼭짓점이 요구하는 것을 그 노드가 안 내걸었다** (docs/03 §0-9, gui-canvas 결정4486 요청).
     *
     * ⚠ **막지 않는다. 알린다.** §0-9 가 그렇게 적혀 있다.
     * ⚠ 노드가 `labels` 를 **안 보냈으면 아무 말도 안 한다** — 안 보고한 것은 "없다" 가
     *   아니다. 판정 모듈이 거기서 `undefined` 를 낸다.
     */
    capabilityGap: {
      /**
       * 카드에 한 줄. 짧아야 한다 — 카드가 좁다.
       *
       * ⚠ **복수형을 내가 고쳤다.** 청해 온 원안은 늘 `capability` 였는데 둘이면
       * *"2 capability"* 가 된다. 문구는 내 몫이라 여기서 가른다(키 이름은 그대로).
       */
      short: (count: number) => `${count} ${count === 1 ? 'capability' : 'capabilities'} not advertised`,
      /** 무엇이 없는지. **이름을 대야 사람이 고친다.** */
      detail: (node: string, missing: string) => `${node} does not advertise: ${missing}`,
    },

    /**
     * **실제로 어디서 돌았나** (지시96 ④ 앞문장 · gui-canvas 결정2498 요청 → 결정1480).
     *
     * 원문이 두 문장인데 뒷문장(`placementDrift`)만 서 있었다. 그래서 **설계와 실제가
     * 맞는 날에는 실제 쪽이 한 글자도 안 떴다** — 사람은 여전히 DB 를 봐야 한다.
     *
     * ⚠ **`…Only` 는 "모른다" 가 아니다.** *"잰 값은 있고, 설계 쪽을 몰라 견주지
     * 못했다"* 다. `placementDrift` 가 침묵하는 자리라 여기서마저 흐리면 그 꼭짓점은
     * **영영 아무 말도 안 듣는다.**
     *
     * ⚠ **한 줄씩 혼자 서야 한다** — `run_steps` 2,768행 중 `provider_id` **0행**
     * (LLM 결정2542)이라 당분간 노드 줄만 뜬다.
     */
    ranWhere: {
      /** 설계와 같은 기계였다. 침묵과 다르다 — 침묵은 "안 봤다" 로 읽힌다. */
      ranOnNode: (node: string) => `Ran on ${node}`,
      /*
       * 잰 값은 있는데 **설계를 몰라 견주지 못했다.** "어긋났다" 로 읽히면 안 된다.
       *
       * ★ **무엇을 못 견줬는지 말한다** (결정3423). 〔실측 08-30, 실물 브라우저〕
       * 이 줄 바로 아래에 공급자 어긋남이 함께 떠 있었다:
       *
       * ```
       * Ran on node-h200-1 (no design to compare)
       * provider-anthropic ≠ onprem-s-ai-pro6000-37     ← 견주고 있다
       * ```
       *
       * 둘은 **다른 축**이다 — 앞은 기계, 뒤는 공급자. 그런데 «견줄 것이 없다» 가
       * 무엇을 가리키는지 안 적혀 있어 두 줄이 **서로 부정하는 것처럼** 읽힌다.
       * 낱말 하나로 갈린다.
       */
      ranOnNodeOnly: (node: string) => `Ran on ${node} (no designed node to compare)`,
      /** 추론이 실제로 간 공급자. 설계와 같았다. */
      inferenceAt: (provider: string) => `Inference went to ${provider}`,
      /** 위와 같되 견줄 설계가 없었다. **무엇을** 못 견줬는지 함께 말한다(결정3423). */
      inferenceAtOnly: (provider: string) =>
        `Inference went to ${provider} (no designed provider to compare)`,
    },

    /**
     * **캔버스가 못 그리는 간선** (지시94 ③ · 결정1485 요청, 결정1421).
     *
     * 끝점이 없는 간선은 **저장에서 살아남지만 화면에 안 뜬다**(xyflow 가 못 그린다).
     * 사용자는 간선이 둘이라고 읽는데 실제로는 셋이고, **발행에서야** 문제가 된다.
     *
     * **지우지 않는다**(결정514 가 그 반대를 고쳤다). 있다고 **말할** 뿐이다.
     */
    undrawableEdges: {
      notice: (count: number) =>
        count === 1
          ? '1 edge is not shown — an endpoint does not exist in this graph.'
          : `${count} edges are not shown — their endpoints do not exist in this graph.`,
      detail: (from: string, to: string) => `${from} → ${to}`,
    },

    toolbar: {
      editingVersion: (version: string) => `Editing v${version}`,
      publishedVersion: (version: string) => ` · Published v${version}`,
      save: 'Save',
      saving: 'Saving…',
      publish: 'Publish (codegen)',
      publishing: 'Publishing…',
      eject: 'Eject (switch to code-owned)',
      /**
       * 지시193 — **파이프라인 지우기.** 누르기 전에 무엇이 사라지나 센 값을 보이고, 런 이력은 사람이 고른다.
       * ⚠ «되돌릴 수 없다» 와 «도는 엔진에서 빠지는 때» 를 함께 말한다 — 안 말하면 «지웠는데 아직 보인다» 가 된다.
       */
      deletePipeline: 'Delete pipeline…',
      deletePipelineHint: 'Deletes this pipeline, its graph versions and its generated code. You choose whether its run history goes too.',
      deleteDialog: {
        title: (name: string) => `Delete ${name}`,
        close: 'Close',
        counting: 'Counting what would be deleted…',
        unreadable: 'Could not count what would be deleted, so nothing is deleted from here: ',
        alwaysTitle: 'Always deleted:',
        graphVersions: (n: number) => `${n} graph version(s)`,
        graphHistory: (n: number) => `${n} replaced-graph backup(s)`,
        limits: (n: number) => `${n} limit setting(s)`,
        codeLine: (path: string) =>
          `Generated code at ${path}. The running engine keeps it in memory until the next land, build and restart — until then calls to this pipeline are refused.`,
        withRuns: 'Also delete the run history',
        runsLine: (runs: number, steps: number, agentIo: number, files: number, approvals: number, events: number) =>
          `${runs} run(s) · ${steps} step(s) · ${agentIo} agent I/O record(s), ${files} of them stored as files · ${approvals} approval(s) · ${events} trigger event(s)`,
        runsStay: (n: number) => `${n} run(s) stay in Runs under this pipeline's name.`,
        refusalReserved: 'Platform and self-check pipelines cannot be deleted.',
        refusalEjected: 'This pipeline is code-owned (ejected). Deleting it would delete hand-written code, so it is not offered here.',
        refusalInFlight: (n: number) => `${n} run(s) have not finished. Wait for them or stop them, then try again.`,
        irreversible: 'This cannot be undone. The audit log keeps the record that it was deleted.',
        confirm: 'Delete pipeline',
        confirmWithRuns: (n: number) => `Delete pipeline and ${n} run(s)`,
        deleting: 'Deleting…',
        failed: 'Could not delete: ',
        done: (name: string) => `${name} was deleted.`,
        runsKept: (n: number) => `${n} run(s) were kept.`,
        codeCommitted: (path: string) =>
          `Removal of ${path} is committed on the publish branch. It leaves the running engine after the next land, build and restart.`,
        codeAbsent: (path: string) => `There was no generated code to remove at ${path}.`,
        filesRemoved: (n: number) => `${n} run file folder(s) removed.`,
        filesFailed: (what: string) => `Could not remove a run file folder — ${what}`,
      },
      run: 'Run',
      runSubmitting: 'Submitting run…',
      autoLayout: 'Auto layout',
      /**
       * 지시38 ② — 자동 배치는 **누를 때만** 돈다. 저장은 하지 않는다는 것을
       * 문구에서 분명히 해야 사용자가 되돌릴 수 있다고 안다.
       */
      autoLayoutHint:
        'Rearrange every vertex from its connections. Vertices on a compute node stay inside its box. Nothing is saved until you press Save.',
      // 지시48 ① — 레이아웃 즉시 저장은 버튼을 누르지 않으므로 표시가 없으면
      // 저장됐는지 알 수 없다.
      // 지시48 ③ — Save As · Load
      saveAs: 'Save As…',
      saveAsHint: 'Save the current graph as a new named version. History only grows when you do this.',
      saveAsPrompt: 'Version name (leave empty for a timestamp)',
      saveAsFailed: 'Could not save the version: ',
      load: 'Load…',
      loadHint: 'Open a saved version. Unsaved changes are kept until you confirm.',
      loadTitle: 'Saved versions',
      /** 조회 중 — 빈 목록과 다른 말이어야 한다. */
      loadLoading: 'Loading saved versions…',
      loadEmpty: 'No saved versions yet — use Save As to keep one.',
      loadFailed: 'Could not load versions: ',
      loadCurrent: ' (editing)',
      // 닫기와 취소는 뜻이 다르다 — 다이얼로그를 닫는 것과 "이 버전은 안 부르겠다"
      // 는 다른 동작이고, 같은 문구면 검사도 사람도 둘을 못 가린다.
      loadClose: 'Close',
      loadCancel: 'Cancel',
      /*
       * 저장 안 된 변경이 있으면 **반드시 묻는다**(지시48 ③). 조용히 덮으면 사람이
       * 하던 일이 사라지고, 그건 되돌릴 수 없다.
       */
      loadDiscardWarning: 'You have unsaved changes. Loading another version discards them.',
      loadDiscardConfirm: 'Discard and load',
      layoutSavedAt: (time: string) => `Layout saved ${time}`,
      // 지시48 ② — 레이아웃 저장과 **따로** 말한다. 무엇이 저장됐는지가 다르다.
      autosavedAt: (time: string) => `Auto-saved ${time}`,
      /*
       * 자동 저장 실패 (상시 규칙 ①②④⑥). **성공 시각을 덮어쓴다** — 그것을 남겨 두면
       * 화면이 "저장됨" 이라고 말하는데 그 뒤 편집은 어디에도 없다.
       *
       * 서버 원문을 여기 싣지 않는다. 도구 모음은 한 줄짜리 자리라 긴 문장이 들어가면
       * 다른 단추를 밀어낸다 — 원문은 [Save] 를 눌렀을 때 그 경로가 보여 준다.
       */
      autosaveFailed: 'Could not auto-save — your recent edits are not stored yet. Press Save.',
      saveHint: 'Stores the canvas as a draft version. Nothing runs and no code is generated yet.',
      publishHint: 'Generates pipeline code from this graph and makes the version runnable. Save first.',
      ejectHint: 'Hands ownership of this pipeline to the generated code permanently — after ejecting, the console can only view it (decision 3).',
      runHint: 'Submits one run of this pipeline with empty input, so you can watch it end to end.',
      saveFailed: 'Save failed: ',
      /**
       * **없는 꼭짓점을 가리키는 연결** (지시139 ①, gui 결정6476).
       *
       * ⚠ 운영자가 본 것은 「Save failed: Internal: internal error — see server logs」였고
       * **정작 그 로그에 답이 그대로 있었다**(`edge e7 references unknown node 'n2'`).
       * 그러니 여기서 **어느 연결인지** 말한다 — 「저장이 안 된다」만으로는 할 일을 모른다.
       *
       * ⚠ 그리고 그 연결은 **화면에 안 그려져서 손으로 고를 수가 없다.** 말만 하고 길을 안 주면
       * 사람이 되돌릴 수 없이 막힌다 — 그래서 아래 `dropAndSave` 가 함께 있어야 한다.
       */
      danglingBlocked: (n: number) =>
        n === 1
          ? '1 connection points at a module that is no longer in this pipeline, so this cannot be saved yet.'
          : `${n} connections point at modules that are no longer in this pipeline, so this cannot be saved yet.`,
      /** ★ 어느 것인지 짚는다. 서버가 대는 이름과 **같은 이름**을 쓴다 — 로그와 화면이 갈리면 안 된다. */
      danglingDetail: (from: string, to: string, missing: string) => `${from} → ${to} (missing: ${missing})`,
      /**
       * ★★ 사람이 **누르는** 자리 (지시94 를 안 어기는 유일한 길).
       *
       * 저장 관이 조용히 지우면 「열었다 저장하면 사라진다」가 되돌아온다. 지우는 것은
       * **사람이 그러겠다고 읽고 누른 뒤**여야 한다 — 그래서 문구가 무엇이 없어지는지 밝힌다.
       */
      dropAndSave: (n: number) =>
        n === 1 ? 'Remove that connection and save' : `Remove those ${n} connections and save`,
      /** ⚠ 되돌릴 수 없다는 것을 **누르기 전에** 말한다. */
      dropAndSaveWarning:
        'The connections are removed from the pipeline. The modules they pointed at are already gone.',
      saved: 'Saved.',
      publishFailed: 'Publish failed: ',
      published: 'Publish complete.',
      /**
       * **못 잰 꼭짓점들** (지시145, gui 결정8423).
       *
       * ⚠ **거부가 아니다.** 발행은 성공한 것이고, 이 줄은 «무엇을 못 쟀는지» 만 말한다 —
       * 거부처럼 그리면 145 와 무관한 사슬 넷이 죽은 것처럼 읽힌다(api 실측).
       * ★ api 원문: *«조용히 통과시키지 않는다» 는 값이 **사람 눈에 닿아야 참이 된다.**
       *   로그로만 남긴 것은 그 절반이었다.* — 이 줄이 나머지 절반이다.
       */
      /**
       * **어디서 도는지 말한다** (지시143 §2, gui 결정8470).
       *
       * ⚠ **경고가 아니다.** 발행은 성공했고 이것은 «이렇게 배치됐다» 는 사실이다 —
       * 문구가 「문제」처럼 들리면 사람이 멀쩡한 발행을 되돌린다.
       * ★ 그런데 **말해 줘야 한다**: 실행 노드는 정의가 아니라 **캔버스 부모 사슬**에서
       * 오므로 상자를 옮기면 바뀌는데, 지금까지 사람은 **거부 문구를 보고서야** 알았다.
       */
      /**
       * **발행이 끝난 것을 대화상자로 말한다** (지시158, 2026-09-09).
       *
       * 🔴 종전에는 «Publish complete.» 와 배치 목록이 **화면 구석 글로만** 떴다
       *   〔운영자 실측 화면〕. 사람이 «끝났나» 를 **글자 크기로** 판단하고 있었다.
       * ⚠ **값은 옮기는 것이지 새로 짓는 것이 아니다** — `name`·`version`·
       *   `executionPlacement` 는 발행 응답에 진작 들어 있다.
       */
      publishedDialogTitle: 'Publish complete',
      /**
       * **«구웠다» 가 «도는 판이 바뀌었다» 가 아니다** (지시174).
       *
       * 🔴 굽는 자는 파일을 쓴 직후 `codegen/publish` 가지에 커밋하고 작업 트리를 되돌린다.
       *   그 가지를 태우던 회차가 사라져 **여섯 커밋이 밀려 있었다** — 그동안 발행은 200 을
       *   냈고 사람은 「바뀌었다」고 읽었다.
       * ⚠ 여기서 태우지 않는다(다시 짓기·재기동이 도는 일을 끊는다) — **말은 한다.**
       */
      notRunningYet: (branch: string, commits: number) =>
        commits === 1
          ? `This build is not running yet — 1 commit is waiting on ${branch}.`
          : `This build is not running yet — ${commits} commits are waiting on ${branch}.`,
      notRunningYetHow: 'Land it with scripts/land-codegen.mjs, then rebuild and restart.',
      /**
       * **선언된 상자** (지시59·66, 운영자 판정 ㉠).
       *
       * 🔴 `container-exec.ts` 머리말이 경고한 상태를 **보이게** 만든다:
       *   *「팔레트에서 상자 안에 꼭짓점을 넣으면 그래프에는 부모가 생긴다. **그것뿐이다**」*
       * ⚠ **«떠 있다» 고 말하지 않는다** — 발행은 기계를 안 두드린다. 그것을 재는 자는
       *   Fleet 쪽(`…/containers`)이다. 여기서 «떴다» 고 하면 그것이 낡은 초록이다.
       */
      boxesTitle: 'Boxes this graph declares',
      boxesRow: (name: string, kind: string, node: string) => `${name} (${kind}) on ${node}`,
      boxesNote:
        'Declared, not checked. Publishing does not start a container — bring it up from the machine\u2019s page.',
      /** 대화상자를 닫는 자리. 이 집의 다른 창들과 같은 낱말을 쓴다. */
      close: 'Close',
      /** ⚠ **무엇이 구워졌는지 이름과 판본으로** 말한다 — «완료» 만으로는 무엇이 완료인지 모른다. */
      publishedBuild: (name: string, version: string) => `Baked ${name} · ${version}`,
      /**
       * ⚠ **«없다» 와 «못 쟀다» 를 가른다.** 배치가 비어 있는 것은 배치를 못 잰 것이
       * 아니라 **선언한 마디가 없다**는 뜻이다. 빈 목록을 그냥 안 그리면 사람은
       * «화면이 안 나왔다» 로 읽는다.
       */
      publishedPlacementNone: 'No step declares the machine it runs on.',
      publishedPlacement: (n: number) =>
        n === 1 ? 'One step runs on:' : `${n} steps run on:`,
      /** `<에이전트> — <노드>`. 노드 id 를 **그대로** 보여 준다 — 사람이 그 이름으로 찾는다. */
      publishedPlacementRow: (agentName: string, nodeId: string) => `${agentName} — ${nodeId}`,
      publishedUnknown: (n: number) =>
        n === 1
          ? '1 step was published without checking the node it runs on.'
          : `${n} steps were published without checking the node they run on.`,
      /** ⚠ 셋 다 «못 쟀다» 지만 **사람이 할 일이 다르다** — 뭉치면 어디를 볼지 모른다. */
      publishedUnknownWhy: {
        node_never_joined: 'that machine has never joined, so nothing is known about it',
        ledger_empty: 'nothing has been recorded for that machine yet',
        build_unknown: 'we do not know which bundle that machine runs',
      } as Record<string, string>,
      /**
       * ⚠ **꼭짓점 이름이 앞에 온다** (gui 결정8437).
       *
       * 종전은 `${kind} on ${nodeId} — ${why}` 였다. 그러면 **같은 종류가 같은 기계에 둘이면
       * 두 줄이 글자까지 같다** — 사람이 어느 단계를 볼지 못 고른다. 내가 이 줄을 지으며
       * *«뭉치면 어디를 볼지 모른다»* 라고 적어 놓고 **그 줄이 뭉쳐 있었다.**
       * 서버는 `graphNodeId` 를 주고 있었고 화면은 그것을 **`key` 로만** 쓰고 있었다 —
       * 「쓴다」와 「보인다」는 다르다.
       */
      /* ── 갖추기 판정 (api `c23e218d`, 결정8439) ── */
      /**
       * ⚠ **어느 갈래도 «갖춰졌다» 가 아니다.** 그래서 제목이 «준비됨» 이 아니라 «무엇이 남았나» 다.
       */
      publishedProvisioning: (n: number) =>
        n === 1 ? 'Provisioning check for 1 node:' : `Provisioning check for ${n} nodes:`,
      /**
       * ⚠ **여섯** 갈래가 서로 다른 일이다. 색이 아니라 이 문장이 가른다(지시141 잣대).
       * ⚠ `nothing_new` 를 «준비 완료» 로 쓰지 마라 — 「지난 회차와 같다」일 뿐이고
       *   그 회차가 기계에 안 닿았으면 **여전히 안 닿았다**(api 원문).
       * ⚠ `cannot_provide` 를 «실패» 로 쓰지 마라 — 그 노드는 오늘도 돈다.
       *
       * ## ⚠ 이 표가 **넷에서 멈춰 있었다** (gui 결정9408, 2026-09-07 KST)
       *
       * 계약은 여섯인데 여기는 넷이었다. 그래서 서버가 `provision_ran`·`provision_failed` 를
       * 내면 화면에 **기계 낱말이 그대로** 떴다 — `provisioningLine` 이 «모르는 갈래를 삼키지
       * 않는다»(결정8440)라 사라지진 않았지만, 사람이 읽을 문장은 **없었다.**
       * ★ 하필 그 둘이 «사람이 읽으라고 낸 값» 이다 — 계약 원문:
       * *«걸다 실패해도 **발행은 성공이다** … 되돌리면 판까지 잃는다. 대신 `provision_failed`
       *   로 **눈에 보이게** 남긴다»*〔`api/schemas.ts`〕. **눈에 보이게 낸 값이 낱말로 보였다.**
       * ▶ 왜 안 울었나: 이 표를 재던 자가 **손으로 적은 넷**을 세고 있었다. 이제 그 자가
       *   **계약의 enum 을 읽는다**(`provisioningLine.test.tsx`) — 일곱째가 오면 붉는다.
       *
       * ## 두 갈래의 뜻 〔`api/routes/graphs.ts` 의 `걸린것` 원문〕
       * ```
       * provision_ran     `can_provide` 였던 것을 **실제로 걸었고 성공**했다
       * provision_failed  걸다 실패했다. ⚠ **발행은 그대로 성공**이고 판은 살아 있다 —
       *                   그 노드만 키 없이 돈다. 「발행 실패」로 읽히면 안 된다
       * ```
       * ⚠ 「거는 것 = 그 노드 에이전트 재시작」은 **api 쪽 사실**이라 문구에 안 넣는다
       *   (그쪽이 바꾸면 내 글이 낡는다). 여기 주석에만 적어 둔다.
       */
      publishedProvisioningVerdict: {
        can_provide: 'has keys ready to hand over, but they have not been applied yet',
        nothing_new: 'has nothing new to apply this time (this does not mean the node already has them)',
        not_measured: 'was not checked, so we do not know whether anything is missing',
        cannot_provide: 'cannot be provisioned this way — it keeps running as it is',
        provision_ran: 'was provisioned just now — the keys below were applied to it',
        provision_failed:
          'could not be provisioned — the graph was still published, so this node keeps running without those keys',
      } as Record<string, string>,
      publishedProvisioningRow: (nodeId: string, verdict: string) => `${nodeId} — ${verdict}`,
      /** ⚠ **이름들이지 값이 아니다** — 그렇게 읽히게 «keys» 라고 적는다. */
      publishedProvisioningKeys: (keys: readonly string[]) => `keys: ${keys.join(', ')}`,
      publishedUnknownRow: (graphNodeId: string, kind: string, nodeId: string, why: string) =>
        `${graphNodeId} (${kind}) on ${nodeId} — ${why}`,
      ejectFailed: 'Eject failed: ',
      runFailed: 'Run submission failed: ',
      runSubmitted: (runId: string) => `Run submitted — runId ${runId}`,
    },

    palette: {
      collapse: 'Collapse palette',
      expand: '☰ Palette',
      basicVertices: 'Basic Module',
      /**
       * 캔버스 알림을 닫는 단추 (지시65, 결정453).
       *
       * 알림은 **다음 동작까지 남아 있어야 한다** — 방금 무슨 일이 일어났는지가 정보다.
       * 특히 등록은 "같은 내용이 이미 있어 아무 일도 안 일어났다" 를 말해야 하는데,
       * 그 문장이 사라지면 사용자는 두 번째 설계가 생긴 줄 안다. 그렇다고 캔버스를
       * 영영 가리면 그것대로 방해라 닫을 길을 준다.
       */
      dismissNotice: 'Dismiss this message',
      /**
       * 종류를 만드는 둘째 단추 (지시73, 결정454).
       *
       * `Registered Tools` 는 만들기 경로가 둘이다 — MCP Tool 정의와 **에이전트 종류**.
       * `docs/03` §0-1 이 그 둘을 다른 것으로 못박았고(persistent = agent ·
       * transient = tool), 단추 하나로 접으면 사용자가 방금 무엇을 만들었는지 못 가른다.
       */
      /*
       * **`+` 를 누르면 무엇을 만들지 고르는 자리** (지시86 ②, 결정475·479).
       *
       * 운영자가 요구한 것은 구역별 **명사**다 — `What kind of tool?` ·
       * `What kind of agent?` · `What kind of environment?`. `Registered Tools` 에서
       * *"What kind of agent?"* 가 나온 것이 그 요구의 출발점이었다.
       *
       * 그 명사는 이제 **선언에서 온다**(`paletteSection.kindNoun`, 비LLM 결정1604 ·
       * engine 이 계약에 올렸다). 화면이 구역마다 손으로 박지 않는다 — 구역이 늘어도
       * 이 줄은 안 고친다.
       */
      createKindInSection: (noun: string) => `What kind of ${noun}?`,
      /**
       * 명사가 없는 구역의 대비책. `Basic Module` 처럼 선언이 `kindNoun` 을 안 주는
       * 구역이 있고, 그때 억지 명사를 지어내는 대신 **구역 이름**으로 묻는다.
       * 서버가 명사를 더하면 위 줄로 올라간다 — 깨지지 않고 늘어난다.
       */
      createInSection: (section: string) => `What would you like to create in ${section}?`,
      registeredTools: 'Registered Tools',
      registeredAgentDefs: 'Registered agent definitions',
      registeredDatasources: 'Registered datasources',
      notVerifiedTitle: (reason: string) => `Not verified (${reason}) — the server will reject this at run time`,
      unverifiedSuffix: ' (unverified)',
      toolEmpty: 'No registered Tools — no MCP server has been registered yet.',
      toolNotWired: 'Tool catalog is not wired up on the server (501) — notify an operator.',
      agentDefEmpty: 'No registered agent definitions.',
      agentDefNotWired: 'Agent definition catalog is not wired up on the server (501) — notify an operator.',
      datasourceEmpty: 'No registered datasources.',
      datasourceNotWired: 'Datasource catalog is not wired up on the server (501) — notify an operator.',

      /*
       * 선언에서 온 구역이 비었을 때 (지시66).
       *
       * `agentDefEmpty` 를 재사용하지 않는다 — 그 문구는 "등록된 정의가 없다" 만
       * 말하고, 사용자는 **여기에 무엇이 들어갈 수 있는지**를 모른다. 종류 이름을
       * 넣어서 "Docker 나 Ollama 를 여기 만들 수 있다" 가 읽히게 한다.
       */
      declaredSectionEmpty: (kinds: string) =>
        `Nothing registered yet — use + to define a ${kinds} module.`,

      /*
       * 도커 갈래마다 **다른 한 줄** (지시66).
       *
       * 운영자가 가르라고 한 셋이 여기 있다: 없다 · 못 깔았다 · sudo 가 막혔다.
       * 한 문장으로 뭉치면 사람이 엉뚱한 곳을 뒤진다 — "없다" 는 아직 아무 일도
       * 안 한 것이고, "못 깔았다" 는 해 보고 실패한 것이고, "sudo 가 막혔다" 는
       * 권한 문제여서 **고칠 사람이 다르다.**
       *
       * 사유·다음 행동은 여기 적지 않는다. 서버가 사람 말로 준다 — 화면이 지어내면
       * 계획이 바뀔 때 그 문장만 남아 거짓이 된다.
       */
      docker: {
        absent: 'This machine has no Docker.',
        notRunning: 'Docker is installed here but is not running.',
        installed: 'Docker is ready.',
        alreadyPresent: 'Docker was already here — we did not touch it.',
        sudoBlocked: 'sudo was refused — we cannot install as administrator.',
        installFailed: 'The install ran and failed.',
        gpuUnsupported: 'Docker is installed, but this machine cannot hand GPUs to containers.',
      },

      // 지시47 ② — 찾기·거르기·접기.
      searchLabel: (section: string) => `Search ${section}`,
      searchPlaceholder: 'Search by name…',
      // 몇 개 중 몇 개가 보이는지 말해 준다. 걸렀는데 숫자가 없으면 "왜 안 보이지" 가 된다.
      filteredCount: (shown: number, total: number) => `${shown} of ${total}`,
      noMatch: 'Nothing matches the current search or filter.',
      placementFilterLabel: 'Filter by connection state',
      placementFilter: {
        all: 'All',
        connected: 'Connected',
        unregistered: 'Unregistered',
        no_executor: 'No agent',
        offline: 'Offline',
      },
      toggleSection: (section: string) => `Collapse or expand ${section}`,

      // 지시47 ③ — 레이블 옆 `+` 로 새 정의를 만든다.
      addTo: (section: string) => `Add to ${section}`,
      defineSave: 'Create',
      defineSaving: 'Creating…',
      defineCancel: 'Cancel',
      defineClose: 'Close',
      /**
       * **자격증명을 못 읽었다** (gui 결정8499). 「하나도 없다」와 **절대 안 뭉친다** —
       * 빈 드롭다운은 두 상태가 같아 보이고, `postgres` 는 서버가 그 칸을 **필수**로 막아
       * 저장하면 400 이 난다(까닭 없이).
       * ⚠ 폼을 **안 그리고** 이 말을 낸다 — 고를 수 없는 상자를 내밀지 않는다.
       */
      defineCredentialsUnread: (why: string) =>
        `Could not read the credential list — ${why}. Try again before creating this.`,
      defineChoose: 'Choose…',
      defineFailed: 'Could not create: ',
      // 필수 표시는 별표 하나로 끝내지 않는다 — 별표만 보고는 무엇을 뜻하는지 모른다.
      defineRequiredLabel: (label: string) => `${label} (required)`,
      defineRequired: (label: string) => `${label} is required.`,
      defineNotNumber: (label: string) => `${label} must be a number.`,
      defineTooSmall: (label: string, min: number) => `${label} must be ${min} or more.`,
      defineTooLarge: (label: string, max: number) => `${label} must be ${max} or less.`,
      defineNotJson: (label: string) => `${label} must be valid JSON.`,
      // 선언의 `pattern` 이 막은 것. 무엇이 옳은 모양인지는 그 필드의 hint 가 말한다.
      /** 정해진 값 중 하나여야 하는 숫자 (결정618). 범위만 말하면 587·143 을 넣는다. */
      /*
       * 만들어졌는데 **서버가 거절했다** (상시 규칙 ⑥). 201 이지만 그 도구로는 실행이
       * 안 된다. 사유가 없으면 그렇게 말한다 — 빈 괄호를 보여 주지 않는다.
       */
      defineRejected: (reason: string) =>
        reason.trim() === ''
          ? 'Created, but the server rejected it — it cannot be used to run anything yet. No reason was given.'
          : `Created, but the server rejected it — it cannot be used to run anything yet: ${reason}`,
      defineNotOneOf: (label: string, allowed: string) => `${label} must be one of: ${allowed}`,
      /** 링크로컬 주소 (결정161·186). 왜 안 되는지까지 말한다 — 오타로 읽히면 다시 넣는다. */
      defineLinkLocal: (label: string) =>
        `${label} points at a link-local address. Those reach cloud metadata services, so they are not allowed here.`,
      defineBadShape: (label: string) => `${label} is not in the expected format.`,

      // 지시47 ③ — 구역마다 무엇을 만드는지. 제목이 곧 만들 것의 이름이다.
      defineToolTitle: 'Register a Tool',
      defineToolSummary:
        'Registers an MCP server. The server verifies it on registration and rejects poor tool descriptions.',
      defineAgentDefTitle: 'Register an agent definition',
      defineAgentDefSummary: 'A reusable agent you can drop onto any compute node.',
      defineDatasourceTitle: 'Register a datasource',
      defineDatasourceSummary: 'A named data endpoint that agents and tools can read or write.',

      // 필드 이름·도움말. 서버 계약(03 §13.1~13.5)의 필드를 그대로 묻는다.
      fieldName: 'Name',
      fieldSourceDir: 'Source directory',
      fieldSourceDirHint: 'Must sit inside a root the operator allowed (MCP_TOOL_ROOTS).',
      fieldEntrypoint: 'Entrypoint',
      fieldEntrypointHint: 'Command the server runs to start the MCP process.',
      fieldVertexKind: 'Graph vertex kind',
      fieldVertexKindHint: 'Only for Python vertices. Leave blank to register an ordinary Tool.',
      fieldDescription: 'Description',
      fieldInstruction: 'Instruction',
      fieldInstructionHint: 'The system prompt this agent runs with.',
      fieldModel: 'Model',
      fieldMaxTurns: 'Max turns',
      fieldMaxTurnsHint: 'Upper bound on agent turns in one run. Leave blank for the server default.',
      fieldDatasourceKind: 'Kind',
      fieldCredential: 'Credential',
      fieldCredentialHint: "Required when kind is 'postgres' — connection secrets are never stored in plain config.",
      fieldTable: 'Table',
      fieldColumns: 'Columns',
      fieldColumnsHint: 'Comma-separated. Leave blank to read every column.',

      /*
       * 만들 수 없는 구역은 `+` 를 **회색으로 두고 이유를 말한다.** 눌러서 폼을
       * 채우게 해 놓고 저장할 때 501 로 실패시키면 그 입력이 통째로 버려진다.
       */
      /*
       * 지시50 ③ — 단가를 확인 못 한 모델. 목록에서 **지우지 않고** 고를 수 없게 한다.
       * 0 인 단가를 그대로 두면 비용이 0 으로 잡혀 일일 비용 상한이 그 경로를 못 본다.
       */
      /*
       * **쓸 수 있는데 상한이 안 걸린다** (결정526). `modelPricingUnknown` 과 다른 말이다 —
       * 저쪽은 못 쓰는 것이고 이쪽은 쓸 수 있는 것이라, 사람이 할 일이 다르다.
       */
      modelCostCapBlind: 'no daily cost cap (all rates are 0)',
      /*
       * 서버가 판정을 안 실어 보냈다. **막지 않는다** — 필드가 없다고 전부 잠그면
       * 서버가 조금 달라질 때 화면이 통째로 죽는다(운영자가 실제로 겪었다).
       * 다만 확인된 것과 같아 보이면 안 된다: 상한이 걸리는지 아무도 안 보게 된다.
       */
      /*
       * 부를 수 없다 (결정546). **목록에서 지우지 않는다** — 지우면 "그런 공급자가
       * 없다" 로 읽히는데 사실은 아직 못 부르는 것이고, 그건 누군가 할 일이 있다는 신호다.
       *
       * 단가 미확인과 다른 말이어야 한다: 저쪽은 운영자가 단가를 넣으면 열리고,
       * 이쪽은 넣어도 안 열린다.
       *
       * **이건 서버가 사유를 안 실어 보냈을 때만 쓰는 물러날 자리다**(결정441).
       * 사유를 지어내지 않는다 — 전에는 여기서 *"이 공급자 종류는 아직 부르는 구현이
       * 없다. Anthropic 은 오늘 쓸 수 있다"* 라고 단정했는데, 라이브에서 못 부르는 것이
       * 바로 그 Anthropic 이었다(키가 두 자리 다 비어서, 결정575·579). 갈래가 둘이
       * 된 순간 한 갈래를 이름 붙인 문장이 **거짓이 됐고 고칠 수 있는 것을 가렸다.**
       * 사유가 오면 `notRunnableReason` 을 그대로 쓴다.
       */
      modelNotRunnable:
        'the server says this model cannot be called right now but did not say why — check that the provider kind has a runtime and that a key is attached',
      /**
       * **서버 사유 앞에 붙는 영문 이름표** (결정4469).
       *
       * 결정441 이 «서버가 준 사유를 그대로 쓴다» 로 정한 것은 옳다 — 화면이 다시
       * 판정하면 규칙이 두 벌이 되고, 실제로 콘솔의 추측이 라이브에서 거짓이었다.
       * ⚠ 그런데 그 사유는 **서버가 한국어로 짓는다**(`src/llm/runnable.ts`).
       * 목록을 글로 읽어 보니 이렇게 뜬다:
       *
       * ```
       * OpenAI · GPT-5 — pricing not confirmed — … escape the daily cost cap
       * Gemini · Gemini 3.1 Pro — 공급자 종류 'google' 는 아직 부르는 구현이 …
       * ```
       *
       * **한국어를 못 읽는 사람에게는 줄표 뒤가 그냥 글자다** — 그것이 «못 부른다» 는
       * 경고인지, 값 설명인지, 그냥 메모인지 알 수가 없다. 그러면 못 쓸 모델을 고른다.
       *
       * ▶ **판정은 안 새로 만든다**(441 그대로). 다만 **이름표는 붙인다** —
       * 서버가 이미 `runnable === false` 라고 말했으니 그것을 영문으로 되풀이하는 것은
       * 짐작이 아니다. 지시107 이 «gui 는 팔레트 구별과 **문구**» 라고 한 그 자리다.
       *
       * ⚠ 사유가 **안 올 때**는 위 `modelNotRunnable` 이 이미 온전한 영문 문장이라
       * 이름표를 겹쳐 붙이지 않는다.
       */
      modelNotRunnableLabel: 'cannot be called now',
      /**
       * **못 부르는 사유를 코드로 받아 화면이 영어로 짓는다** (llm 결정4552 → gui 결정5446).
       *
       * ## 왜 이 표가 필요한가 — 산문은 한국어다
       *
       * 결정441 이 «서버가 준 사유를 그대로 쓴다» 로 정한 것은 옳았다. 화면이 다시
       * 판정하면 규칙이 두 벌이 되고, 실제로 콘솔의 추측이 라이브에서 거짓이었다
       * (결정575·579). ⚠ **그런데 그 산문은 서버가 한국어로 짓는다**
       * 〔`src/llm/runnable.ts` 의 `providerKindNotRunnableReason`〕. 영어 화면에
       * 한국어 문단이 뜨고, **한국어를 못 읽는 사람에게 줄표 뒤는 그냥 글자다.**
       *
       * llm 이 그 자리를 갈랐다 — *"판정은 서버가, 문구는 화면이"*(내 결정3429 를 그쪽이
       * 인용했다). 서버는 **안정된 코드**만 내고 문구는 이쪽이 짓는다.
       *
       * ## ⚠ 이것은 «다시 판정» 이 아니다
       *
       * 서버가 이미 `runnable === false` 라고 **판정했고** 어느 갈래인지도 **코드로
       * 말했다.** 화면은 그 코드에 이름표를 붙일 뿐이다 — 판정은 서버 것, 이름표는
       * 화면 것(결정5416 에서 같은 잣대를 세웠다).
       *
       * ## ⚠ 이것은 산문을 **대신하지 않는다. 더한다**
       *
       * ★★ 처음엔 산문을 이 문구로 갈아치웠고 **그것이 틀렸다.** 기존 걸쇠가 잡았다
       * (`instruction110ModelIsSelectable` 의 «서버 사유가 잘렸다», 결정441).
       * 틀린 이유가 검사보다 크다 — **산문이 내 문장보다 많이 말한다.**
       * `kind-not-implemented` 산문은 *"지금 쓸 수 있는 것은 anthropic 과
       * openai_compatible"* 까지 알려 주는데 내 문장에는 그것이 없다.
       * 갈아치우면 **운영자가 할 수 있는 일을 지운다.**
       *
       * ▶ 그래서 이 값은 **이름표 꼬리**로 간다: «… — cannot be called now · <이 문구>».
       * 서버 산문은 `disabledReason` 에 **한 글자도 안 건드린 채** 그대로 있다.
       * 그래서 문장이 아니라 **짧은 꼬리**로 쓴다 — 산문과 겹쳐 읽히기 때문이다.
       *
       * ## ⚠ 모르는 코드는 꼬리를 안 붙인다
       *
       * `Partial<Record<…>>` 라 `undefined` 가 되고 기본 이름표만 남는다.
       * llm 이 사유를 더할 수 있게 계약을 `z.string()` 으로 열어 뒀으므로 **목록을
       * 못박으면 새 코드가 오는 날 화면이 빈다** — 「목록을 박으면 다음 것이 조용히
       * 사라진다」. 새 코드는 한국어라도 뜨는 편이 안 뜨는 것보다 낫다.
       *
       * ⚠ 뜻은 llm 이 코드 옆에 적어 둔 것을 그대로 옮겼다. **짐작으로 짓지 않았다** —
       * 코드 이름에서 기제를 유추하면 그럴듯한 거짓말이 된다.
       */
      modelNotRunnableByCode: {
        /* llm: "그 공급자 종류를 부르는 구현이 이 저장소에 없다. **우리가 할 일**이다." */
        'kind-not-implemented':
          'no runtime for this provider kind — our side, not yours',
        /* llm: "환경에 `ANTHROPIC_API_KEY` 가 없다. **운영자가 할 일**이다." */
        'anthropic-env-key-missing':
          'the server needs an API key for this provider',
        /* llm: "위와 같은데 **자격증명은 붙어 있다** — 그 경로가 그것을 안 본다는 것을 따로 말해야 한다." */
        'anthropic-env-key-missing-credential-present':
          'a credential is attached but this path does not read it — the server itself needs the key',
      } as Partial<Record<string, string>>,
      modelPricingUnstated: 'this deployment does not report whether pricing is confirmed',
      modelPricingUnknown: 'pricing not confirmed — a definition on this model would be billed as $0 and escape the daily cost cap',
      /**
       * ⚠ **못 읽은 것을 «등록된 것이 없다» 로 말하지 않는다** (결정3475).
       *
       * 아래 `defineNoModels` 는 **서버 상태에 대한 단정**이고 **할 일까지 지시한다**
       * (*"register a provider first"*). 목록 질의가 실패했을 때 그 문장이 뜨면
       * 운영자는 **이미 있는 공급자를 또 등록하러 간다** — 데모 중이면 무대 위에서.
       * 3474 와 같은 부류인데 **지시가 붙어 있어 더 나쁘다.**
       */
      defineModelsUnreadable:
        'Could not load the model catalog, so there is nothing to choose from yet. This does not mean no provider is registered — try reloading.',
      defineNoModels: 'No model is registered — register a provider first in Settings.',
    },

    /*
     * 지시47 ④ — `Compute Nodes` 의 `+` 는 갈래가 둘이다.
     *
     * 호스트키 지문·비밀번호 보관 문구는 여기 다시 적지 않는다. 우클릭 쪽
     * (`provision`)의 **같은 문자열**을 쓴다 — 같은 사실을 두 곳에 적으면 한쪽이 낡는다.
     */
    addCompute: {
      title: 'Add a compute node',
      close: 'Close',
      done: 'Done',
      methodLabel: 'How to add it',

      sshTitle: 'Over SSH',
      sshHint: 'Give an address and login. The platform installs node-agent itself.',
      tailscaleTitle: 'From Tailscale',
      tailscaleHint: 'Pick a machine already visible on the tailnet.',

      nodeIdLabel: 'Node id',
      // 서버가 호스트네임 모양만 받는다 — 눌러 보고 400 을 받게 두지 않는다.
      nodeIdBad: 'Letters, digits, dot, dash and underscore only, starting with a letter or digit.',
      hostLabel: 'Address (IP or hostname)',
      portLabel: 'SSH port',
      usernameLabel: 'Login user',
      passwordLabel: 'Password',
      sshRegister: 'Register target',
      sshRegistering: 'Registering…',
      /*
       * 대상 등록과 노드 생성은 **다른 사실**이다. 이 줄이 없으면 사람은 팔레트에서
       * 새 노드를 찾다가 없다고 여긴다.
       */
      sshRegistered:
        'The target is registered and allowed. The compute node itself appears once node-agent is installed.',
      sshInstall: 'Install node-agent…',

      rescan: 'Rescan tailnet',
      rescanHint: 'Scanning reaches every machine on the tailnet, so results are cached for about a minute.',
      scanning: 'Scanning…',
      noneToAdd: 'Every machine found on the tailnet is already registered.',
      addOne: 'Add',
      itemOffline: 'powered off',
      addedCount: (n: number) => `Added ${n} — they are in the palette now.`,

      /*
       * 지시58 — 세 단계를 드러낸다. 운영자가 "어디까지 왔는지 안 보인다" 고 했다.
       */
      stepRegister: 'Connect',
      stepTrust: 'Trust',
      stepInstall: 'Install',
      stepOf: (n: number, total: number) => `Step ${n} of ${total}`,

      /*
       * **지문 없이 신뢰하게 두지 않는다** (지시58 ②).
       *
       * 종전에는 "Not recorded yet — it is captured on the first connection" 이라고만
       * 하고 `Allow this target` 을 누르게 했다. 무엇을 신뢰하는지 모르고 신뢰하는
       * 것이라 그 단계에 의미가 없다. 붙어 본 적이 없으면 그렇게 말하고, **붙어 보는
       * 버튼**을 준다 — 지문이 나와야 승인이 뜻을 갖는다.
       */
      trustNotTried: 'This host has not been contacted yet, so there is no key to check. Try connecting to fetch its fingerprint.',
      trustTry: 'Try connecting',
      trustTrying: 'Connecting…',
      trustConfirm: 'Trust this fingerprint',
      trustGateHint: 'Nothing to trust until a fingerprint has been fetched.',
      /*
       * ⚠ **여기 있던 `trustUnreachable` 을 지웠다** (결정5454).
       *
       * *"도달 불가는 화면이 고칠 수 있는 문제가 아니니 다음에 무엇을 할지 말한다"* 는
       * 취지로 적힌 긴 문단이었고, **한 번도 안 그려졌다**(결정5448 의 자가 짚었다).
       *
       * ★ 안 그려진 것이 맞았다 — **서버가 그 말을 이미 한다**〔실측〕:
       * ```
       * TargetProbe.nextStep   **required**   서버가 «다음에 무엇을 하라» 를 보낸다
       * TargetProbe.detail     선택          까닭
       * 화면                    `probe.detail` · `probe.nextStep` 을 **그대로 그린다**
       * ```
       * 내 문단을 나란히 두면 **서버 것과 경쟁**하고, 갈래가 늘면 내 쪽만 낡는다 —
       * 결정441 이 경고한 그 자리다(«판정은 서버 것, 화면이 다시 만들지 않는다»).
       * `probeTitle.unreachable` 이 제목을 대고 나머지는 서버 문장이 채운다.
       */

      /** 같은 기계를 이름만 바꿔 두 번 등록한 흔적이 실제로 있었다(지시58 사실3). */
      /*
       * 붙어 보기 결과 (지시58, 결정704). **제목만 우리 것이다** — 사유와 다음 걸음은
       * 서버가 마스킹해 보낸 `detail`·`nextStep` 을 그대로 쓴다. 지어내면 서버가 아는
       * 것보다 덜 정확해지고, 갈래가 늘면 낡는다.
       */
      probeTitle: {
        connected: 'Connected.',
        auth_failed: 'The machine answered, but the login was rejected.',
        unreachable: 'Could not reach the machine.',
        host_key_mismatch: 'The host key changed since it was recorded.',
        /* 지시177 — 「못 붙었다」 가 아니라 **「대 보지도 못했다」** 다. 네트워크를 안 잰다. */
        credential_unreadable: 'The saved credential could not be read, so nothing was dialled.',
      },
      probeSeenFingerprint: 'Fingerprint seen just now:',
      /** 붙었다는데 대상 행에 지문이 없다 — 성공으로 뭉개면 승인이 왜 잠겼는지 알 수 없다. */
      probeConnectedNoFingerprint:
        'Connected, but no fingerprint was recorded for this target. Try again — trusting is not possible until one is stored.',
      /**
     * ⚠ **못 읽으면 «중복이 없다» 가 아니다** (결정3477).
     *
     * 아래 경고는 실제 사고에서 나왔다(지시58 사실3 — 같은 기계를 이름만 바꿔 두 번
     * 등록한 흔적). 그런데 대상 목록 질의가 **실패하면** 찾을 것이 없어 경고가
     * **조용히 사라진다** — 사람은 그 침묵을 «중복 아님» 으로 읽고 그 사고를 되풀이한다.
     * 결정3471 에서 내 안전 문구가 같은 식으로 사라졌던 그 모양이다.
     */
    duplicateUncheckable:
      'Could not check whether this machine is already registered — the target list failed to load.',
      duplicateTarget: (targetId: string) =>
        `“${targetId}” already points at this host and port. Registering another name for the same machine will not fix a connection problem.`,

    },

    /*
     * **놓인 노드에 대한 동작** (지시57).
     *
     * 팔레트의 `+` 는 정의를 **만드는** 것이고, 여기 있는 것은 이미 놓인 노드를
     * **조작하는** 것이다. 종전에는 이 문구들이 `addCompute`(노드를 더하는 화면) 아래
     * 있었는데, 자리를 옮긴 뒤에도 이름이 그대로면 **다음 사람이 그 경계를 다시
     * 잘못 배운다** — 운영자가 곁가지로 훑어 보라고 한 그 부류다.
     */
    nodeOps: {
      /** 이 다이얼로그들도 닫는 단추가 필요하다 — addCompute 에 기대지 않는다. */
      done: 'Done',
      /*
       * 지시46 ②③ — Paseo 를 설치하고 비밀번호까지 세팅한다.
       *
       * 설치와 등록은 다른 라우트다. 서버가 그렇게 가른 이유(저장·검증·교체 규칙을 한
       * 곳에만 둔다)가 화면에도 그대로 걸린다 — 확인이 통과해야 등록으로 넘어간다.
       */
      installTitle: 'Install Paseo',
      installHint: 'Installs the Paseo daemon on this machine and sets the password you give.',
      installPassword: 'Daemon password',
      installPasswordHint:
        'You choose it. The platform never generates one for you. It is sent once, used to set the daemon up, and the temporary credential is revoked afterwards.',
      installBind: 'Bind address',
      installBindHint:
        'The tailnet address the daemon listens on. Filled in from this target — the address we SSH to is the address the daemon opens. Change it only if you know it should differ.',
      /*
       * 채울 수 없을 때 (지시69). **빈칸으로 두고 사람에게 떠넘기지 않는다** —
       * 왜 모르는지 말하고, 그러면 사람이 할 일이 분명해진다.
       */
      installBindUnknown:
        'We could not read this target\u2019s address, so this is not filled in. It is the tailnet address we SSH to — check the target is registered under Compute nodes.',
      installBindBad: 'Must be an IPv4 address like 100.70.28.80.',
      installStart: 'Install',
      installRunning: 'Installing…',
      /** 설치는 됐는데 붙지 못했다. "설치했다" 로 끝내지 않는다(지시46 ④). */
      installVerifyFailed: 'Installed, but could not connect with that password — nothing was registered.',
      /**
       * ★ **묶였는데 «등록 안 됨» 이라 말하던 자리** (지시99 (1)5, 결정6471).
       *
       * 사고의 증상: *«매핑이 없으면 화면은 «등록 안 됨» 이라 말하는데 **데몬은 그 값으로
       * 돈다»***. 서버가 붙이는 일을 하고도 결과를 **감사에만** 남겨서 화면이 몰랐다.
       * 이제 `credentialAttached` 가 오므로 **화면이 그 사실을 말한다.**
       */
      installCredentialAttached:
        'The daemon password was attached to this node by the install — there is nothing left to register.',
      /** ⚠ `false` 는 «설치는 됐는데 안 묶였다» 다 — 사람이 이어서 할 일이 있다. */
      installNoPasswordSet:
        'Installed, but the daemon accepts any password, so this one could not be verified. Set a password on the machine itself.',
      installRegistering: 'Registering the credential…',
      installDone: 'Installed and registered — the node can read its sessions now.',
      installFailedAt: (step: string) => `Failed at “${step}”.`,
      installSteps: 'Steps',


      /*
       * 지시50 ①② — 온프렘 추론 서버를 띄우고 성공하면 프로바이더로 등록한다.
       *
       * `gpuHourlyUsd` 가 이 화면의 핵심이다(결정194). 온프렘 모델은 토큰 단가가 0 이라
       * 이 값이 없으면 **비용이 0 으로 잡혀 일일 비용 상한이 그 경로를 못 본다.**
       */
      inferTitle: 'Set up an inference server',
      inferHint: 'Starts an on-prem inference server on this machine and registers it as an LLM provider.',
      inferModel: 'Model',
      // 모델은 사용자가 고른다 — 받는 데 시간과 디스크가 든다(인프라B 브리프).
      inferModelHint: 'You choose it. Pulling a model takes time and disk on that machine, so the platform does not pick for you.',
      inferModelBad: 'Letters, digits and . _ - / : only, starting with a letter or digit.',
      inferBind: 'Bind address',
      inferBindHint: 'The tailnet address the server listens on. Must be an IPv4 address.',
      inferGpuHourly: 'Cost per GPU hour (USD)',
      /**
       * 청구서가 아니라 **운영자가 정하는 환산값**이다(결정194) — 감가 + 전기 + 점유
       * 기회비용, 또는 같은 GPU 의 시간당 임대가. 우리가 숫자를 지어내지 않는다.
       */
      inferGpuHourlyHint:
        'Your own conversion figure — depreciation, power, or what renting the same GPU would cost. It is what makes the daily cost cap see this model at all.',
      /*
       * 0 은 금지가 아니다. **말하고 해야** 한다 — 기본값에 기대어 조용히 상한 없는
       * 경로가 서는 것만 막는다(결정194).
       */
      inferUnmetered: 'I accept that this model will not count toward the daily cost cap',
      inferUnmeteredRequired:
        'A rate of 0 means this model is never billed, so the daily cost cap cannot see it. Tick the box above to say that is deliberate.',
      inferAdvanced: 'Optional',
      inferGpuDevices: 'GPU devices',
      inferGpuDevicesHint: 'Comma-separated indices, e.g. 1,2,3. Leave blank to let the plan decide.',
      /**
       * **잰 것을 보여 준다** (결정6452). 형제 칸인 `arch` 와 같은 잣대다 —
       * 「쟀다 / 못 쟀다 / 재 봤더니 없다」 셋을 갈라 말한다.
       *
       * ⚠ 셋을 뭉개면 «못 쟀다» 가 «없다» 로 읽히고, 그것이 `GET …/gpus`
       * («장치 배정을 추측하지 않기 위한 자리», 결정730)가 막으려던 그 추측이다.
       */
      inferGpuMeasured: (count: number) =>
        `Measured on that machine: ${count} GPU${count === 1 ? '' : 's'}.`,
      inferGpuFree: (indexes: string) => `Free right now: ${indexes}. Click one to use it.`,
      inferGpuAllBusy: 'Every GPU on that machine is in use right now.',
      inferGpuDevice: (d: { index: number; name: string; usedByProcesses: number; usedMb: number; totalMb: number }) =>
        `${d.index}: ${d.name} — ${d.usedMb} of ${d.totalMb} MB used by ${d.usedByProcesses} process${d.usedByProcesses === 1 ? '' : 'es'}`,
      /** 잰 결과 **없는** 것. «못 쟀다» 와 다른 말이라 문구를 따로 둔다. */
      inferGpuNone: 'Measured on that machine: no GPUs. Leave this blank.',
      /** **못** 잰 것. 「없다」로 읽히면 안 된다. */
      inferGpuProbeFailed:
        'Not measured — the platform could not read the GPUs on that machine. Leave this blank rather than guessing; a wrong index sends the model to a card that is already busy.',
      inferGpuLoading: 'Reading the GPUs on that machine…',
      /**
       * ⚠ **못 읽은 것은 «없다» 가 아니다** (결정3480). `probeFailed` 와도 다른 말이다 —
       * 그쪽은 «서버가 그 기계를 못 읽었다», 이쪽은 **«우리가 서버를 못 읽었다»** 다.
       * 셋을 한 문구로 뭉개면 사람이 «GPU 없는 기계» 로 읽고 그대로 짐작해 채운다.
       */
      inferGpuUnreadable:
        'Could not read the GPU list — this does not mean that machine has no GPUs. Leave this blank rather than guessing.',
      inferPort: 'Port',
      /* 비워 두는 것이 정상이라고 **말해 준다** — 안 그러면 뭔가 채워야 하는 줄 안다. */
      inferPortHint: 'Leave blank to let the runtime pick its default port.',
      inferContext: 'Context length',
      inferContextHint:
        'Tokens the model can hold at once. Leave blank for the runtime default — a larger window needs more VRAM.',
      inferStart: 'Set up',
      inferRunning: 'Setting up…',
      /** 성공 응답 안에 "등록 안 됨" 갈래가 있다 — 뭉개면 프로바이더가 생긴 줄 안다. */
      inferStartedNotRegistered:
        'The server started, but it was not registered as a provider — so nothing can use it yet.',
      inferRegistered: (providerId: string, count: number) =>
        `Registered as “${providerId}” with ${count} model${count === 1 ? '' : 's'}.`,
      inferFailedAt: (step: string) => `Failed at “${step}”.`,
    },

    /*
     * 지시51 ③ — 종류를 고르고 **그 선언에서** 폼을 만든다.
     *
     * 여기에 **종류 이름이 하나도 없다.** 목록도 설명도 서버(`GET /v1/agent-kinds`)가
     * 준다 — 적어 두면 종류가 늘 때 화면이 서버보다 좁아진다.
     */
    /**
     * 테스트 유입구 — 폼을 채워 실행을 하나 만든다 (지시75, 결정458).
     */
    webForm: {
      /*
       * **쓸 때도 테스트용임을 말한다.** 만들 때 한 번 말했다고 쓸 때 안 말하면,
       * 정의를 만든 사람과 쓰는 사람이 다를 때 그 사실이 사라진다.
       */
      summary:
        'Fills in one run by hand. This is a testing entrance — what you type is passed to the pipeline as data, never as an instruction.',
      /*
       * 정의에 경로가 없다. **부르지 않는다** — 없는 경로로 POST 하면 404 가 나는데
       * 그 404 는 "그 수신기가 없다" 로 읽혀 정의가 잘못됐다는 진짜 사실을 가린다.
       */
      noPath: 'This definition has no path set, so there is nothing to submit to. Edit the definition and set its path first.',
      /*
       * 스키마가 없거나 속성이 없다. 빈 폼은 제출 단추만 덩그러니 떠서 누르면
       * 무엇이 보내지는지 아무도 모른다.
       */
      noFields:
        'This definition declares no form fields, so there is nothing to fill in. Edit its “Form fields (JSON schema)” to add some.',
      /*
       * **전체 중단이다. 실패가 아니다.** 행은 남았고 큐에만 안 들어갔다
       * (`docs/03` §18.2-1 의 정정 — 비LLM 결정667 이 값으로 잡았다).
       * 실패로 그리면 기록이 안 남은 줄 알고, 성공으로 그리면 run 을 찾으러 간다.
       */
      heldByGlobalStop: (runId: string) =>
        `Recorded as ${runId} but not queued — the global stop is engaged, so nothing will run until it is lifted. The submission itself was accepted and is in the audit trail.`,
      /** 제출이 실행을 만들었다. */
      submitted: (runId: string) => `Started ${runId}.`,
      menuItem: 'Fill in this form…',
      /** 방금 만든 실행으로 데려다준다 (결정460). id 만 말하면 손으로 복사해 찾아야 한다. */
      openRun: 'Open this run',
    },

    /**
     * 에이전트 런타임 상태 (`docs/03` §13.2-4, engine 결정1304 · 읽기는 결정464).
     *
     * **`stale` 을 "멈췄다" 로 옮기지 않는다.** 아는 것은 *"소식이 끊겼다"* 뿐이다 —
     * 멈춘 것일 수도, 엔진이 죽은 것일 수도, 폴러가 선 것일 수도 있다. 셋을 하나로
     * 부르면 그중 둘에 대해 거짓말이 된다.
     */
    agentRuntime: {
      face: {
        running: 'running',
        starting: 'starting',
        stopped: 'stopped',
        failed: 'failed',
        /*
         * **`stale` 의 낯이다.** "stopped" 도 "running" 도 아니다 — 우리가 아는 것은
         * 소식이 끊겼다는 것뿐이다. 낱말이 그 이상을 말하면 안 된다.
         */
        unknown: 'no news',
      },
      /*
       * 소식이 끊겼을 때. **마지막으로 들은 상태와 그 시각을 둘 다** 말한다 —
       * 그것이 사람이 판단할 유일한 근거다(계약이 `lastBeatAt` 을 같이 보이라고 했다).
       */
      staleHint: (lastState: string, at: string) =>
        `No heartbeat since ${at}. The last thing it reported was “${lastState}”, but that may no longer be true — the agent, the engine, or the poller could be the one that stopped.`,
      freshHint: (at: string) => `Last heartbeat ${at}`,
    },

    agentKind: {
      pickTitle: 'What kind of agent?',
      /**
       * 되돌릴 수 없는 일을 할 수 있는 종류 (결정625, 읽기는 결정456).
       *
       * **막지 않는다** — 그러라고 있는 종류들이다(카톡을 보내고, 로봇에 신호를 내고,
       * 코딩 에이전트가 파일을 고쳐 push 한다). 다만 만들기 전에 알아야 한다.
       *
       * 무엇이 되돌릴 수 없는지는 **말하지 않는다.** 서버가 갈래를 접어서 참/거짓만
       * 주기 때문이고(조건부도 참이다), 화면이 종류별 문장을 지어내면 그 순간
       * 종류 이름이 화면에 박힌다 — 이 저장소가 되풀이해 없애 온 것이다.
       */
      /**
       * 테스트용 문 (지시75, 결정457).
       *
       * **막지 않는다** — 쓰라고 만든 것이다. 다만 운영 트래픽을 이 문으로 받으면
       * 안 된다는 것을 만들기 전에 말한다.
       *
       * *"덜 위험하다"* 로 읽히면 안 된다. `docs/03` §18.2-1 이 그 반대를 적었다 —
       * 사람이 손으로 미는 문이라 오타가 그대로 유입된다.
       */
      testingOnlyNote:
        'This is a testing entrance — meant for trying a pipeline by hand, not for production traffic. It lives behind the console login and does not open a public endpoint. What you type in is treated as data, never as an instruction to the agent.',
      irreversibleWarning:
        'Definitions of this kind can do things that cannot be undone once they run — sending messages, driving hardware, or changing files. Nothing happens until you run it, but there is no undo afterwards.',
      pickHint: 'Each kind asks for different settings. The list comes from the server.',
      title: (label: string) => `New ${label}`,
      /** 배치할 수 있는 노드를 좁힌다 — 만들기 전에 알아야 헤매지 않는다. */
      needsCapability: (caps: string) => `Runs only on nodes with: ${caps}`,
      /**
       * 선언은 있는데 **이 배포에 실행 구현이 없다.** 못 쓰는 것(blockedReason)과
       * 다른 사실이다 — 만들 수는 있고 지금 돌릴 수만 없다.
       */
      noRuntime: 'Can be defined, but this deployment cannot run it yet.',
      kindsEmpty: 'The server reports no agent kinds at all — notify an operator.',
      kindsFailed: (message: string) => `Could not load the agent kinds — ${message}`,
    },

    /*
     * 지시51 ② 수정·삭제 — 폼이 저장만 되고 있었다.
     *
     * 잘못 넣은 값을 고칠 길이 없으면 ROS2 의 속도 상한처럼 **틀리면 위험한 값**이
     * 그대로 남는다.
     */
    agentDefEdit: {
      editLabel: (name: string) => `Edit ${name}`,
      title: (name: string) => `Edit ${name}`,
      /**
       * 저장된 종류를 서버가 더 이상 모른다. 빈 폼을 주면 `config` 를 통째로 덮어써
       * 설정이 사라지므로 **폼을 지어내지 않는다.**
       */
      /**
       * ⚠ **못 읽은 것을 «서버가 더는 선언 안 한다» 로 말하지 않는다** (결정3474).
       *
       * 아래 `unknownKind` 는 **서버에 대한 단정**이다. 그런데 종류 목록 질의가
       * 그냥 **실패**했을 때도 같은 문장이 떴다 — 그러면 사람은 *"이 종류가 플랫폼에서
       * 없어졌구나"* 로 읽고 정의를 지우려 든다. 침묵보다 나쁘다: **틀린 말을 한다.**
       */
      kindsUnreadable:
        'Could not load the kind declarations, so this form cannot be built safely. This does not mean the kind was removed — try reloading.',
      unknownKind: (kind: string) =>
        `This definition has kind “${kind}”, which the server no longer declares. Editing it here could wipe its settings, so the form is not shown.`,
      deleteStart: 'Delete this definition…',
      // 무엇이 지워지는지 **이름으로** 말한다. 지우면 끝이다.
      deleteConfirm: (name: string) => `Delete “${name}” permanently? This cannot be undone.`,
      deleteReally: 'Delete',
      deleting: 'Deleting…',
    },

    /*
     * 지시47 ⑤ — 캔버스의 모듈을 사전 정의로 등록한다.
     *
     * 막을 때는 **이유를 함께 준다.** 메뉴에서 항목을 통째로 감추면 "여기서는 못 한다"
     * 가 아니라 "그런 기능이 없다" 로 읽힌다.
     */
    registerPredefined: {
      menuItem: 'Register as Predefined Agent',
      title: 'Register as a predefined agent',
      summary: 'The prompt on this vertex becomes a definition you can drop onto any compute node.',
      instructionHint: 'Taken from this vertex. Edit it here if the wording was specific to one run.',
      blockAlready: 'This vertex already points at a registered definition.',
      blockEmpty: 'This vertex has no prompt yet — there is nothing to register.',
      /*
       * ⑤ 의 tool·datasource 절반 — **판단을 내렸다**(지시47 ⑤, 결정428).
       *
       * 셋 중 2번을 골랐다. 이 꼭짓점이 가리키는 **정의는 이미 등록돼 있고**, 재사용하고
       * 싶은 것은 정의가 아니라 그 꼭짓점의 설정(인자·모드) 한 벌인데 담을 자리가 없다.
       *
       * 새 프리셋 저장소(1번)를 ⑤ 를 위해 먼저 만들면 ⑥ 의 설계 저장이 그 모양에
       * 끌려간다. `defaultParams`(3번)는 **정의당 한 벌**이라 같은 도구를 두 가지로
       * 쓰는 파이프라인에서 무너진다.
       *
       * 그래서 항목을 감추지 않고 **흐리게 두고 두 가지를 다 말한다** — 이미 등록돼
       * 있다는 것과, 설정 재사용은 아직 자리가 없다는 것. 감추면 "그런 기능이 없다" 로
       * 읽힌다(검증 안 된 Tool·카톡 인바운드와 같은 잣대).
       */
      blockNoStore:
        'This vertex already points at a registered definition. Reusing its per-vertex settings needs a store that does not exist yet.',
      /** trigger·transform·branch·output — 애초에 정의가 되는 종류가 아니다. */
      blockNotDefinable: 'Only agent, tool and datasource vertices can become definitions.',
      /** 등록 뒤 그 꼭짓점이 새 정의를 가리키게 됐다는 사실. 조용히 바꾸지 않는다. */
      switched: (name: string) => `Registered as “${name}” — this vertex now uses that definition.`,
    },

    inspector: {
      /* 지시184 — 선의 조건 칸의 보이는 이름. */
      edgeConditionLabel: 'Condition',
      /*
       * ★ **편집기가 없는 종류** (결정5438). 지어내지 않고 **없다고 말한다.**
       * ⚠ «설정이 없다» 가 아니라 «여기서 못 고친다» 다 — 선언은 `image`·`tag` 를
       * `required` 로 요구하는데 콘솔에 채울 길이 없다(gui-canvas 7824 실측).
       */
      noEditorForKind:
        'This kind has no hand-written editor. The fields below come straight from the ' +
        'declaration, so they stay correct when the declaration changes.',
      requiredMark: ' (required)',
      notSetHere: 'not set',
      /**
       * ⚠ **기본값을 값인 척 채우지 않는다** (결정5441).
       *
       * 선언의 `default` 를 입력칸에 미리 넣으면 화면은 «설정됐다» 로 보이는데
       * 그래프에는 아무것도 안 적혀 있다. 「빈 것·없는 것은 다르다」 —
       * 안 채운 칸은 **빈 채로 두고 서버가 무엇을 쓸지만 말한다.**
       */
      willUseDefault: (v: string) => `not set — the server will use ${v}`,
      /*
       * 「빈 것·없는 것·아직 안 온 것·못 읽은 것은 다 다르다」 — 칸이 0개인 이유를
       * 가르지 않으면 «설정이 없는 종류» 와 «선언을 못 읽었다» 가 같은 화면이 된다.
       */
      noKindsDeclared: 'This kind declares no settings.',
      kindsNotWired: 'The kind declarations are not wired up, so this editor cannot draw its fields.',

      emptySelection: 'Select a node or edge on the canvas to edit its parameters.',
      nodeSuffix: ' node',
      delete: 'Delete',
      close: 'Close',
      edgeCondition: 'Edge condition',
      edgeConditionPlaceholder: 'e.g.: output.approved === true',
      edgeConditionHint: 'Only taken when this expression evaluates truthy. Leave blank to always follow this edge.',
      loadingCatalog: 'Loading…',
      /*
       * ⚠ **여기 있던 `loadFailed` 를 한 칸 위로 올렸다** 〔gui 결정9434〕 —
       * `pipelineBuilder.loadFailed` 다. 그 문장은 «Could not load: …» 로 **온전히 일반**이라
       * 인스펙터 이야기가 한 글자도 없는데, **세 자리가 이 절에서 빌려 쓰고** 있었다
       * (인스펙터·기계 상자·캔버스 카드). 그러면 **한 열쇠에 주인이 셋**이 되고,
       * 이 절을 고치는 사람이 남의 화면을 조용히 바꾼다.
       */
      jsonInvalid: 'Not valid JSON — will save once it is valid',
    },

    editors: {
      agent: {
        /*
       * ★ **가릴 수 없을 때** (결정5406). 이름 참조인지 프롬프트 본문인지는
       * **정의 목록이 있어야** 안다. 못 읽었으면 «프롬프트다» 로 단정하지 않는다 —
       * 단정하고 사람이 그 칸을 고치면 정의 참조가 **덮인다.**
       * 그래서 칸을 감추고 **왜 감췄는지**를 말한다.
       */
      refUndecidable:
        'Cannot tell whether this step points at a registered definition — the definition list ' +
        'could not be read. The box below is locked on purpose: editing it would overwrite the ' +
        'reference with prompt text. It unlocks once the list loads.',
      configMode: 'Configuration mode',
        configModeHint: 'A one-off prompt runs standalone; a registered definition reuses a shared prompt, model, and I/O schema across pipelines.',
        promptDirect: 'Enter prompt directly',
        promptDirectHint: 'Write the instruction here, on this one node. Fastest to try something, but nothing about it is reusable or shared.',
        useDefinition: 'Use a registered agent definition',
        useDefinitionHint: 'Point at an agent registered once and reused everywhere — it brings its own model, instruction, and input/output definition, and it can be pinned to a specific execution node.',
        agentDefinition: 'Agent definition',
        agentDefinitionHint: 'Registered agent definitions carry their own model, instruction, and (if set) a bound execution node — see docs 03 §13.2.',
        agentDefEmpty: 'No registered agent definitions — register one first.',
        agentDefNotWired: 'Agent definition catalog is not wired up on the server (501).',
        prompt: 'Prompt',
        promptHint: 'Plain-language instruction the LLM receives verbatim as this step’s task. Be specific — vague prompts produce vague output.',
        promptPlaceholder: 'Summarize the attached meeting notes in three bullet points',
        model: 'Model',
        modelHint: 'Which registered LLM runs this prompt. Larger models cost more per token but follow complex instructions more reliably.',
        selectModel: 'Select model',
        modelEmpty: 'No registered LLM models — register a provider and model first.',
        modelNotWired: 'Model catalog is not wired up on the server (501).',
        maxTurns: 'maxTurns',
        maxTurnsHint: 'Maximum back-and-forth tool-use turns before the agent is forced to stop. Leave blank for the provider default.',

        /**
         * 에이전트 정의의 입출력 명세(agent_defs.input_spec_text·output_spec_text)는
         * **자연어로 적으면 LLM 이 JSON Schema 로 바꿔 주는** 항목이다(운영자 명시 요구,
         * 지시22 §7). 처음 쓰는 사람은 "무엇을 어떻게 적어야 하는지"를 모르므로
         * 예시를 문구 안에 넣는다. 파이프라인 편집기에서는 읽기 전용으로 보여 준다 —
         * 정의를 만드는 화면은 여기가 아니다.
         */
        ioSpec: 'Input / output definition',
        ioSpecHint:
          'Describe in plain language what this agent receives and what it must return; the platform turns that description into a JSON Schema. Example input: “the raw text of one customer email, plus the account tier”. Example output: “a one-line summary, an urgency of low/medium/high, and true/false for whether a refund was requested”. Name every field and say what type it is — vague wording produces a vague schema.',
        ioSpecInput: 'Input',
        ioSpecOutput: 'Output',
        ioSpecMissing:
          'This definition has no plain-language input/output description yet, so downstream steps cannot know what shape it returns. Add one where the agent definition is registered.',
        ioSpecReadOnly: 'Read-only here — edit it where the agent definition is registered.',
      },
      tool: {
        toolMcpServer: 'Tool (MCP server)',
        toolMcpServerHint: 'The registered MCP server this step calls into. Only verified servers (03 §13.4) are safe to run.',
        selectTool: 'Select a Tool',
        selectSpecificTool: 'Select a tool',
        toolHint: 'One specific tool exposed by the selected MCP server.',
        tool: 'Tool',
        toolEmpty: 'No registered Tools — register an MCP server first.',
        toolNotWired: 'Tool catalog is not wired up on the server (501).',
        unverifiedSuffix: ' (unverified)',
        unverifiedWarning: (reason: string) =>
          `This Tool is not verified (${reason}) — it can be added to the graph, but the server will reject it at run time (03 §13.4).`,
        inputMapping: 'Input mapping (JSON)',
        inputMappingHint: 'Maps upstream port values onto this tool’s inputSchema fields, e.g. {"query": "{{n1.output}}"}.',

        /**
         * MCP 요건(주석·스키마)을 **고르기 전에** 보여 준다(운영자 명시 요구, 지시22 §7).
         * 등록이 거절되는 가장 흔한 이유가 description 누락과 inputSchema 누락이라
         * 무엇이 필요한지를 문구로 못박고, 고른 도구의 실제 값도 같이 보여 준다.
         */
        mcpRequirements: 'What an MCP server must provide',
        mcpRequirementsHint:
          'Every tool an MCP server exposes must carry (1) a name, (2) a one-sentence description saying what it does and when to use it — the LLM picks tools by reading this, so “does stuff” gets the tool ignored, and (3) an inputSchema: a JSON Schema object listing each argument, its type, and which ones are required. A server missing either of those is rejected at registration (docs 03 §13.4) and cannot be added here.',
        toolDescription: 'Description (what the LLM reads to choose this tool)',
        toolDescriptionMissing: 'No description — the LLM has nothing to decide by, so this tool will rarely be chosen.',
        toolInputSchema: 'inputSchema fields',
        toolInputSchemaEmpty: 'This tool takes no arguments.',
        toolInputSchemaField: (name: string, type: string, required: boolean) =>
          `${name}: ${type}${required ? ' (required)' : ' (optional)'}`,
      },
      datasource: {
        datasource: 'Datasource',
        datasourceHint: 'The registered connection (Postgres table, HTTP API, or internal run history) this step reads from or writes to.',
        selectDatasource: 'Select a datasource',
        datasourceEmpty: 'No registered datasources — register one first.',
        datasourceNotWired: 'Datasource catalog is not wired up on the server (501).',
        readWrite: 'Read/Write',
        readWriteHint: 'Read pulls rows into the pipeline; write persists upstream output. internal_runs only supports read.',
        read: 'Read',
        write: 'Write',
        internalRunsReadOnly: 'internal_runs is read-only (03 §13.5) — writing is not supported.',
        columns: 'Columns (comma-separated)',
        columnsHint: 'Which columns to read or write, e.g. id, status, summary. Leave blank to use every column.',
      },
      trigger: {
        source: 'source',
        sourceHint: 'What kind of external event starts this pipeline — an inbound email, a Slack message, a schedule, etc.',
        select: 'Select',
        schedule: 'Schedule (cron expression)',
        scheduleHint: 'Standard 5-field cron syntax, evaluated in the server’s local timezone.',
        schedulePlaceholder: 'e.g.: 0 9 * * 1-5',
      },
      branch: {
        /**
         * 🔴 **위 셋(`conditionExpression`·`conditionHint`·`conditionPlaceholder`)을 지웠다**
         * (gui 결정9423).
         *
         * 그 칸은 `params.expression` 을 썼는데 **읽는 곳이 콘솔 밖에 0** 이었다〔전수〕.
         * 엔진의 `branch` 는 `node.ref` 가 없으면 **핸들러 자체를 안 굽는다**
         * (`codegen/emit.ts` 의 `emitBranchHandler`) — 갈림은 **엣지의 `condition`** 이 한다.
         * ▶ 사람이 식을 적고, 카드가 보여 주고, **파이프라인은 그 마디를 그냥 통과시켰다.**
         *
         * ⚠ 그래서 문구도 함께 지웠다 — **안 그리는 문구를 남기면** 다음 사람이
         *   «폼이 빠졌구나» 하고 **되살린다.** 없앤 까닭이 여기 남아 있어야 안 되살린다.
         */
        /**
         * **가는 길을 그 자리에서 말한다.**
         *
         * ⚠ 칸만 지우면 사람은 «그럼 분기를 어떻게 하지» 로 남는다 — 그것도 조용한 실패다.
         * 종전 `note` 가 이미 그 길을 적고 있었는데(*«Each edge's own condition…»*)
         * **안 되는 칸 아래 곁말**로 있어서 안 읽혔다. 이제 이 줄이 **본문**이다.
         * ⚠ 「이 마디가 아무것도 안 한다」가 아니라 **「조건은 저기 있다」** 로 적는다 —
         *   앞엣것은 사람을 멈추게 하고, 뒤엣것은 다음 손짓을 준다.
         */
        conditionLivesOnTheEdge:
          'A branch does not hold the condition itself — each outgoing edge does. ' +
          'Select an edge to set when it is taken (03 §5.1 GraphEdge.condition).',
      },
      transform: {
        /**
         * 🔴 **`transformExpression` 을 지웠다** (gui 결정9423) — 그 칸(`params.expression`)을
         * **읽는 곳이 콘솔 밖에 0** 이었다〔전수〕. 엔진은 **`params.set`**(출력칸 → 식 지도)을
         * 읽는다〔`codegen/emit.ts` 의 `emitTransformHandler`〕. 안 그리는 문구를 남기면 다음 사람이 **폼을 되살린다.**
         */
        setLabel: 'Output fields',
        /**
         * ⚠ **«식» 이라고만 하면 상수를 그냥 적는다.** 엔진이 문자열 아닌 칸을 **발행에서
         * 거부하고**(결정4363) 그 오류 문구가 *«상수를 넣으려면 따옴표로 감싸라»* 라고 이른다 —
         * 그 규칙을 **적기 전에** 말해야 사람이 400 을 안 본다.
         */
        /**
         * 🔴 **자리표를 `{{input.text}}` 라고 적었다가 고쳤다** 〔gui 결정9425, 같은 날〕.
         *
         * 그렇게 적은 까닭이 값이다: **엔진 주석의 예를 그대로 베꼈다** — 그때
         * `emit.ts` 의 `emitTransformHandler` 머리말이 `set` 값을 **프롬프트 꼴로** 적고 있었다.
         * 파서를 안 읽었다.
         *
         * ⚠⚠ **그 예를 여기 글자로 인용했다가 뺐다** 〔gui 결정9432, 2026-09-07 KST〕.
         * 내가 그것을 브리프로 알렸고 **engine 이 그 예를 지웠다**(`898b30e4`) — 그러자
         * 내 `citationTokensResolve` 가 «인용이 원문에 없다» 로 **통합 회차를 붉혔다**
         * 〔integrate 실측: engine 이 든 회차에서만 난다〕.
         * ★ 그 자가 무는 방향이 **둘**이다: ㉠ 적을 때 원문에 없던 것(내가 지어낸 것) ·
         *   ㉡ **적을 땐 맞았는데 남이 원문을 고쳐서 낡은 것** ← 이번은 ㉡ 이다.
         * ▶ ㉡ 의 처방은 «다시 따기» 가 아니다 — **그 문자열은 틀려서 지워진 것**이라
         *   되따면 틀린 것을 되살린다. **주장을 산문으로 옮긴다**(사실은 과거형으로 남는다).
         * 재 보니 `emitTransformHandler` 는 그 값을 **`evaluateExpression(expr, { input })`** 에
         * 그대로 넣고, `core/pipeline/expression.ts` 의 문법은 이렇다:
         * ```
         * 경로     input.foo.bar · output.status      리터럴  'text' · 123 · true · null
         * 비교·논리 == != > < && || !  괄호
         * `{{ }}`  **모른다** — 그건 프롬프트 쪽 문법이다(`llm/input-template.ts`)
         * ```
         * ▶ 곧 자리표대로 적으면 **실행 시점에 깨진다.** 「요약을 원본으로 읽었다」의 그 자리다.
         * ⚠ 한 제품 안에 **두 문법**이 있다(프롬프트 `{{…}}` · 식 `input.x`). 그래서 힌트가
         *   **그 둘을 갈라 말한다** — 안 가르면 사람이 프롬프트 버릇대로 적는다.
         */
        setHint:
          'Each row adds one field to this step’s output, computed from the merged input. ' +
          'Values are expressions like input.text — not prompt {{…}} templates. ' +
          'Wrap a constant in quotes.',
        fieldNamePlaceholder: 'field name',
        placeholder: 'e.g.: input.text',
        addField: 'Add field',
        /** 자리표는 **이름으로** 읽힌다 — 자리(1번째 칸)로 읽으면 줄이 바뀔 때 딴 것을 잡는다. */
        fieldName: (key: string) => `Output field name (${key})`,
        fieldExpression: (key: string) => `Expression for ${key}`,
        /**
         * ⚠ **«비었다» 를 «고장» 으로 읽히게 하지 않는다.** 칸이 없는 transform 은
         * 엔진에서 **통과 마디**이고 그건 정상 상태다(`emit.ts` 주석 원문:
         * *«상자만 놓고 아직 안 채운 자리이고, 그 상태로도 그래프는 뜻이 통한다»*).
         */
        emptyIsPassThrough: 'With no fields, this step passes its input through unchanged.',
        /**
         * **카드 부제 — 칸이 둘 이상일 때만** (gui-canvas 청 → gui 결정9424).
         *
         * 카드 폭이 **168px** 이라 세 꼴 중에서 골랐다〔그쪽 실측〕:
         * ```
         * ㈎ `shout: {{input.text}}`   한 칸이면 **안 잘린다** → 그때는 이 문구를 안 쓴다
         * ㈏ 식만 `·` 로 이음          ✗ **어느 칸의 식인지 사라진다** — 축을 지우는 꼴이다
         *                              (오늘 우리가 고친 그 병: 「낱말은 옳은데 이어 읽으면 거짓」)
         * ㈐ 칸 수                     ✔ 둘 이상이면 이것 — 짧고, 잘리지 않고, 거짓이 아니다
         * ```
         * ⚠ **«sets» 를 넣는다.** 그냥 «3 fields» 면 **입력 칸**으로 읽힌다 — 이 마디가 하는 일은
         *   출력 칸을 **만드는** 것이다(엔진의 `params.set` 이 그 이름 그대로다).
         * ⚠ 수를 뺀 «several fields» 로 쓰지 마라 — 사람이 인스펙터를 열지 말지 그 수로 정한다.
         */
        cardFieldCount: (n: number) => `sets ${n} fields`,
      },
      output: {
        format: 'Format',
        formatHint: 'How the final result is serialized before delivery to the target.',
        text: 'Text',
        json: 'JSON',
        target: 'Target',
        targetHint: 'Where the pipeline’s result is delivered — a Slack channel name, a file path, or similar sink.',
        targetPlaceholder: 'e.g.: #ops-alerts or /reports/daily.json',
      },
    },

    runStatusPanel: {
      runStatus: 'Run status',
      /*
       * ★ **«기록이 없다» 와 «안 돌았다» 는 다르다** (지시101 ①).
       *
       * 운영자 실측: `run_24496911` 의 `run_steps` 에 `sum`·`out` 은 있는데 **`t1` 은 줄이
       * 아예 없다.** 방아쇠는 분명히 돌았다 — 그래서 `sum` 이 돌았다. 그런데 화면이
       * `No record` 라고만 하니 *"세 칸 중 하나가 늘 회색"* 이고, **사람은 그것을
       * 고장으로 읽는다.**
       *
       * ⚠ 화면은 «돌았다» 고 **지어낼 수 없다** — 서버가 안 남긴 것을 콘솔이 만들면
       * 그게 결정423 이 막은 «콘솔이 서버 판정을 다시 만든다» 다. 줄을 남기는 것은
       * engine 몫이고(지시101 담당표), **여기서 할 수 있는 것은 «없음» 의 뜻을
       * 정확히 말하는 것**이다.
       */
      /**
       * **이 실행은 다른 판의 것이라 카드에 상태를 안 얹었다** (gui-canvas 청 → gui 결정9413).
       *
       * ## 왜 — **옳은 규칙이 화면에서 침묵이 됐다**
       *
       * 〔gui-canvas 실측 2026-09-07 KST, 목 판 `version-skew`〕 한 화면에서 셋이 이렇게 떴다:
       * ```
       * 도구줄   "Editing **v2**"
       * 실행 줄  "run-skew-1 **success** · n1 success"
       * 카드     실행 배지 **0개** — 아무 말도 없다
       * ```
       * 까닭은 옳다: **실행 판본이 보고 있는 판과 다르면 카드에 상태를 안 얹는다**
       * (그 실행은 이 그림의 실행이 아니다). 그런데 그 까닭이 화면에 없어서 사람은
       * *«성공했다는데 카드는 왜 비었나»* 로 읽는다 — 침묵이 «없다» 로 읽히는 그 자리다.
       *
       * ## 문구가 지켜야 할 것 셋
       * ```
       * ㉠ **경고가 아니다**   고치고 아직 안 돌렸으면 **늘 그렇다**. 붉히면 사람이 멀쩡한 것을 되돌린다
       * ㉡ **두 값을 다 적는다** «다른 판» 만 말하면 어느 것과 어느 것인지 모른다 —
       *                        실행 판본과 편집 중 판본을 **둘 다** 넣는다
       * ㉢ **«실패» 로 안 읽힌다**  그 실행은 성공했을 수 있다. 여기서 말하는 것은 «안 얹었다» 뿐이다
       * ```
       * ⚠ `Editing v{n}` 과 **같은 손**으로 `v` 를 붙인다(`toolbar.editingVersion`) — 한 화면에서
       *   판본 표기가 갈리면 사람이 다른 것으로 읽는다.
       * ⚠ 이 문구는 **판정을 담지 않는다.** «어긋났다/낡았다» 가 아니라 두 사실과 그 결과다
       *   (결정423 — 판정이 두 벌이 되면 어느 쪽이 맞는지 아무도 못 정한다).
       */
      fromOtherVersion: (runVersion: string, editingVersion: string) =>
        `This run is from v${runVersion} — you are editing v${editingVersion}, ` +
        `so the cards show no run state for it.`,
      noRecord: 'Not recorded',
      /** 왜 회색인지 — 없음이 실패가 아니라는 것을 그 자리에서 말한다. */
      noRecordHint:
        'This run saved no step for this node. That is not the same as “it did not run” — ' +
        'a step can be missing while the work happened.',
      unknownReason: 'Unknown reason',
    },

    /**
     * 지시35 — 모든 꼭짓점은 컴퓨팅 노드 안에 있어야 한다. 막을 때 **왜** 막았는지
     * 말한다. 아무 일도 일어나지 않으면 사용자는 클릭이 안 먹은 줄 안다.
     */
    placement: {
      needComputeNodeFirst:
        'Place a compute node first — every vertex has to sit on one. Drag one from COMPUTE NODES above.',
      outsideComputeNode: (kind: string) =>
        `A ${kind} has to sit inside a compute node. Drop it on one of the boxes.`,
      /*
       * 컨테이너 안의 컨테이너 (지시59, 결정311). **왜 막는지까지 말한다** — 그냥
       * "안 됩니다" 로 끝내면 사용자는 버그로 읽고 다시 시도한다.
       */
      /** Database Module 은 기계 위에 놓여야 한다 — 그 기계의 PostgreSQL 에 적기 때문이다(지시200). */
      databaseNeedsMachine:
        'A Database Module has to sit on a machine — it writes to the PostgreSQL on that machine. Drop it on one of the compute node boxes.',
      dockerInDocker:
        'A container cannot go inside another container. Drop it on the compute node itself, or put steps into the container that is already there.',
      /** 컨테이너를 뺄 때. 컴퓨팅 노드 상자와 같은 잣대다 — 안의 것을 먼저 치운다. */
      dockerNotEmpty: (count: number) =>
        `This container still has ${count} step${count === 1 ? '' : 's'} inside. Move or delete them first — removing the container would take them with it.`,
      computeNodeNotEmpty: (count: number) =>
        `This compute node still holds ${count} vertex${count === 1 ? '' : 'es'}. Move or delete them first — ` +
        `they cannot stay on the canvas without a compute node.`,
      /**
       * **자리 없는 카드를 대신 놓았다** (지시89 ③, 결정481).
       *
       * 저장된 배치에 좌표가 없는 카드가 있으면 화면이 빈 자리를 찾아 놓는다. 그것은
       * **아직 저장되지 않은 변경**이라 사람이 알아야 한다 — 조용히 놓고 말면 다음에
       * 다른 클라이언트가 열 때 또 좌표가 없다.
       *
       * 오류가 아니다. 화면은 제 할 일을 했고, 사람이 할 일은 `Save` 하나다.
       *
       * ## 🔴 «node» 라 적어서 **한 문구가 두 것을 덮었다** (gui-canvas 가 그림에서 잡음, 여기 결정9457)
       *
       * 이 수는 **셋의 합**이다 — `GraphCanvas.autoPlacedTotal`:
       * ```
       * autoPlacedCount        좌표 없는 **상자**
       * autoPlacedVertexIds    좌표 없는 **꼭짓점**
       * inferredBoxCount       `parentId` 로 **지어낸 상자** (결정7669)
       * ```
       * 그런데 «node» 는 사람에게 **꼭짓점**으로 읽힌다. 실물에서 그 차이가 났다
       * 〔gui-canvas 실측 2026-09-07 13:3x **UTC** · 내가 내 자로 되쟀다 같은 날 13:3x **UTC**〕:
       * ```
       * 발행본 `carewell-form-3-ver-2`  꼭짓점 **8/8 좌표 있음** · 좌표 없는 꼭짓점 **0**
       * 서로 다른 `parentId`            **둘** → 유도된 상자 둘
       * 화면 알림                        «**2 nodes** had no saved position …»
       * ```
       * ▶ 사람은 «내 마디 둘이 자리를 잃었다» 로 읽는데 **여덟 마디는 하나도 안 잃었다.**
       *
       * ### ⚠ 그런데 **그 «2» 를 발행본에서 재현하지 못했다** 〔gui 되잼 2026-09-07 13:5x **UTC**〕
       *
       * 이 고침의 근거는 **낱말이 셋을 덮는다**는 것이고 그건 위 코드가 곧바로 말한다.
       * 다만 «carewell 에서 2 가 떴다» 는 **관측은 내 자로 안 서 있다** — 발행본을 그대로
       * 술어에 태우니 셋 다 0 이었다(실물 `layout` 값을 그대로 먹였다):
       * ```
       * readUnplacedComputeNodes(발행본 layout)  → **0** (`x`·`y` 가 다 숫자다)
       * withInferredComputeBoxes(발행본)         → **같은 객체를 그대로 돌려준다** = 지어낸 상자 0
       * 꼭짓점                                    8/8 좌표 있음 = 0
       * ⇒ autoPlacedTotal **0** — 이 발행본에서는 알림이 **아예 안 떠야 한다**
       * ```
       * 〔같은 확인: `slack-echo`·`smoke-target-01` 도 `layout.computeNodes` 에 상자 좌표가 **있다**〕
       * ▶ 그래서 그 «2» 는 **발행본이 아닌 상태**(저장 전 · 판 도우미의 판)에서 나온 값이다.
       * ⚠ **이 고침은 그 관측에 매달려 있지 않다** — «셋의 합에 한 갈래 이름을 붙였다» 는
       *   코드로 참이다. 관측 쪽만 «내 자로 못 세웠다» 로 남긴다.
       * ★ 내가 처음에 상대의 곁값(«상자엔 저장된 좌표가 없으니 열 때마다 뜬다»)을 **재지 않고
       *   «참이다» 라고 되돌려 줬다.** 재니 이 세 발행본에서는 거짓이다. 정정했다.
       * ⚠ 그렇다고 «compute node» 로 바꾸면 **꼭짓점이 섞인 회차에서 거짓**이 된다
       *   (셋의 합이라 어느 한 갈래의 이름도 참이 아니다).
       * ▶ 그래서 **«card»** 다 — 상자도 꼭짓점도 화면에서 카드로 그려진다
       *   (`.compute-node-card` · `.graph-node-card`). **셋 다 참인 유일한 낱말**이고,
       *   사람이 실제로 보는 물건의 이름이다.
       * ★ 셈은 **옳다.** 결정7653 이 알림과 dirty 를 같은 수로 맞춘 것은 일부러다 —
       *   갈리면 «Save 를 눌러라» 가 죽은 Save 를 가리킨다. **로직은 건드리지 않았다.**
       */
      autoPlaced: (count: number) =>
        count === 1
          ? '1 card had no saved position — it was placed in a free spot. Save to keep it there.'
          : `${count} cards had no saved position — they were placed in free spots. Save to keep them there.`,
      /**
       * **정의가 그렇게 말해서 상자 안으로 들어간 꼭짓점** (gui-canvas 결정7610 이 청함,
       * 여기 결정4433).
       *
       * 왜 필요한가: `agent_defs.executionNodeId` 를 보고 화면이 꼭짓점을 그 기계 상자
       * **안으로 넣는다**(결정48). `parentId` 가 바뀌니 그래프가 dirty 가 되는데
       * **화면이 아무 말도 안 했다.** 결정481 의 규약은 *"dirty 로 올리고 **화면이 그
       * 사실을 말한다**"* 인데 **뒷문장이 안 지켜지고 있었다.** 사람은 «내가 뭘
       * 건드렸나» 를 찾다가 결국 **자기가 안 한 편집을 저장한다.**
       *
       * ⚠ 낱말 둘을 일부러 골랐다(그쪽이 청한 것이다):
       *  ① **«정의가 그렇게 말한다»** 가 드러나야 한다 — 화면이 **멋대로 옮긴 것이
       *    아니다.** 그래서 «moved» 가 아니라 «belong … as their definition says» 다.
       *  ② **«저장해야 남는다»** — 안 넣으면 사람이 그냥 닫고, 다음에 열면 또 같다.
       */
      wrappedIntoBoxes: (count: number) =>
        count === 1
          ? '1 node belongs inside a machine box, as its definition says — it was put there. Save to keep it.'
          : `${count} nodes belong inside machine boxes, as their definitions say — they were put there. Save to keep them.`,
    },

    /** 지시34 §1 — 파이썬으로 만든 transform·branch 를 고르는 칸. */
    pythonVertex: {
      label: 'Python script',
      hint: 'Runs a registered Python script that inherits our Transform/Branch base class. Same isolation as an MCP tool — not a sandbox.',
      useExpression: '(none — use an expression instead)',
      none: 'No Python vertices registered yet. Register one with POST /v1/tool-defs (vertexKind).',
      branchNote: 'Edges from this branch use the paths the script declares: node.<id>.path == \'<name>\'.',
    },

    computeNode: {
      sectionTitle: 'Compute nodes',
      sectionHint: 'Physical/virtual execution nodes (docs 03 §0-1). Drag one onto the canvas to see which agents run on it.',
      dragHandle: 'Drag onto the canvas',
      empty: 'No registered execution nodes.',
      notWired: 'Node registry is not wired up on the server (501) — notify an operator.',
      online: 'online',
      offline: 'offline',
      draining: 'draining',
      cpuOnly: 'CPU only',
      /*
       * **안 잰 것을 "CPU 뿐" 이라고 말하지 않는다** (지시77 gui 몫, 결정519).
       *
       * `FleetScreen` 과 **같은 결함이 여기에도 있었다**(결정518). 이쪽이 더 나쁘다 —
       * 이 카드는 **에이전트를 끌어다 놓는 상자**라, 안 잰 기계에 *"CPU 뿐"* 이라고
       * 적히면 GPU 가 달려 있어도 사람이 **거기 안 놓는다.**
       *
       * 표식은 `arch` 다. `uname -m` 한 번이 곧 *"그 기계에 다녀왔다"* 는 뜻이고,
       * 그것조차 없으면 나머지 빈 칸은 "없다" 가 아니라 **"모른다"** 다.
       */
      notMeasured: 'Not measured yet',
      /**
       * **공급자는 있는데 등록된 모델이 0개** (지시110, gui-canvas 청 → gui 결정8476).
       *
       * ## 왜 이 줄이 필요한가 — **침묵이 거짓말을 한다**
       *
       * 〔gui-canvas 실측 2026-09-06〕 그 상태를 상자가 그리면 **공급자칸 0 · 모델칩 0 ·
       * 구획 제목까지 사라진다.** 곧 「공급자는 서 있고 모델이 0개」가 **아무 말도 안 하는
       * 상태**가 되고, 사람은 그것을 **「이 기계엔 아무것도 없다」**로 읽는다.
       * ★ 지금 최우선 110 의 실물이 바로 그 모양이다 — `onprem-s-ai-pro6000-43` 은
       *   **떠 있고 비어 있다**(ollama 200 · `{"models":[]}`).
       *
       * ## ⚠ 문구가 지켜야 할 것 셋
       * ```
       * ㉠ 「없다」가 아니라 **「아직 0개」**   — 공급자는 **있다**. 기계를 지운 것이 아니다
       * ㉡ **경고가 아니다**                   — 갓 세운 기계의 정상 상태다. 붉히면 사람이 되돌린다
       * ㉢ 「모른다」와 안 섞는다              — 이건 **서버가 준 0**(`modelCount`)이지 못 잰 것이 아니다
       *                                         (못 잰 것은 위 `notMeasured` 가 맡는다)
       * ```
       */
      /**
       * 🔴 **㉣ 「installed」가 틀렸다 — 그 낱말이 세는 것과 다른 것을 셌다** (gui 결정9416).
       *
       * 종전: *«Provider is registered here, but no models are **installed** on it yet»*.
       * 〔실측 2026-09-07 KST〕 **`modelCount` 는 «깔린 것» 이 아니라 «등록부 행 수» 다**
       * (`api/routes/llm-providers.ts` 의 `deps.llmModels.listByProvider` 를 센 수다).
       * 그리고 지금 지시110 의 그 기계가 **정확히 그 갈림 위에 있다**〔env 실측, 대장 110 줄〕:
       * ```
       * 기계    `s-ai-pro6000-43` 의 `/api/tags` 에 `qwen3.5:122b-a10b-q4_K_M` · **81.37GB 받아 놨다**
       * 등록부  그 공급자의 `llm_models` 행 **0**  → `modelCount: 0`
       * 화면    "no models are **installed** on it yet"  ← **거짓이다.** 깔려 있다
       * ```
       * ★ 지시110 이 «사람이 되는 길을 안 된다로 읽는다» 고 적은 자리가 **바로 여기다** —
       *   깔아 놓고도 «안 깔렸다» 고 읽으면 **이미 끝난 내려받기를 다시 한다**(81GB다).
       * ▶ 그래서 **등록부의 말로** 적고, 그 갈림을 문장 안에서 말한다.
       *   ⚠ 위 ㉠㉡㉢ 은 그대로다: «없다»가 아니라 «아직 0개» · 경고 아님 · «모른다»와 안 섞음.
       */
      providerNoModels:
        'Provider is registered here, but no models are listed for it yet — ' +
        'a model can already be on the machine and still not be listed.',
      /**
       * **이 기계에 있다는 것이 «추측»일 때** (지시97 §1 계열, gui-canvas 청 → gui 결정8487).
       *
       * ## 이것은 새 판단이 아니다 — **내 파일이 이미 정해 둔 규칙**이다
       * ```
       * `apiTypes.LlmProviderRow`   *«`nodeIdSource` 를 뭉개지 마라 … **뭉개면 추론을 사실로 그린다**»*
       * `graphNode.derivedNode`     *«`declared` 면 사람이 적은 **사실**, `derived` 면 서버가
       *                               **주소로 추측**한 것〔`hostname === node.tailscaleAddr`〕»*
       * ```
       * 〔그때 실측〕 `llm_providers.node_id` 가 채워진 줄 0/5 — **뜨는 자리가 전부 추측인데
       * 전부 사실처럼** 그려지고 있었다. 그 자리를 꼭짓점 카드에서 먼저 고쳤다(결정1480).
       *
       * ## ★ 기계 상자는 **셋 중 가장 센 주장**을 한다
       * ```
       * 꼭짓점 카드    문장에 단서를 단다                 (결정1480)
       * 원격 호출 선   **안 긋는다** — 선은 단서를 못 단다 (결정4483)
       * 기계 상자      모델을 그 기계 **안에** 그려 넣는다  ← 여기가 제일 세다
       * ```
       * 〔gui-canvas 실측 2026-09-06〕 온프렘 `declared` **1** · `derived` **2** —
       * 곧 **상자 안 모델 묶음 셋 중 둘이 추측**인데 아무 표시가 없었다.
       *
       * ## ⚠ `declared` 에는 **안 붙인다** — 그것이 소음을 막는 규칙이다
       *
       * 확정이 기본이고 **추측일 때만** 단서를 단다(`derivedNode` 가 같은 손). 늘 붙는 표시는
       * 안 읽힌다. ⚠ 그리고 **경고가 아니다** — 정상 상태다. 붉게 그리면 멀쩡한 배치를 되돌린다.
       * ⚠ 구획 제목(*«…as the registry knows it»*)이 감싸는 것은 **장부의 앎**이지
       *   **이 기계라는 판단의 확신**이 아니다 — 두 물음이라 제목으로 안 닫힌다.
       *
       * ⚠ **낱말을 꼭짓점 카드와 같게 둔다**(`inferred from address`). 두 화면이 같은
       * 불확실성을 다른 말로 부르면 사람이 **다른 것**으로 읽는다.
       */
      providerNodeInferred:
        'Inferred from the address — nobody recorded that this provider runs on this machine',
      /*
       * ★ **«bound» 와 «placed» 를 사람 말로 바꾼다** (지시101 ③).
       *
       * 종전: `1 agent definition bound here` + `(0 placed)`.
       * 운영자: *"묶여 있는데 놓이지 않았다 는 것이 무슨 뜻인지 화면만 보고는 알 수
       * 없다."* 그리고 그 상자는 **방금 한국어 음성을 실제로 만든 기계**였는데
       * **아무것도 안 하는 것처럼 보였다.**
       *
       * 두 사실이 섞여 있던 것이 원인이다 — 갈라서 각각 말한다:
       *
       * ```
       * 등록부의 사실   이 기계에서 **도는** 정의가 몇인가      ← 실물이 하는 일
       * 캔버스의 사실   그중 **이 그림에 그려진 것**이 몇인가   ← 보기의 상태
       * ```
       *
       * ⚠ «0 placed» 가 «아무것도 안 한다» 로 읽히던 것이 사고의 핵심이다.
       * 그래서 캔버스 쪽 문장은 **이 캔버스에 대한 말**임을 문구 안에 박는다.
       */
      boundAgents: (count: number) =>
        `${count} agent definition${count === 1 ? '' : 's'} run on this machine`,
      /** 그중 몇이 이 그림에 있는가. **기계가 노는지와는 무관하다.** */
      placedOnCanvas: (placed: number, total: number) =>
        `${placed} of ${total} drawn on this canvas`,
      noBoundAgents: 'No agent definitions are bound to this node yet.',

      /* ── 아래 넷은 지시97 §4 — 이 기계에 떠 있는 모델을 상자 안에 그린다 (결정2429) ── */
      /** 구획 제목. **"등록부가 아는 만큼"** 이라는 단서를 문구가 지고 간다. */
      modelsHere: 'Models on this machine, as the registry knows it',
      /**
       * 가운데 겹의 설명.
       *
       * ⚠ **`Ollama` 라고 박지 않는다.** 등록부는 그 공급자가 Ollama 인지 vLLM 인지
       * **모른다**〔실측: `LlmProvider.kind` enum 이 anthropic·openai_compatible·google
       * 뿐이고, 목의 온프렘 셋은 전부 openai_compatible 이며 같은 줄의 카탈로그 이름은
       * 'On-prem vLLM' 이다〕. vLLM 위에 `ollama` 라 그리면 **화면이 거짓을 말한다.**
       *
       * 그래서 **공급자에 저장된 이름**을 그대로 넣는다 — 이름은 이미 사람이 정하는 값이다.
       */
      servedBy: (provider: string) => `Served by “${provider}”`,
      /** 크게 그린 모델. **이 파이프라인이 실제로 쓴다.** */
      usedByThisPipeline: 'This pipeline uses this model',
      /** 작게 그린 모델. 있지만 안 쓴다 — **없다는 뜻이 아니다.** */
      hereButUnused: 'On this machine, not used by this pipeline',
      /*
       * **무엇이 없어지는지 말한다** (지시89 ④, 결정482).
       *
       * 운영자는 `×` 를 누른 결과를 *"없앴더니 다른 게 생겼다"* 로 읽었다. 겹침은
       * 따로 고쳤지만(결정480), 그 전에 이 단추가 **무엇을 없애는지**가 분명해야 한다 —
       * 캔버스에서 치우는 것과 등록을 지우는 것은 되돌릴 수 있느냐가 다르다.
       *
       * 등록은 그대로다. 팔레트에 남아 있고 다시 끌어다 놓을 수 있다.
       */
      remove: 'Remove from canvas — the node stays registered',
      /** 치운 뒤 되돌릴 수 있게(지시89 ④). 이름을 말해 준다 — 무엇이 돌아오는지 알아야 한다. */
      removed: (display: string) => `Removed ${display} from the canvas. It is still registered.`,
      undoRemove: 'Undo',
      /*
       * ★ **꼭짓점을 지웠을 때** (gui-canvas 7709 물음 → gui 결정4474).
       *
       * gui-canvas 가 실물로 재서 물었다: 상자를 치우면 알림과 [되돌리기] 가 있는데
       * **카드를 지우면 아무 말도 없다.** 그런데 잃는 것은 카드가 더 크다 —
       * 상자는 자리와 크기, 꼭짓점은 `ref` 와 `params` **설정 한 벌**이다.
       * 방아쇠는 Delete **한 번**, 확인 없음.
       *
       * ⚠ **바로 위 상자 문구를 베끼지 않았다.** 저쪽은 끝에 안심을 단다
       * (*"It is still registered."*) — 잃은 것이 작기 때문이다. 꼭짓점은 반대라
       * 같은 자리에 안심을 두면 **거짓말**이 된다. 그래서 잃은 것을 말한다.
       */
      vertexRemoved: (name: string) => `Removed “${name}” — its settings went with it.`,
      /** 간선까지 끊긴 경우. **몇 개인지 말한다** — 안 말하면 다시 이을 때 빠뜨린다. */
      vertexRemovedWithEdges: (name: string, edges: number) =>
        `Removed “${name}” and ${edges} connection${edges === 1 ? '' : 's'} — its settings went with them.`,
      /*
       * ★ **아직 아무것도 안 정한 꼭짓점.** 여기서 «설정이 같이 갔다» 고 하면
       * **헛경보**다. 헛경보를 몇 번 보면 사람은 진짜 경보도 흘려 듣는다.
       */
      vertexRemovedUnconfigured: 'Removed a step that had nothing set yet.',
      offlineHint:
        'This node is offline right now. You can still place it — jobs routed to it wait in its queue until it comes back.',
      /**
       * **비우는 중** (결정483). `offline` 과 할 일이 반대다 — 그쪽은 기계를 켜라는
       * 것이고 이쪽은 **이미 켜져 있다.** 누군가 일부러 새 일을 안 받게 해 둔 것이다.
       *
       * 놓는 것은 막지 않는다. 비우는 중인 노드에 미리 그려 두는 것은 정당하고,
       * 그 판단은 지시33 §3 이 offline 에 대해 이미 내린 것과 같다.
       */
      drainingHint:
        'This node is being drained — it is up, but it is deliberately not taking new work. You can still place it; jobs wait until draining ends.',
      /** 지시36 §1 — tailnet 에는 있으나 nodes 에 없는 기기. */
      unregisteredHint:
        'Detected on the tailnet but not registered yet. Placing it registers it — capabilities stay empty until its agent reports them.',
      placement: {
        connected: 'connected',
        unregistered: 'unregistered',
        /*
         * **기계는 떠 있는데 실행기가 없다.** 종전에는 이것도 'offline' 이었다 —
         * 탐지가 방금 살아 있다고 확인한 기계를 꺼졌다고 부르는 셈이었다(실측:
         * lab-4090-1 은 Paseo 가 깔려 돌고 있는데 팔레트가 offline 이라고 했다).
         * 할 일이 다르다: offline 은 기계를 켜고, 이쪽은 node-agent 를 세운다.
         */
        no_executor: 'no agent',
        /*
         * **일부러 비우는 중** (결정483). 서버가 `nodes.status` 로 셋을 내는데 화면이
         * `draining` 을 `offline` 이라고 불렀다 — 켜져 있는 기계를 꺼졌다고 부른 것이다.
         */
        draining: 'draining',
        offline: 'offline',
      },
      /**
       * 지시37 §2 — tailnet 에서는 보이는데 실행기가 없는 상태. "offline" 만
       * 보여 주면 사용자는 등록이 실패한 줄 안다. 다음 행동을 말해 준다.
       */
      agentMissing: 'Reachable on the tailnet, but no node-agent yet — deploy one to run jobs here.',
      /*
       * ★ **가려진 것이 있다고 말한다** (gui-canvas 실측 → gui 결정5427, 지시89·96).
       *
       * 〔그쪽 실측〕 실행 겹침으로 카드가 12px 덮인다(아래 카드의 13%). 누르면 앞으로 온다.
       * ⚠ **잃는 것이 아니라 숨는 것**이다 — 그래서 «저장이 깨졌다»·«배치를 잃었다» 는
       * **안 쓴다.** 안 잃는다. (그쪽도 처음 «저장된 배치를 깬다» 라고 적었다가 스스로 정정했다)
       *
       * ⚠ 그런데 덮이는 자리에 있던 글이 **어긋남 줄**이었다
       * 〔실측: "ec2-control-1 ≠ s-ai-pro…"〕. 가려지는 것이 장식이면 안 말해도 되지만
       * **경고면 말해야 한다.** 「가려진 것은 없는 것이다」 — 그래서 낸다.
       *
       * ⚠ 옮겨서 겹침을 푸는 길은 **안 고른다** — 지시38 ① 이 사람이 정한 자리를
       * 흔드는 것을 막는다. 말해 주고 **사람이 누르게** 한다.
       */
      runOverlayCovers: (count: number) =>
        `Run details make cards taller — ${count} card${count === 1 ? ' is' : 's are'} partly covered. ` +
        'Click a card to bring it to the front.',
      /*
       * ★ **«없다» 가 아니라 «못 찾는다»** (gui-canvas 실측 → gui 결정5427, 지시96 ④).
       *
       * ⚠ «machine» 이라 단정하지 않는다 — 같은 자리에 **지워진 컨테이너 상자**도 온다
       * (그쪽 결정7674 · 실측: ollama 상자가 지워진 판에서 같은 갈래를 탄다).
       *
       * ⚠ 그쪽 초안은 *"This id is not in the fleet"* 이었는데 **그쪽이 스스로 단 주의와
       * 어긋난다** — *"«없다» 가 아니라 «못 찾는다» 로"*. `is not in the fleet` 은
       * **없다는 단정**이다. 우리가 아는 것은 «우리가 받은 목록에 없다» 까지고,
       * 서버가 늦게 알려 줄 수 있다(결정6497 과 같은 잣대). 그래서 고쳐 냈다.
       */
      idNotInLoadedFleet: (id: string) =>
        `“${id}” is not in the fleet list we loaded. It may have been removed, or the list may not have it yet.`,
      registerFailed: (reason: string) => `Could not register this device as a compute node — ${reason}`,
      placementHint: {
        connected: 'Registered and reporting heartbeats.',
        unregistered: 'Seen on the tailnet, not registered. Place it to register.',
        // 다음에 할 일이 offline 과 다르다 — 켜는 것이 아니라 세우는 것이다.
        no_executor: 'Registered and reachable, but no node-agent is reporting. Install one to run jobs here.',
        // 켜져 있다. 끄라거나 켜라는 말이 아니라 **왜 새 일이 안 가는지**를 말한다.
        draining: 'Up, but deliberately not taking new work while it drains.',
        offline: 'Known, but not reachable right now.',
      },
      alreadyPlaced: 'Already on the canvas.',
      /*
       * 끌 수 없는 이유를 말한다 ("조용히 실패하지 마라" ③④). 실행 노드 id 도 tailnet
       * 주소도 없는 것을 끌면 종전에는 캔버스가 조용히 아무것도 안 했다 — 사용자는
       * 자기가 잘못 끈 줄 안다.
       */
      notPlaceableHint:
        'Not a compute node yet — finish adding it first. Nothing can run here until its node agent is installed.',
    },

    /**
     * 설치 대기 대상 (지시58). **컴퓨팅 노드와 다른 구역**이라 문자열도 따로 둔다 —
     * `computeNode` 밑에 두면 다음 사람이 같은 것으로 읽는다.
     */
    pendingTargets: {
      sectionTitle: 'Pending setup',
      sectionHint:
        'Machines added over SSH that are not compute nodes yet. They cannot run anything until a node agent is installed.',
      state: {
        awaiting_trust: 'untrusted',
        awaiting_install: 'not installed',
      },
      stateHint: {
        awaiting_trust:
          'Added over SSH, but its host key has not been trusted yet. Check the fingerprint before anything is installed.',
        awaiting_install:
          'Trusted, but the node agent has not been installed yet. Install it to turn this into a compute node.',
      },
      /**
       * **못 읽었다 ≠ 대기 중인 것이 없다** (지시70, gui 결정8498).
       *
       * 이 구역은 «대기 중인 것이 없으면 구역째 안 그린다» 로 지어져 있다 — 평소엔 그것이 옳다.
       * 그런데 **목록 조회가 실패해도 똑같이 안 그려졌다**〔실측 2026-09-06: `provisionTargets` 가
       * 이 파일의 **유일한 조회인데 `isError` 를 한 번도 안 본다**; 실패하면 `pendingTargets(…, undefined)`
       * 가 **빈 배열**을 내고 구역이 사라진다〕.
       *
       * ⚠ **하필 이 구역이 그러면 안 된다.** 이 구역이 있는 까닭이 *«등록했는데 아무 데도
       * 안 나타난다»* 를 고치는 것이다 — 그 사람이 보는 화면에서 **조용히 사라지면**
       * «내 기계가 등록이 안 됐구나» 로 읽는다. 실제로는 **우리가 못 읽은 것**이다.
       * ★ 지시70(«기계가 오면») 의 첫 걸음이 정확히 그 흐름이다.
       *
       * ⚠ «없다» 라고 말하지 않는다 — 우리는 **몇 개인지 모른다.**
       */
      loadError: 'Could not read the SSH targets — machines waiting for setup may be missing here.',
      /** 다음 걸음. 같은 다이얼로그를 열지만 지금 할 일이 다르다. */
      reviewFingerprint: 'Check fingerprint…',
      installAgent: 'Install node agent…',
    },

    /** 컨테이너 상자 꼭짓점 (지시59). */
    dockerCard: {
      empty: 'Empty — drop steps in here to run them in this container.',
      contains: (n: number) => `${n} step${n === 1 ? '' : 's'} inside`,
      /** 정의를 못 찾았다. id 를 이미지 이름인 척 보여 주지 않는다. */
      unknownDef: (ref: string) => `Unknown container definition (${ref})`,
      noDef: 'No container definition selected',
      /**
       * **«정의를 안 골랐다» 와 «골랐는데 필수 칸이 비었다» 는 다른 상태다** (지시146 ㉠,
       * gui-canvas 청 → gui 결정9430).
       *
       * 〔gui-canvas 실측 2026-09-07 KST, 목 판 전수〕 필수 칸을 가진 마디 **2** ·
       * 그중 빈 것 **1**(`k-ollama` 의 `models`). 그런데 **둘 다 같은 문장**(`noDef`)을 받는다 —
       * 채운 상자와 안 채운 상자가 화면에서 **구별되지 않는다.**
       * ▶ 그래서 **수를 담아** 말한다. «비었다» 만 쓰면 «아무것도 없다» 로 읽힌다 —
       *   오늘 이 저장소에서 세 번 고친 그 병(«접힌 것·빈 것을 «없는 것» 으로 읽히게 하지 마라»).
       * ⚠ 이 셈은 **화면이 새로 만드는 판정이 아니다**: `defineForm` 이 이미
       *   `f.required && f.default === undefined` 로 같은 규칙을 쓰고 있고, 서버가 그 답을
       *   내는 라우트는 **없다** — 곧 두 벌이 될 판정이 애초에 없다(결정423 과 안 부딪친다).
       */
      /**
       * ★ **이름이 수보다 낫다 — 실물 값이 그렇게 말했다** 〔gui-canvas 실측 → gui 결정9431〕.
       *
       * 나는 처음에 «수를 담아라» 를 규칙으로 냈다(«비었다» 만 쓰면 «없다» 로 읽힌다).
       * 그런데 그쪽이 목 전수를 재서 값을 보냈다: **빈 칸이 1개인 마디 1 · 2개 이상 0.**
       * ```
       * 이름  «Models is required.»       → 사람이 **어디를 열지** 안다
       * 수    «1 required fields empty»   → 몇 개인지만 안다. 그리고 「1 fields」 는 셈이 틀린 걸로 읽힌다
       * ```
       * ▶ **이름이 들어가면 이름을 쓴다.** 수는 «이름이 안 들어갈 때» 의 물러남이다.
       *   내 앞 규칙은 «비었다는 사실만 말하지 마라» 였고, **이름은 그 걱정을 아예 안 만든다** —
       *   더 센 쪽으로 갈린 것이라 뒤집는 것이 맞다.
       * ⚠ 하나일 때는 **새 문구를 안 쓴다** — 정의 만들 때 쓰던 `palette.defineRequired(label)`
       *   («Models is required.»)가 **같은 물음**에 답한다. 문구를 새로 지으면 같은 말이 두 벌이 된다.
       */
      requiredEmptyTwo: (a: string, b: string) => `${a} and ${b} are required.`,
      /** 셋 이상 — 카드 폭(168px)에 이름이 다 안 든다. 그때만 수로 접는다. */
      requiredEmpty: (n: number) => `${n} required fields are empty.`,
      /**
       * **비었는데 정상인 상태** — `transform` 의 칸 0 과 같은 부류다(결정9423).
       * 카드가 좁아(168px) 인스펙터 문장(`emptyIsPassThrough`)은 못 쓴다.
       */
      passesThrough: 'passes through',
    },

    /**
     * 설치 동의 (상시 규칙 — "남의 기계에 무언가 깔 때는 반드시 묻는다").
     *
     * **"설치할까요?" 만 띄우지 않는다.** 무엇을·어디에·권한이 필요한지를 적는다.
     * 홈 아래에 푸는 것과 시스템을 바꾸는 것은 사용자에게 전혀 다른 일이라,
     * 그 차이를 문구로 드러낸다.
     */
    /** 설계 적용 (지시65 ②③). 되돌릴 수 없는 쓰기 앞에서 확인 없이 들어가지 않는다. */
    /** 설계 등록 (지시65 ②). 무엇이 담기고 무엇이 빠지는지 먼저 보여 준다. */
    registerDesign: {
      title: (node: string) => `Register what is on ${node} as a design`,
      contains: (modules: number, edges: number) =>
        `This saves ${modules} module${modules === 1 ? '' : 's'} and ${edges} connection${edges === 1 ? '' : 's'}.`,
      /*
       * **빠지는 것을 말한다** (지시65). 담으면 매달린 엣지가 되므로 담지 않는데
       * (결정171 부류), 말하지 않으면 이었던 선이 왜 사라졌는지 모른다.
       */
      droppedEdges: (count: number) =>
        `${count} connection${count === 1 ? '' : 's'} leaving this node ${count === 1 ? 'is' : 'are'} not saved — the other end is not part of the design, so keeping ${count === 1 ? 'it' : 'them'} would leave a dangling edge when applied.`,
      nameLabel: 'Name this design',
      /** 중복은 **내용 해시**로 서버가 판정한다 — 이름은 사람이 찾기 위한 것이다. */
      nameHint:
        'Used to find it later. Duplicates are decided by content, not by name — saving the same contents twice does not create a second design.',
      nothingToRegister: 'There is nothing on this node yet, so there is nothing to save.',
      register: 'Register design',
      registering: 'Registering…',
      cancel: 'Cancel',
      /*
       * **등록 결과는 세 가지가 다른 말이다** (지시65 ④, 결정448).
       *
       * 서버가 `created`·`revived`·`dropped` 를 따로 준다. 전부 "저장했습니다" 로
       * 뭉치면 두 가지를 잃는다 — ① 같은 내용이 이미 있어서 **아무 일도 안 일어난**
       * 경우를 새로 만든 것과 구분 못 하고, ② 서버가 매달린 엣지·설계 밖 부모를
       * **떼어냈다**는 사실이 사라진다. ②는 등록이 성공했는데도 담긴 것이 보낸 것보다
       * 적다는 뜻이라 조용히 넘기면 안 된다(`docs/03` §0-2).
       */
      savedNew: (name: string) => `Registered “${name}”.`,
      savedRevived: (name: string) =>
        `“${name}” already existed as an archived design with the same contents — it is active again. Nothing new was created.`,
      savedDuplicate: (name: string) =>
        `The same contents are already registered as “${name}”, so nothing was created. Duplicates are decided by content, not by name.`,
      serverDropped: (edges: number, parents: number) => {
        const bits: string[] = [];
        if (edges > 0) bits.push(`${edges} connection${edges === 1 ? '' : 's'}`);
        if (parents > 0) bits.push(`${parents} container link${parents === 1 ? '' : 's'}`);
        return `The server left out ${bits.join(' and ')} because the other end is not part of the design — applying it will not recreate ${bits.length === 1 ? 'them' : 'those'}.`;
      },
    },

    applyDesign: {
      title: (design: string, node: string) => `Apply “${design}” to ${node}`,
      creates: (modules: number, edges: number) =>
        `This creates ${modules} module${modules === 1 ? '' : 's'} and ${edges} connection${edges === 1 ? '' : 's'}.`,
      modeLegend: 'What to do with what is already there',
      /** **기본값이다** — 되돌릴 수 없는 쪽을 기본값으로 두지 않는다. */
      modeMerge: 'Keep what is there and add these (default)',
      modeReplace: 'Remove what is there first',
      /*
       * "덮어씁니다" 만으로는 무엇을 잃는지 모른다 — **개수와 종류를 센다.**
       */
      willDelete: (count: number) =>
        `This deletes ${count} module${count === 1 ? '' : 's'} that are on this node right now: `,
      nothingToDelete: 'There is nothing on this node yet, so nothing gets deleted.',
      applyMerge: 'Add to this node',
      applyReplace: 'Replace what is on this node',
      applying: 'Applying…',
      cancel: 'Cancel',
      /** 적용 뒤 캔버스는 **아직 저장되지 않았다** — 그 사실을 말한다(결정448). */
      applied: (design: string, created: number) =>
        `Applied “${design}” — ${created} module${created === 1 ? '' : 's'} added. This is not saved yet; use Save on the toolbar.`,
    },

    installConsent: {
      whatLabel: 'What gets installed',
      whereLabel: 'Where it goes',
      /**
       * **자격증명을 정하는 설치는 그 사실을 시작 전에 말한다** (지시99 ①).
       * 운영자 말: *"플랫폼이 만든 비밀을 사용자에게 되묻는 것은 앞뒤가 뒤집힌 것이다."*
       * 되묻지 않으려면 **먼저 말해야** 한다.
       */
      credentialLabel: 'Credential this sets',
      needsAdmin: 'This needs administrator rights on that machine.',
      /** 홈 아래 — 되돌리기가 깨끗하다. 그 사실이 사용자를 안심시키는 정보다. */
      homeNote:
        'Everything goes under your home directory and runs as a user service. Removing it deletes those files — nothing outside your home is touched.',
      /**
       * 시스템 — **되돌리기가 깨끗하지 않다.** 되돌릴 수 있는 척하지 않는다(지시64 ④).
       */
      systemNote:
        'This changes the machine itself, so it cannot be undone completely — removing Docker later will not restore the machine to exactly how it was. On a shared lab machine, check with whoever else uses it first.',
    },

    /**
     * 프로비저닝 단계 목록 (지시72, 결정450). 세 다이얼로그가 **같은 목록**을 쓴다.
     */
    provisionSteps: {
      /*
       * 실패한 단계는 접지 않으므로 이 문구를 안 쓴다 — **성공한 단계에만** 붙는다.
       * "Show output" 이라고만 하면 실패 출력도 접혀 있는 줄 오해할 수 있어
       * 무엇의 출력인지 말한다.
       */
      showOutput: 'Show this step’s output',
    },

    contextMenu: {
      paneTitle: 'Add to canvas',
      computeNodeTitle: (display: string) => `Add inside ${display}`,
      addBoundAgent: (name: string) => `Place “${name}”`,
      addAgentDirect: 'Add agent (direct prompt)…',
      noBoundAgentsHint: 'No registered agent definition is bound to this node — bind one via its agent definition, or add an unbound prompt below.',
      /** 지시47 ⑤ — 꼭짓점 우클릭. 무엇을 우클릭했는지 제목이 말해 준다. */
      vertexTitle: (kindLabel: string) => `${kindLabel} vertex`,
      /** 설계 등록·적용 (지시65, 결정448). */
      registerDesign: 'Register contents as a design…',
      registerPreset: 'Register as a preset…',
      applyDesign: (name: string) => `Apply design “${name}”…`,
      noDesignsHint:
        'No design is registered yet — right-click a compute node with something on it and choose “Register contents as a design”.',
      cancel: 'Cancel',
    },

    quickCreate: {
      title: (kindLabel: string) => `New ${kindLabel}`,
      create: 'Create',
      cancel: 'Cancel',
      nameHint: 'Shown on the card so you can tell vertices of the same kind apart.',
    },

    /**
     * 캔버스 안 컴퓨팅 노드에 Paseo 데몬·세션을 보여 주는 화면 문구(지시24·25 콘솔측).
     *
     * 상태 문구를 지어내지 않는다 — 서버가 넷으로 나눠 주는 상태(unregistered ·
     * daemon_detected · connected · not_detected)를 그대로 옮긴다. 특히
     * `daemon_detected` 는 "데몬은 있는데 자격증명이 없어 **읽지 못했다**" 는 뜻이므로,
     * 세션이 없다고 말하지 않고 "연결이 필요하다"고 말한다.
     */
    /**
     * node-agent 프로비저닝 (지시42 ③). "id/password 만 넣으면 나머지는 플랫폼이 한다".
     */
    provision: {
      /**
       * **잊은 데몬 비밀번호** (지시99 (2)6~8, 결정6467).
       *
       * ⚠ **되찾기와 재설정은 다른 일이다.** 되찾기는 아무것도 안 바꾸고, 재설정은
       * **데몬을 재시작한다.** 합쳐 두면 «되찾아 볼까» 를 누른 사람이 재시작을 겪는다 —
       * 그래서 계약이 라우트를 둘로 갈랐고, 화면도 그렇게 갈랐다.
       */
      secret: {
        heading: 'Forgot the daemon password?',
        /** 되찾기는 **안전하다**는 것을 먼저 말한다 — 안 그러면 누르기를 겁낸다. */
        readHint:
          'This only compares what is on that machine against the credentials you already have. Nothing is changed and the password itself never leaves the node.',
        read: 'Check stored credentials',
        reading: 'Checking…',
        /** ★ 셋을 갈라 말한다 — 사람이 할 일이 서로 다르다. */
        matched: (name: string) =>
          `That machine is using ${name}. Nothing needs to change on the node — re-attach that credential to the node.`,
        noMatch: (tried: number) =>
          `None of the ${tried} credential(s) tried match what is on that machine. Resetting is the only way back in.`,
        noMatchUnknownCount:
          'None of the credentials tried match what is on that machine. Resetting is the only way back in.',
        /** ⚠ **«못 읽었다» 는 «안 맞는다» 가 아니다.** */
        unreadable:
          'The value on that machine could not be read, so we cannot say whether your credentials match it.',
        readFailed: 'Could not check the stored credentials',
        /**
         * ⚠ **«못 읽었다» 의 까닭이 대개 여기 있다.** 서버가 파일 권한을 함께 준다 —
         * 안 그리면 사람이 «왜 못 읽지» 를 원격에 들어가서야 안다.
         */
        perm: (mode: string) => `env file permissions: ${mode}`,

        /** 재설정 — **되돌릴 수 없고 데몬이 재시작된다.** */
        resetHeading: 'Set a new daemon password',
        resetFacts: [
          'The env file on that machine is rewritten and the daemon is restarted.',
          'Anything currently talking to that daemon is disconnected.',
          'The new value comes from a credential you pick here — it is never typed into this screen.',
        ],
        newCredentialLabel: 'New password credential',
        newCredentialHint: 'Register the credential first, then pick it here.',
        /**
         * ★★ **고를 것이 하나도 없을 때** (지시99 ①, gui 결정9444 · env 가 값으로 짚어 줬다).
         *
         * ## 왜 — 단추 둘이 잠기고 **까닭을 한 마디도 안 했다**
         *
         * 자격증명 0 개로 이 판을 그려 봤다 〔실측 2026-09-07 KST〕:
         * ```
         * 고를 것    **1개** — 자리표시(«New password credential») 뿐
         * 단추       «Check stored credentials» **잠김** · «Reset the password» **잠김**
         * 화면의 말  **없다.** 그러면서 위 문장은 «고른 자격증명에서 온다» 고 한다 — **모순이다**
         * ```
         * ⚠ 그리고 이것이 **목의 기본 상태**다 — `mockCredentials` 가 빈 배열로 시작한다.
         *   곧 이 막다른 길은 «드문 갈래» 가 아니라 **처음 열면 보이는 것**이었다.
         *
         * 계약이 `newPasswordCredentialId` 를 **필수**로 두어서(env 실측: 빼면 400) 자격증명이
         * 없으면 이 일은 **시작조차 안 된다.** 잠긴 단추가 아니라 **문장**이 필요하다.
         *
         * ▶ 결정8436 의 잣대를 그대로 쓴다 — *«비면 어디서 등록하는지 말한다»*. 실측한 자리다:
         *   `ResourcesScreen` 이 `CredentialsPanel` 을 그리고 그 제목이 `resources.credentials.title`
         *   = **«Credentials»** 다. ⚠ 그 자리가 바뀌면 이 문장이 거짓말이 된다 —
         *   `noCredentialsIsNotADeadEnd` 의 마지막 검사가 **그 두 값이 같은지** 를 문다.
         */
        noCredentials:
          'You have no stored credentials yet, so there is nothing to check against and nothing to set. ' +
          'Register one first in Resources → Credentials, then come back here.',
        /**
         * ★★ **«하나도 없다» 와 «다 죽었다» 를 가른다** (gui 결정9447 · env 가 짝을 짚어 줬다).
         *
         * 〔도는 판 실측 2026-09-07 KST〕 자격증명 **26행** 중 `verdict.usable === false` 가 **9행**.
         * 곧 «폐기된 것만 남은 계정» 은 지어낸 갈래가 아니다.
         *
         * ⚠ 둘을 한 문장으로 뭉치면 **이미 등록해 둔 사람이 하나 더 만든다** — 자격증명은
         *   되돌리기 어려운 물건이다. 할 일이 다르다: 앞은 «등록하러 가라», 뒤는 **«새로 발급하라»**.
         * ⚠ **수를 적는다.** «다 못 쓴다» 만 말하면 몇 개를 되살려야 하는지 모른다.
         * ⚠ 까닭(폐기냐 만료냐 상류냐)은 **안 적는다** — 서버 판정이고 여기서 뭉개면 틀린 말이 된다.
         */
        allCredentialsUnusable: (n: number) =>
          `All ${n} of your stored credentials are unusable right now, so none can be set as the new password. ` +
          'Issue a new one in Resources → Credentials, then come back here.',
        acknowledge: 'I understand the daemon will be restarted',
        reset: 'Reset the password',
        resetting: 'Resetting…',
        /** ok — 이제 매핑을 바꾼다. */
        resetOk: 'The daemon answered with the new value. Re-attach the credential to the node now.',
        /**
         * ⚠ 데몬이 값을 **어떤 꼴로 받는가**(`bearer` 헤더 / `query` 인자).
         * 다른 것이 그 데몬에 붙어야 할 때 필요한 사실이라, 서버가 준 것을 버리지 않는다.
         */
        shape: (how: string) => `The daemon takes it as: ${how}`,
        /** ⚠ **아무것도 안 바뀐 실패** — 다시 해 보면 된다. */
        rolledBack: (why: string) =>
          `Nothing on that machine was changed (the change was rolled back): ${why}`,
        /** ★★ **반쯤 바뀐 상태.** «실패» 로 뭉치면 사람이 그 기계를 보러 가지 않는다. */
        dirty: (why: string) =>
          `HALF-CHANGED: the reset failed AND rolling it back failed — ${why}. That machine is in an unknown state; open the provisioning run below and fix it there.`,
        runLink: (provisionId: string) => `Provisioning run: ${provisionId}`,
        resetFailed: 'The reset did not finish',
      },
      menuItem: 'Provision node-agent…',
      title: (node: string) => `Provision node-agent on ${node}`,
      hint:
        'The platform will SSH in and install everything: Node runtime, the agent bundle, config, and a user-level systemd service. You only supply how to log in.',
      hostLabel: 'Host (tailnet address)',
      portLabel: 'SSH port',
      usernameLabel: 'SSH username',
      /*
       * ⚠ **여기 있던 문구 셋(`authLabel`·`authPassword`·`authKey`)을 지웠다** (결정5450).
       *
       * *"How to authenticate / Password / Existing credential"* — 비밀번호와 키를 고르는
       * **라디오**를 그리려고 적힌 것이고, `6dece9ed`(지시42 ③) 때부터 있었다.
       * 〔실측〕 그 라디오는 **한 번도 안 그려졌다**(결정5448 의 자가 짚었다) —
       * 그리고 **그릴 수가 없다**:
       *
       * ```
       * UpsertProvisionTargetBody   targetId host port username **credentialId** allowed
       *                             → 비밀번호 칸이 **없다.** 자격증명 id 뿐이다
       * ```
       *
       * 비밀번호를 받는 계약은 딴 데 있고(`InstallPaseoBody.password`) 그쪽 폼은
       * 제 문구(`installPassword`)를 이미 쓴다. 즉 이 셋은 **버려진 설계의 잔재**다.
       * 남겨 두면 다음 사람이 «있는 걸 왜 안 그리나» 하고 **400 맞는 길을 짓는다.**
       */
      passwordLabel: 'SSH password',
      credentialLabel: 'Credential',
      /*
       * ★ **고르면 실패하는 선택지에 사유를 붙인다** (결정5450).
       *
       * 〔실측 `provisioning/service.ts` 의 `assertProvisioningCredentialKind`〕 서버는
       * **`ssh_key` 아니면 `ssh_password` 만** 받고 나머지는 `InvalidCredentialKindError` 다.
       * 그런데 이 드롭다운은 `credentials.data` 를 **그대로** 냈다 — `llm_api_key`·
       * `git_pat`·`slack_bot_token` 까지 고를 수 있었고, 고르면 저장에서 거절당한다.
       * 「고르면 실패하는 선택지를 목록에서 걸러 낸다」(결정522·546·575)
       *
       * ⚠ **감추지 않고 사유를 붙여 잠근다.** 감추면 *"내 자격증명이 왜 안 보이나"* 가
       * 되고, 그건 「가려진 것은 없는 것이다」 를 내가 만드는 일이다.
       */
      credentialKindNotUsable: 'SSH only — this kind cannot log in',
      /**
       * ★ **못 읽은 것을 «등록 안 됨» 으로 말하지 않는다** (결정3474 의 잣대 → 결정5451).
       *
       * 이 창은 `existing = targets.data?.find(…)` 로 «이미 등록됐나» 를 판정했는데,
       * 조회가 **실패해도 `undefined`** 다. 그러면 **이미 등록된 노드에 등록 폼을
       * 내민다** — 사람이 채워 보내면 중복 등록 오류를 맞거나, 더 나쁘면 접속 정보를
       * 덮어쓴다.
       *
       * 「빈 것·없는 것·아직 안 온 것·못 읽은 것은 다 다르다」 — 셋을 갈라 말한다.
       */
      targetsLoading: 'Checking whether this node is already registered…',
      targetsUnreadable:
        'Could not read the registered nodes, so this screen cannot tell whether this one is already set up. Registering again could overwrite its access details — reload before continuing.',
      /** 조회 중 — "등록된 것이 없다" 와 다른 말이어야 한다. */
      credentialLoading: 'Loading credentials…',
      /**
       * ⚠ **못 읽은 것도 «없다» 가 아니다** (결정3476). 이 자리는 «오는 중» 은 이미
       * 가르고 있었는데(바로 위 `credentialLoading`) **실패는 안 갈랐다** — 그러면
       * *"register one under Resources first"* 라고 **시킨다.** 이미 있는 것을 또 만들러 간다.
       */
      credentialUnreadable:
        'Could not load stored credentials — try reloading. This does not mean none are stored.',
      credentialNone: 'No stored credentials — register one under Resources first.',
      /** 비밀번호 경로에 무엇이 저장되는지 화면에 밝힌다(지시42 ④). */
      passwordStored:
        'The password is encrypted with CREDENTIALS_KEY and stored as a credential. It is used for every connection to this host until you replace it with a key.',
      nodeIdLabel: 'Node ID',
      environmentIdLabel: 'Environment ID',
      dbCredentialLabel: 'DATABASE_URL credential',
      apiKeyCredentialLabel: 'API key credential (for the bundle download)',
      capabilityTitle: 'Advertised capability',
      gpuModelLabel: 'GPU model',
      gpuCountLabel: 'GPU count',
      vramLabel: 'VRAM per GPU (GB)',
      /** 장당 값이라는 것을 화면에서 말한다 — 총합을 적으면 잡이 잘못 라우팅된다. */
      vramHint: 'Per card, not the total. A job asking for more VRAM than one card has cannot run here.',
      archLabel: 'Architecture',
      /*
       * 서버가 잰 값 (engine 결정1315, 읽기는 결정462). **잰 것과 안 잰 것을 가른다** —
       * `null` 을 `x86_64` 로 접으면 엉뚱한 바이너리가 그 기계에 올라간다.
       */
      archMeasured: (value: string) => `Measured on that machine: ${value}. Change it only if you know it is wrong.`,
      archNotMeasured:
        'Not measured yet — the platform has not run uname on that machine. Leave it empty rather than guessing; a wrong value here picks the wrong binary to install.',
      labelsLabel: 'Labels (comma separated)',
      register: 'Register target',
      run: 'Install now',
      running: 'Installing…',
      revert: 'Roll back',
      reverting: 'Rolling back…',
      /** 호스트키는 조용히 신뢰하지 않는다(안전장치2). */
      fingerprintTitle: 'Host key fingerprint',
      fingerprintNew:
        'This is the fingerprint recorded on the first connection. Confirm it matches the machine before you trust it — it is compared on every later connection and never updated automatically.',
      fingerprintNone: 'Not recorded yet — it is captured on the first connection.',
      allowedOn: 'Allowed',
      allowedOff: 'Not allowed — the platform will refuse to connect until you allow it.',
      allow: 'Allow this target',
      stepsTitle: 'Steps',
      nodeStatusLabel: 'Node status after install',
      nodeStatusOnline: 'online — the agent is reporting heartbeats',
      nodeStatusNotOnline:
        'The service started but the node is not online yet. The heartbeat has not arrived — check the last step’s output.',
      /*
       * **적어도 번호는 보여 준다** (결정1464).
       *
       * 이 대화상자는 apply 응답을 **지역 상태로만** 들고 있다 — 닫거나 새로고침하면
       * 진행 중인 프로비저닝이 화면에서 사라지고 **다시 열 길이 없다**(`getProvisionRun`
       * 을 부르는 곳이 0 이다〔실측〕). 그 큰 구멍은 화면 설계 판단이 붙어서 따로 올렸다.
       *
       * 그때까지라도 **번호는 손에 쥐여 준다.** 사람이 서버·로그에서 그것으로 찾는다.
       * 번호마저 없으면 사라진 뒤에 **아무 단서도 없다.**
       */
      runIdLabel: 'Provision run',
      failed: 'Installation failed',
      failedHint: 'The step that failed is where to look. Roll back to undo the steps that did succeed.',
      close: 'Close',
    },

    paseo: {
      sectionTitle: 'Paseo',
      sectionHint:
        'Paseo is the agent daemon that can run on this execution node. The console probes port 6767 over the tailnet to see whether it is there and whether we are allowed to read it.',

      stateLabel: {
        connected: 'Connected',
        daemon_detected: 'Connection required',
        not_detected: 'No daemon',
        offline: 'Offline',
        unregistered: 'Unregistered daemon',
        /**
         * ⚠ **까닭을 못 가를 때만 쓴다** (gui 결정8477). 아래 `unknownLabel` 을 먼저 본다 —
         * 안 그러면 이름표가 **바로 밑 설명과 서로 다른 말**을 한다(그 사고가 실제로 났다).
         */
        unknown: 'Not scanned',
      },
      /**
       * **까닭을 아는 만큼 이름표도 갈라 말한다** (지시69 의 나머지 반쪽, gui 결정8477).
       *
       * 〔gui-canvas 실측 2026-09-06 → 내가 원문에서 확인〕 한 상자에 이 둘이 **같이** 떴다:
       * ```
       * 이름표   Paseo **Not scanned**                      ← `stateLabel.unknown`
       * 설명     The tailnet **was scanned**, but nothing…  ← `unknownWhy.address_mismatch`
       * ```
       * ▶ **정면으로 어긋난다.** 지시69 가 «아는 만큼 갈라 말한다» 로 **까닭은 갈랐는데
       *   이름표는 안 갈랐다** — 그래서 훑기를 이미 돌린 사람에게 «다시 훑어라» 로 읽힌다.
       * ⚠ 그 주소가 지금 최우선 110 의 그 기계다(`100.71.194.51`).
       *
       * 셋은 사람이 할 일이 다르고, **이름표만 보고도** 갈려야 한다:
       * ```
       * not_swept         아직 안 훑었다        → 훑어라
       * no_address        주소가 없다          → 주소를 넣어라
       * address_mismatch  훑었는데 거기 없다   → 주소를 고쳐라 (훑기는 죄가 없다)
       * ```
       */
      unknownLabel: {
        not_swept: 'Not scanned',
        no_address: 'No address',
        address_mismatch: 'Not at its address',
      },
      stateHint: {
        connected: 'A Paseo daemon answered on port 6767 and the stored credential let us read its agent list.',
        daemon_detected:
          'A Paseo daemon is listening on port 6767, but no credential is registered for this node, so the console cannot read anything from it. Nothing below is this node’s data — register a credential for the node to see its sessions.',
        not_detected: 'Nothing is listening on port 6767 on this node, so either Paseo is not running there or the node is not reachable over the tailnet.',
        offline:
          'This machine is on the tailnet but is currently powered off, so it was not probed. You can still place it on a graph — jobs routed to it wait until it comes back.',
        unregistered: 'A Paseo daemon was found at a tailnet address that is not registered as an execution node.',
        /*
         * 지시69 — 종전 문구는 **틀린 진단**이었다. 탐지가 안 돈 것이 아니라 합치는
         * 열쇠(주소)가 어긋난 경우가 대부분인데 "sweep 이 아직 안 돌았다" 고 말했다.
         * 아래 `unknownWhy` 가 아는 만큼 갈라 말하고, 이 문구는 못 가를 때만 쓴다.
         */
        unknown: 'The console has no daemon status for this node yet.',
      },

      /**
       * `unknown` 인 이유를 **아는 만큼** 말한다 (지시69).
       *
       * 셋은 사람이 할 일이 서로 다르다 — 기다리기 / 주소를 넣기 / 주소를 고치기.
       * 하나로 뭉치면 무엇을 해야 하는지 모른다.
       */
      unknownWhy: {
        not_swept:
          'The tailnet has not been scanned yet, so there is nothing to match this node against. Run a rescan from the palette.',
        no_address:
          'This node has no tailnet address recorded, so no scan result can ever match it. Add its address under Compute nodes.',
        /** 탐지는 돌았다 — 짝이 안 맞은 것이다. 등록 주소를 보여 줘야 비교할 수 있다. */
        address_mismatch: (addr: string) =>
          `The tailnet was scanned, but nothing was found at ${addr} — the address recorded for this node. Either the machine moved to a different address, or the recorded one is a typo.`,
      },

      /**
       * **고칠 자리** (지시169).
       *
       * 🔴 위 문구는 사람에게 **할 일을 말한다** — 그런데 그 일을 할 자리가 제품에
       *   없었다〔실측: 주소를 고치는 라우트 0건〕. 오늘 그 값을 바꾸려고 DB 를 직접 쳤다.
       * ⚠ 말한 자리와 고치는 자리가 **같아야 한다.** 다른 화면으로 보내면 사람이
       *   그 문장을 읽은 맥락을 잃는다.
       */
      fixAddress: {
        label: 'tailnet address',
        save: 'Save address',
        saving: 'Saving…',
        /** ⚠ 고친 뒤에 **다시 훑어야** 판정이 바뀐다 — 안 그러면 옛 판정이 그대로 남는다. */
        saved: 'Address saved. Rescanning the tailnet to check it.',
        failed: 'Could not save the address: ',
      },

      /*
       * **콘솔이 못 본 것과 없는 것을 가른다** (지시99 ④).
       *
       * 종전 문구는 *"No credential registered"* 였는데 **콘솔은 그걸 모른다** —
       * 매핑이 있는지 전선에 실려 오지 않고(`PaseoNodeDaemonStatus` 성질 일곱에 없다),
       * 화면은 탐지 상태에서 **되짚고** 있을 뿐이다. 매핑이 있고 데몬이 잘 도는데
       * 콘솔만 못 붙은 사이에도 이 줄이 떴다 — 그때 이 문장은 **거짓말**이다.
       *
       * 그래서 **관측한 것만** 말한다: 붙지 못했다. 왜인지는 아래 줄이 가른다.
       * ⚠ 전선에 매핑 칸이 생기면 다시 «등록 안 됨» 이라 단정해도 된다
       * (`instruction99Blocked.test.tsx` 가 그때 붉어진다).
       */
      credentialRequired: 'The console cannot authenticate to this daemon — sessions cannot be read.',
      /*
       * **플랫폼이 만든 비밀을 사용자에게 되묻지 않는다** (지시99).
       *
       * 종전 문구는 *"우클릭해서 Register Paseo credential 를 골라라"* 였다. 그런데
       * **플랫폼이 깐 데몬의 비밀번호는 사용자가 정한 적이 없다** — 설치할 때 우리가
       * 만들었다. 운영자가 아는 값을 넣으니 *"데몬이 거부했다"* 고 답한 자리가 여기다.
       *
       * 그래서 **두 경우를 갈라 적는다.** 사람이 직접 깐 데몬이면 그 비밀번호를
       * 등록하는 것이 맞고, 플랫폼이 깐 것이면 값은 원격 기계의 `daemon.env` 에 있다 —
       * 이번에 운영자가 실제로 복구한 경로다.
       *
       * ⚠ 재설정 라우트가 서면 이 문단은 *"재설정을 눌러라"* 로 바뀐다. 지금은
       * **없는 버튼을 가리키지 않는다** — 종전 문구가 딱 그래서 틀렸다
       * (없는 «Fleet → node → Paseo credential» 을 가리켰다).
       */
      credentialRequiredDetail:
        'If you installed Paseo on this machine yourself, right-click the node and register its ' +
        'password. If the platform installed it, the password was generated during that install — ' +
        'it is in ~/.config/platform-paseo/daemon.env on that machine. Do not guess a password ' +
        'here: the daemon will reject it.',
      notDetectedDetail: 'Start Paseo on this node, or check that its tailnet address is registered, then refresh discovery.',

      loading: 'Checking for a Paseo daemon…',
      notWired: 'Paseo discovery is not wired up on the server (501) — notify an operator.',
      loadFailed: (message?: string) => `Could not check for a Paseo daemon${message ? `: ${message}` : ''}`,
      /*
       * ⚠ **`credentialRemoveMenuItem`·`…Hint` 를 지웠다** (결정5456, gui-canvas 7849 실측).
       * 떼는 길은 `RegisterPaseoCredentialDialog` 에 이미 온전히 있다(`detachNodePaseoCredential`
       * · `confirmingRemove` 두 단계 확인). 메뉴 항목을 또 만들면 같은 일에 길이 둘이 되고
       * **메뉴 쪽은 확인이 없어 덜 안전한 길**이 된다. 안 만들기로 했으니 문구도 군더더기다.
       */
      refresh: 'Re-scan',
      refreshHint: 'Probes the tailnet again instead of using the cached result (the server caches for about a minute).',

      sessionsTitle: 'Sessions',
      sessionsEmpty: 'No sessions running on this daemon.',
      sessionStatusLabel: {
        idle: 'idle',
        running: 'running',
        permission: 'needs approval',
        other: 'unknown',
      },
      sessionStatusHint: {
        /*
         * 지시55 가 콕 집은 자리 — **idle 은 죽은 것이 아니다.** 운영자가 헷갈릴 수
         * 있다고 명시했으므로 "살아 있다" 와 "다음 지시를 기다린다" 를 둘 다 말한다.
         */
        idle: 'Alive and healthy — finished its last task and waiting for the next instruction. Not stopped, not dead.',
        running: 'Working — it will keep going without you.',
        permission: 'Stopped and waiting for a person to allow or deny an action. It will not move until someone answers.',
        other: 'The daemon reported a status this console does not recognise — shown verbatim.',
      },
      /*
       * 지시57 — 지금 상태에서 말이 안 되는 항목을 **감추지 않고** 흐리게 두고 이유를
       * 말한다. 감추면 "그런 기능이 없다" 로 읽힌다(지시33 §3 과 같은 원칙).
       */
      actionBlocked: {
        already_installed: 'Paseo is already running on this machine — register its password instead.',
        not_installed: 'No Paseo daemon is answering on this machine yet — install it first.',
        already_connected: 'Already connected — its sessions are listed on this card.',
        machine_off: 'This machine is not reachable right now.',
      },
      installMenuItem: 'Install Paseo…',
      permissionCount: (count: number) => `${count} waiting for approval`,
      permissionCountHint: 'These sessions are stopped until a person answers. Open ⑥ Sessions to allow or deny.',

      /**
       * 세션 목록의 출처를 숨기지 않는다. 노드별 조회 경로(결정53)가 생겨
       * '어느 데몬의 목록인지 모른다' 는 제약은 사라졌지만, **자격증명이 없는
       * 노드**는 여전히 읽을 수 없다. 그때 다른 데몬의 목록을 붙이는 것은
       * 거짓이므로 읽을 수 없다는 사실을 그대로 말한다.
       */
      sessionsUnavailable: 'Sessions unavailable',
      sessionsUnavailableHint:
        'No Paseo credential is registered for this node, so the console cannot read its sessions. Showing another daemon\'s sessions here would be wrong. Right-click this node to register one.',
      sessionsLoadFailed: (message?: string) => `Could not read sessions${message ? `: ${message}` : ''}`,

      /*
       * ⚠ **여기 있던 `unregisteredCount` 를 지웠다** (결정5454).
       *
       * «tailnet 에 미등록 데몬 N 개» 라는 **요약 한 줄**이었고 한 번도 안 그려졌다.
       * 안 그려진 것이 맞았다 — **팔레트가 이미 하나씩 낸다**〔실측
       * `computeNodeOptions.tsx` 의 `mergeComputeNodes`〕: 등록 행과 짝이 없는 탐지
       * 결과는 `placement: 'unregistered'` 로 **개별 선택지가 되어 뜬다.**
       *
       * ★ 즉 **가려진 것이 없다.** 요약을 얹으면 개수를 두 곳에서 세는 셈이고,
       * 한쪽만 고쳐지는 부류가 된다. 「가려진 것은 없는 것이다」 의 반대 —
       * **보이는 것을 또 세지 않는다.**
       */
      unregisteredHint:
        'A Paseo daemon answered on the tailnet at an address that is not registered as an execution node, so it cannot be placed on the canvas. Register the node first (⑤ Fleet).',

      /**
       * 우클릭 → Paseo 자격증명 등록.
       *
       * 사슬이 여기서 끊겨 있었다. `daemon_detected`("연결 필요") 카드는 무엇이
       * 빠졌는지는 말해 줬지만 **채울 곳이 화면에 없었고**, 서버에도 자격증명을
       * 만드는 라우트가 없었다. 이제 카드가 "Sessions unavailable" 이라고 말하는
       * 바로 그 노드를 우클릭해 곧장 조치할 수 있다.
       */
      credentialMenuItem: 'Register Paseo credential…',
      credentialMenuItemHint:
        'Stores the daemon password for this node so the console can read its sessions. The password is encrypted on the server and never comes back to the browser.',
      credentialTitle: (nodeDisplay: string) => `Paseo credential for ${nodeDisplay}`,
      credentialIntro:
        'The password this node’s Paseo daemon asks for on port 6767. The server encrypts it at rest (AES-256-GCM) and only decrypts it at the moment it connects — it is never shown again, here or anywhere else.',
      credentialPassword: 'Daemon password',
      credentialPasswordHint:
        'The password configured on the Paseo daemon running on this node. If you do not know it, read it from that machine’s Paseo configuration — the console cannot recover it for you afterwards.',
      credentialSubmit: 'Register',
      credentialSubmitting: 'Checking the password…',
      /*
       * 서버가 저장 전에 **데몬에 실제로 붙어 본다**(결정193). 대조 프로브까지 최대
       * 2회 왕복이고 각 5초다 — 즉 10초 가까이 걸릴 수 있다. 그 사실을 말하지 않으면
       * 사람은 멈춘 줄 알고 다시 누르거나 창을 닫는다.
       */
      credentialSubmittingHint: 'The server connects to the daemon before saving, so this can take a few seconds.',
      /*
       * **무엇이 바뀌는지 눌리기 전에 말한다** (지시99 ③).
       *
       * 종전에는 «Remove credential» 이 **한 번 누르면 바로** 지웠다. 그런데 이 노드의
       * 데몬은 **그 값으로 지금 돌고 있을 수 있다** — 지우면 콘솔이 그 데몬에 다시
       * 못 붙고, 값은 원격 기계의 `daemon.env` 에만 남는다. 지시99 의 사고가 정확히
       * 그 상태였고, 운영자가 SSH 로 들어가서야 복구했다.
       *
       * ⚠ **막지는 않는다** — 데몬을 내렸거나 진짜로 바꿀 때는 정당한 조작이다.
       * 서버가 «쓰는 것이 있는가» 를 보고 거절하는 것은 engine 몫이고(지시99 ④),
       * 화면은 **그 전에 사실을 말하는** 자리다.
       */
      credentialRemoveConfirm:
        'Removing the credential does not stop the daemon. If it is running with this password, ' +
        'the console will no longer be able to reach it — and the password will only exist in ' +
        '~/.config/platform-paseo/daemon.env on that machine. Remove it anyway?',
      /*
       * ⚠ **물러서는 단추 이름을 `Cancel` 과 갈랐다.** 확인 단계에서 둘 다 «Cancel» 이면
       * 같은 다이얼로그에 이름이 같은 단추가 둘이 된다 — 검사가 «여럿 찾았다» 로
       * 잡았고, 그건 **읽어 주는 화면에서도 똑같이 모호하다.**
       */
      credentialRemoveKeep: 'Keep it',
      credentialRemoveReally: 'Remove it anyway',
      credentialRemove: 'Remove credential',
      credentialRemoving: 'Removing…',
      /**
       * 실패를 삼키지 않는다. 특히 서버에 CREDENTIALS_KEY 가 없으면 503 이 오는데,
       * 그 문구에 무엇을 채워야 하는지가 들어 있으므로 **서버 message 를 그대로**
       * 보여 준다(describeError). 조용히 실패하면 운영자는 등록된 줄 알고 지나간다.
       */
      credentialRemoveFailed: (message: string) => `Could not remove the credential — ${message}`,

      /*
       * 지시45 (c) — **"비밀번호 거부" 와 "데몬에 못 닿음" 은 할 일이 정반대다.**
       * 앞의 것은 다시 치면 되고, 뒤의 것은 다시 쳐도 소용없다. 같은 문구로 뭉개면
       * 사람은 멀쩡한 비밀번호를 몇 번씩 다시 친다. 서버가 코드로 갈라 준다(결정193).
       */
      credentialRejected: 'The daemon refused this password. Check it and try again — nothing was saved.',
      credentialUnsupported:
        'This password cannot be sent to a Paseo daemon at all — it contains non-ASCII characters, which neither the console nor the Paseo CLI can transmit. Change the password on the machine itself, then register the new one.',
      credentialUnreachable:
        'Could not reach the Paseo daemon, so the password was not checked and nothing was saved. This is not a password problem — check that the machine is on and reachable over the tailnet.',
      /*
       * 결정205 — 그 주소가 **답하긴 했는데** Paseo 인증 API 가 아니었다(웹 UI 의 HTML
       * 등). "닿지 못했다" 와 같은 문구를 쓰면 사람이 멀쩡한 기계의 전원을 보러 간다.
       */
      credentialNotApi:
        'Something answered at that address, but it was not the Paseo authentication API — so nothing was checked and nothing was saved. Another service may be using that port, or the daemon may be answering somewhere else. This is not a password problem.',
      credentialNoAddress:
        'This node has no tailnet address, so there is nothing to connect to. Register its address first.',
      credentialOtherFailure: 'The credential was not saved.',
      /**
       * 저장은 됐지만 **확인하지는 못했다** (결정193 의 `no_password_set`). 성공으로
       * 조용히 넘기면 화면이 "확인됨" 과 "확인할 수 없었음" 을 구분하지 못한다.
       */
      credentialWarning: (message: string) => `Saved, but not verified — ${message}`,
      credentialWarningAck: 'Understood',
      credentialReplaceNote:
        'This node already has a credential. Registering a new one replaces it, and the old one is revoked if the server issued it.',

      /** 우클릭 → Paseo 에이전트 생성(지시24). */
      /*
       * 왜 지금 못 띄우는가 (지시60). **다음에 할 일이 같은 메뉴 안에 있다** —
       * 그것을 가리켜 준다. "안 됩니다" 로 끝내면 사용자가 메뉴를 닫고 헤맨다.
       */
      createBlocked: {
        machine_off: 'That machine is not reachable right now. Turn it on first.',
        no_credential:
          'We do not have this node\u2019s Paseo password, so we cannot reach its daemon. Register it first — the item is right below.',
        no_daemon:
          'There is no Paseo daemon on that machine yet, so there is nothing to start an agent on. Install it first — the item is right below.',
      },
      createMenuItem: 'Create Paseo agent…',
      createMenuItemHint: 'Starts a new Paseo agent on this node’s daemon.',
      createTitle: (nodeDisplay: string) => `New Paseo agent on ${nodeDisplay}`,
      createPrompt: 'Task prompt',
      createPromptHint:
        'The first thing the agent is told to do. Write it like a work order: what to change, where, and what “done” looks like. This is the only field most people need to fill in.',
      createPromptPlaceholder: 'Fix the failing tests in src/api and explain what was wrong',
      createModel: 'Model',
      createModelHint: 'Which registered LLM the agent runs on. Bigger models cost more per token but follow long, multi-step tasks more reliably.',
      createModelEmpty: 'No registered LLM models — register a provider and model first.',
      createModelNotWired: 'Model catalog is not wired up on the server (501).',
      createMode: 'Permission mode',
      createWorkdir: 'Working directory',
      createWorkdirHint:
        'Absolute path on the node that the agent treats as its project root. Everything it reads or edits is relative to this, so pointing it at the wrong checkout is how agents edit the wrong repository.',
      createWorkdirPlaceholder: '/home/paseo/project',
      create: 'Create',
      cancel: 'Cancel',

      /**
       * 모드 기본값 근거를 화면에도 남긴다 — 코드 주석은 운영자가 읽지 않는다.
       * 기본값은 `default`(모든 위험한 동작마다 사람에게 묻는다).
       */
      /*
       * 생성 실패 갈래 (지시60). **할 일이 갈래마다 다르다** — 한 문구로 뭉치면
       * 사용자가 무엇을 해야 하는지 못 찾는다.
       */
      creating: 'Starting…',
      done: 'Done',
      /*
       * 만든 뒤 **닫지 않고** 무엇이 만들어졌는지 보여 준다(상시 규칙 ①·⑥).
       * 자율적으로 명령을 실행하는 프로세스를 띄운 것이라, 떴다는 사실을 눈으로
       * 확인하고 닫아야 한다.
       */
      createdOk: (name: string) => `Started '${name}'.`,
      /** running 으로 왔다가 목록에서 idle 이 된다 — 둘 다 정상이다(인프라B 실측). */
      createdStatus: (status: string) => `Status: ${status}. 'idle' means it is waiting for its next instruction, not that it died.`,
      createdWhereToLook: 'It now appears in this node\u2019s session list.',
      createFailGlobalStop:
        'Everything is stopped right now, so no new agent was started. Lift the global stop in Settings and try again.',
      createFailNoCredential:
        'That node has no Paseo password stored, so we cannot reach its daemon.',
      createFailUnreachable:
        'The Paseo daemon on that node did not answer. Check that the machine is on the tailnet and the daemon is running.',
      createFailForbidden:
        'You do not have permission to start agents. An agent runs commands on its own, so this is granted separately — ask an admin.',
      /** 지금은 Anthropic 만 확인됐다(인프라B). 지우지 않고 이유를 붙여 잠근다. */
      createProviderUnsupported: 'not available for Paseo agents yet',
      modeLabel: {
        default: 'Ask every time (default)',
        acceptEdits: 'Auto-accept file edits',
        plan: 'Plan only — no changes',
        auto: 'Let a model judge each request',
        bypassPermissions: 'Bypass all permissions',
      },
      modeHint: {
        default:
          'The agent stops and asks a person before each action that changes something or runs a command. Slowest, and the only mode where nothing happens that nobody approved.',
        acceptEdits:
          'File edits inside the working directory are applied without asking; commands and anything outside it still ask. Convenient for refactors you already trust.',
        plan: 'The agent investigates and writes a plan but is not allowed to change anything. Safe way to see what it would do first.',
        /*
         * 실물 데몬이 지원하는 모드다(인프라B 가 `inspect_provider claude` 로 확인).
         * **사람이 아니라 모델이 심사한다**는 것을 분명히 적는다 — "auto" 라는 이름만
         * 보면 편의 기능으로 읽히지만, 승인 주체가 바뀌는 선택이다.
         */
        auto:
          'A model reads each permission prompt and decides for you. Faster than asking a person, but nobody sees the decisions — a wrong judgement is not caught until afterwards.',
        bypassPermissions:
          'DANGER: the agent runs every command and edit with no approval at all — including deleting files, pushing, and calling the network. It cannot be interrupted per action. Use only on a node you are willing to lose.',
      },
      modeFieldHint:
        'How much the agent is allowed to do without a person saying yes. Defaults to asking every time; read each option’s own tooltip before loosening it.',
      bypassWarning:
        'Bypass mode runs everything unattended — no approval prompts, including destructive commands. Only pick this on a node whose contents you are willing to lose.',

      /**
       * ⚠ **이 문구가 사람에게 거짓을 말하고 있었다** (지시127, gui 결정6457).
       *
       * 종전 글: *«The control API cannot create Paseo agents yet … there is no create
       * route … creating is blocked until the server adds the route»*.
       * **라우트는 섰다** — 지시60(결정721) 〔실측: `openapi.json` 의 `/paseo/agents` 가
       * `['get','post']`, 그리고 `apiClient.createPaseoAgent` 가 그것을 부른다〕.
       * 같은 저장소의 `GraphCanvas` 는 이미 *«생성 라우트가 섰다 — 잠금을 푼다»* 라고
       * 적어 두고 핸들러를 넘긴다. **한 곳만 갱신되고 이 문구가 안 따라왔다.**
       *
       * ▶ 그래서 뜻을 갈았다: 이 막음은 **화면 배선의 빈자리**를 말하는 것이지
       *   «서버가 못 한다» 가 아니다. 남의 축에 없는 죄를 씌우지 않는다.
       */
      createUnavailable: 'This dialog was opened without a way to create.',
      createUnavailableDetail:
        'No create handler was wired into this dialog, so the form has nothing to call. This is a gap in this screen, not in the server — the control API does have a create route (POST /paseo/agents).',
    },
  },

  runs: {
    /*
     * 실시간 연결이 끊겼을 때 (결정2405). ⚠ **'오류' 라고 쓰지 않는다** — 실행은
     * 멀쩡히 돌고 있을 수 있다. 정확히 모르는 것은 *"화면이 최신인지"* 뿐이다.
     */
    streamDown: 'Live updates disconnected — what you see may be out of date.',
    streamDownRetrying: 'Reconnecting…',
    streamRefresh: 'Refresh now',
    /*
     * 자기 점검을 갈라내는 거르개 (지시88 걸쇠, 결정1465). **서버가 거른다** —
     * 받아 온 한 장만 걸러 보이면 잘린 목록 위에서 수가 거짓말한다.
     */
    /**
     * **내부 검증 보이기** (지시165). 이 화면은 **플랫폼 사용자**가 보는 자리라
     * 기본은 설계된 사슬의 회차만 보인다.
     * ⚠ 「안 보임」과 「없음」을 가른다 — 가려 둔 것이 있다고 **말한다.**
     */
    showInternal: 'Show internal platform checks',
    internalHidden:
      'Internal platform checks (such as status-check) are hidden. They still run and are still recorded.',
    internalShown: 'Internal platform checks are shown.',
    filterPipeline: 'Pipeline',
    /*
     * 거르개 목록을 **못 읽었다** (결정2422). 실행 목록 자체는 멀쩡하므로 크게
     * 외치지 않되, **빈 것과 못 읽은 것을 가른다**(결정1401) — 종전에는 둘이
     * 같은 그림이었다: 고를 것이 없는 드롭다운.
     */
    filterListError: 'Could not load the pipeline list — filtering is unavailable.',
    filterAll: 'All pipelines',
    /*
     * **어느 그래프 판이 돌았나** (결정1437). 실행마다 다를 수 있다 — 오늘 `slack-echo`
     * 는 하루에 넉 번 바뀌었고, 판을 안 적으면 **옛 판의 실패**와 **지금 판의 실패**가
     * 한 줄로 보인다.
     */
    versionHint: 'The pipeline version this run used — republishing changes it.',
    heading: 'Runs',
    loading: 'Loading…',
    loadError: 'Could not load run list',
    /*
     * 목록이 잘렸을 수 있다 (결정438). **단정하지 않는다** — 정확히 그 수만큼 있는
     * 경우와 구분할 방법이 응답에 없다. 모르는 것을 아는 척하지 않는다.
     */
    maybeTruncated: (shown: number) =>
      `Showing the ${shown} most recent runs. There may be older ones that are not listed.`,
    columns: {
      runId: 'Run ID',
      pipeline: 'Pipeline',
      status: 'Status',
      /*
       * ★ **처리 시각 셋** (지시108, 결정3438).
       *
       * 운영자가 `Input Time` 을 **둘로 갈랐다**. 까닭이 문구에 실려야 한다:
       * *"둘의 간격이 큐 지연이다. 하나로 뭉치면 «느린 것» 과 «밀린 것» 을 구별 못 한다."*
       * 오늘 `run_1a39d929` 이 6분 4초 만에 죽었을 때 운영자가 처음 물은 것이
       * «서버가 느린가 큐가 밀렸나» 였다.
       *
       * ⚠ **둘 중 하나가 없을 수 있다** — 방아쇠가 물었는데 아직 안 돈 런은 시작
       * 시각이 없고, 방아쇠 기록 없이 도는 길도 있다(수동 실행).
       * **없으면 빈칸이지 서로 베끼지 마라.**
       */
      /**
       * **우리에게 닿은 시각** — `trigger_events.received_at` (지시108, 결정3444).
       * 아래 `processingStartedTime` 과 **둘의 간격이 큐 지연**이다.
       * ⚠ 둘 중 하나가 없을 수 있다(아직 안 돈 런 · 방아쇠 기록 없는 수동 실행).
       *   **없으면 빈칸이지 서로 베끼지 마라** — 베끼면 큐 지연이 늘 0 으로 보인다.
       */
      inputReceivedTime: 'Input Received Time',
      /** 어디서 들어왔나 — `trigger_events.source`(`slack`·`imap`…). 없으면 빈칸. */
      inputSource: 'Input Source',
      /* 지시108 의 열 둘. 계약(engine 결정3398)이 서서 이제 그린다 — 결정5443. */
      inputUserId: 'Input User ID',
      outputChannel: 'Output Channel',
      processingStartedTime: 'Processing Started Time',
      outputTime: 'Output Time',
      environmentNode: 'Environment/Node',
      tokens: 'Tokens',
      cost: 'Cost',
      duration: 'Duration',
    },
    detail: {
      /*
       * **추론이 어디로 갔나** (결정1415). 꼭짓점이 돈 노드와 **다른 물음**이다 —
       * 오늘 꼭짓점은 `ec2-control-1`, 추론은 `onprem-s-ai-pro6000-37` 였다.
       */
      /*
       * **그 단계가 내놓은 것** (지시88 ④, 결정1428). 접어 둔다 — 자유 형식이라 길 수 있다.
       * 상태 보고의 **내용**이 여기 있다: 요약은 *"임계 초과 항목 있음"* 까지만 말한다.
       */
      /*
       * **런이 남긴 산출** (지시88, 결정2420). 상태 보고가 사는 곳이다.
       *
       * ⚠ 계약이 모양을 안 정해서(`anyOf: [{}, null]`) 화면은 **해석하지 않고** 원문을
       *   보인다. 그래서 제목도 *"보고"* 가 아니라 *"산출"* 이다 — 보고가 아닌 것이
       *   들어 있어도 거짓말이 안 되게.
       */
      /**
       * **트리거가 넣은 것** (결정2437). 산출과 **짝**이다.
       *
       * ⚠ 비-LLM 단계만 있는 런은 `agent-io` 행이 없어 **여기가 입력을 볼 유일한 자리**다.
       */
      runInput: 'Run input',
      /* 기록에 든 파일 (운영자 지시 2026-10-03) — base64 덩어리 대신 이름·크기·듣기·내려받기. */
      files: {
        listLabel: (n: number) => `${String(n)} file${n === 1 ? '' : 's'} in this record`,
        unnamed: 'Unnamed file',
        play: 'Play',
        show: 'Show',
        download: 'Download',
        playsAs: (type: string) => `plays as ${type}`,
        at: (path: string) => `at ${path}`,
        playerLabel: (name: string) => `Player for ${name}`,
        cannotDecode: 'This file could not be decoded — the recorded content may be cut short.',
        loading: 'Loading…',
        cannotLoad: (why: string) => `This file could not be loaded: ${why}`,
        noLoader: 'the file content was left out of this record and there is no way to fetch it here',
        bodyPlaceholder: (chars: number) => `[file content hidden here: ${String(chars)} base64 characters — play or download it from the file card above]`,
      },
      /** 조회는 됐는데 비었다. 못 읽은 것과 다르다. */
      noRunInput: 'This run recorded no input.',
      /**
       * **의사가 읽는 자리** (운영자 결정 08-31, 결정4409).
       *
       * 이 사슬의 독자가 우리에서 **임상의**로 바뀌었다. 운영자:
       * *"우리는 «모델이 그럴 수 있지» 를 안다. **의사는 모른다.**"*
       * 그래서 셋을 **말로** 가른다 — 색만으로는 못 가른다(색맹·인쇄·흑백 캡처).
       */
      provenance: {
        heading: 'Where each value came from',
        /** ⚠ ③ 이 제일 중요하다 — 빈칸이 아니라 «못 읽었다» 라고 **말해야** 한다. */
        unread: 'not heard',
        code: 'computed by code',
        /*
         * ⚠ **«model said» 만으로는 부족하다** (결정4429).
         *
         * 의사는 «모델이 말했다» 를 «보고됐다» 로 읽는다. 실제 뜻은 **코드가 그 칸을
         * 안 봤다** 이다 — 장부의 `fields` 가 «코드가 손댄 칸» 이고, 거기 없다는 것은
         * 검산이 그 칸을 **안 덮었다**는 말이다. llm 의 `skipped` 한 줄이 그 자리를
         * 실물로 보여 줬다: `items[no=4].score = 1` 은 아무도 검산 안 한 숫자다.
         */
        model: 'model said · not checked',
        /*
         * ⚠ **«코드가 계산함» 인데 칸이 비어 있으면 그 말이 안 맞는다** (결정4445).
         *
         * llm 결정5544 가 가른 자리다: `judgement: null` 은 «못 읽음» 이 아니라
         * **코드가 판정을 안 내기로 한 것**이다(규칙8 — 총점이 불완전하면 판정을 안 낸다).
         * 뜻은 4424 에서 갈랐는데 **말이 안 따라갔다** — 화면에는
         * `computed by code | — ` 로 떠서, 의사가 «코드가 계산했는데 아무것도 안 나왔나»
         * 로 읽는다. 코드가 **일부러 비운 것**이라고 말해야 한다.
         *
         * ⚠ «왜» 는 안 적는다 — 까닭은 서버가 `judgement_basis` 에 적고, 화면이
         * 짐작해서 «총점이 불완전해서» 를 붙이면 판정을 새로 만드는 것이다(결정423).
         */
        codeLeftBlank: 'code left this blank',
        modelSaidWas: (v: string) => `model had: ${v}`,
        /** 표식이 안 실려 오면 그 사실도 말한다 — 없는 것을 «모델이 말했다» 로 안 읽는다. */
        /*
         * ## ⚠ **이 문장이 내 손으로 거짓이 됐다** (결정4449)
         *
         * 원래 *"… only «not heard» is certain"* 이었다. 그때는 참이었다 —
         * 규칙이 «값이 null 이면 못 읽은 것» 하나뿐이라, 표식이 없어도 그 판정만은 섰다.
         *
         * 그런데 **결정4424·4445 가 빈칸을 둘로 갈랐다**:
         *
         * ```
         * 코드가 «못 읽어서» 비웠다   → not heard        (표식의 `nulled` 가 말해 준다)
         * 코드가 «안 내기로» 비웠다   → code left blank  (표식의 `fields` 가 말해 준다)
         * ```
         *
         * **둘 다 표식이 있어야 갈린다.** 표식이 없으면 빈칸이 어느 쪽인지 **모른다** —
         * 그러니 «not heard 만은 확실하다» 는 이제 **거꾸로 된 말**이다.
         * 화면이 그 자리에 «not heard» 를 찍는 것은 **판정이 아니라 그냥 빈칸이라는 뜻**이다.
         *
         * ⚠ 이 부류가 llm 이 «제일 나쁘다» 고 한 자리다 — **표식에 대한 거짓 안심**은
         * 코드가 잡아 준 것까지 의심하게 만들거나, 없는 보증을 믿게 만든다.
         */
        noMark:
          'The server did not send provenance marks for this run, so “computed by code” cannot be shown. A value marked “not heard” here only means it is empty — code may have left it blank on purpose, and this screen cannot tell which.',
        /**
         * **근거** (llm 결정5519·5530, 여기 결정4413).
         *
         * ⚠ **«확인됨» 이라고 쓰지 않는다.** llm 이 한계를 못박았다 — 이것은
         * *"그 문장이 녹음에 있었나"* 이지 *"그 사람이 말했나"* 가 아니다.
         * **주입은 전사문에 심는다.** «verified» 라고 적으면 의사가 주입된 값을
         * 확인된 것으로 읽는다.
         */
        quoted: 'in the recording',
        noBasis: 'no basis in the recording',
        quotedCaveat:
          'Quoted text was found in the transcript by code, not asserted by the model. It does not prove who said it.',
        /**
         * ⚠ **접힌 상자 안에 갇히지 않게** 실행 위쪽에 한 줄로 낸다 (결정4417).
         * 임상 JSON 이 단계 산출로 오면 표가 `<details>` 안이라 의사가 안 펼치면 못 본다.
         */
        /*
         * ⚠ **까닭을 단정하지 않는다** (결정4450 — 4449 를 훑다 나온 둘째).
         *
         * 전에는 *"… were **not heard**"* 였다. 그런데 이 줄에 세어지는 칸에는
         * **까닭이 확실한 것과 아닌 것이 섞인다**:
         *
         * ```
         * 표식의 `nulled` 에 있다      → «못 읽었다» 가 llm 이 보장하는 사실
         * 표식이 아예 안 왔다          → 코드가 일부러 비운 것일 수도 있다 (모른다)
         * 표식은 왔는데 그 칸을 안 든다 → 모델이 낸 null 이다 (짐작)
         * ```
         *
         * 그리고 **이 줄은 판 밖에 홀로 선다** — 판 안의 «표식이 안 왔다» 단서가
         * 곁에 없다. 그러니 여기서는 **사실만** 말하고(비었다) **할 일**을 준다
         * (채우지 마라). 까닭은 판 안에서 칸마다 말한다.
         */
        runUnread: (n: number, paths: string) =>
          `${n} field${n === 1 ? '' : 's'} in this run ${n === 1 ? 'is' : 'are'} empty — do not fill in a value: ${paths}`,
        /**
         * **장부의 줄** (llm 실물 실측 → 결정4420).
         *
         * 〔llm 라이브 6회〕 장부가 회차마다 **12~14줄**이고 갈래가 다르다:
         * ```
         * fields 6줄  «어디를 손댔나»   ← 내 표가 이미 칸마다 보여 준다. **줄로는 안 낸다**
         * contradictions 3 · quotes 2 · skipped 1   ← «무슨 일이 있었나». 사람에게 값이 크다
         * ```
         * ⚠ `skipped` 는 «못 읽음» 도 «계산함» 도 아닌 **셋째**다 — 코드가 검산을
         * **하려다 못 한** 자리다. 의사에게는 «거긴 코드가 안 봤다» 로 읽혀야 한다.
         */
        skippedHeading: 'Code could not check these',
        contradictionsHeading: 'The model contradicted itself',
        quotesHeading: 'Quotes corrected against the transcript',
        droppedHeading: 'Not counted',
        /*
         * ⚠ «값이 비었다» 가 아니라 **«그 칸이 아예 안 왔다»** 다 (결정4425).
         *   장부는 그 칸을 이름으로 드는데 산출에는 열쇠가 없다 — 모델이 한 말이
         *   코드에 지워진 자리다. 의사에게는 «없다» 도 하나의 답이다.
         */
        absentHeading: 'The ledger names these, but the output has no such field',
        /* ⚠ 색만으로 두지 않는다 — 이 줄도 첫 칸에 **말**을 든다(결정4425) */
        absentMark: 'not in the output',
        /*
         * ⚠ 옛 문장(*"Treat a filled-in value with care only if it is marked"*)은
         *   **무엇을 믿어도 되는지를 안 말했다.** 셋을 하나씩 뜻으로 적는다.
         */
        /*
         * ⚠ **판이 내는 표식을 다 적어야 한다** (결정4446).
         *
         * 결정4445 에서 «code left this blank» 를 더하면서 **범례를 안 고쳤다.**
         * 판을 글로 읽어 보니 표식이 넷인데 범례는 셋만 풀고 있었다 — 그리고 빠진
         * 그 하나가 하필 **②와 ③을 가르는 자리**다(코드가 «비워 뒀다» 와 «못 읽었다»).
         * 범례는 의사가 **열쇠를 찾으러 오는 곳**이라, 없으면 그 자리에서 막힌다.
         */
        legend:
          '“computed by code” means code checked this value. “code left this blank” means code had the field and chose to leave it empty — that is not the same as not hearing it. “model said · not checked” means the model produced it and code did not check it. “not heard” means the recording gave no value — do not fill one in.',
        /*
         * ★ **운영자가 «값으로 말해 줘라» 고 한 자리** (결정4429).
         *
         * > *"의사에게 «무엇을 믿어도 되는지» 를 **값으로** 말해 줄 수 있어야 한다"*
         *
         * 여태 화면은 **못 읽은 것만 수로** 말했다. 그런데 이 표는 40줄이고 그중
         * 검산된 칸은 몇 안 된다 — 그 비율이 한 줄로 안 서면 의사는 표 전체를
         * «검토된 것» 으로 읽는다. 셋을 다 수로 적는다.
         */
        /*
         * ★ **셀 것이 아예 없을 때** (결정5415). `tally(0,0,0)` 은
         * *"0 with no value"* 라고 말하는데, 그건 **«빠진 것이 없다»** 로 읽힌다 —
         * 코드가 아무것도 못 읽은 런에서 정확히 반대의 뜻이다.
         * 수를 말하지 말고 **무엇이 남았는지**를 말한다.
         */
        tallyNothingCameThrough:
          'No values came through in this output. What follows is only what the code recorded ' +
          'about the attempt — read it as “nothing was read”, not as “nothing was wrong”.',
        tally: (code: number, model: number, unread: number) =>
          `Of ${code + model + unread} values: ${code} checked by code, ${model} from the model with no check, ${unread} with no value.`,
      },
      runOutput: 'Run output',
      /** 조회는 됐는데 **비어 있다.** 못 읽은 것과 다르다. */
      /**
       * **상태 보고 표식** (지시88 · 결정5453). *"평상시와 같은 모양이면 아무도 안 본다."*
       *
       * ⚠ 셋을 갈라 말한다 — 넘겼다 · 평상시다 · **보고가 없다.**
       * 마지막을 «평상시» 로 접으면 못 읽었는데 정상으로 보이는 화면이 된다.
       */
      /**
       * ★★ **보고 자체를 사람이 읽게 낸다** (지시88, gui 결정9446).
       *
       * ## 왜 — 31줄짜리 사람 글이 **한 줄 2,828자**로 눌려 있었다
       *
       * 도는 판의 status-check 산출을 열어 쟀다 〔실측 2026-09-07 KST, `run_61ddc5d1…`〕:
       * ```
       * output           칸 **17개**(gpu · disk · hungShells · connections …)
       * output.report    `{ text, breached }` — `text` 가 **31줄** · breached **true**
       * 화면이 그리던 것  판정 한 줄 + `JSON.stringify(output, null, 2)`
       *                  → `<pre>` **533줄**, 그중 `"text"` 줄이 **2,828자** · `\n` 이스케이프 **30개**
       * ```
       * 리드가 청한 두 줄(«매달린 셸 수» · «늙은 idle 연결의 나이»)이 **거기 다 있었다** —
       * `매달린 셸: 4건 … 최장 40.6시간` · `늙은 idle 연결: 8개, 최장 1.2일`.
       * 곧 **없는 값이 아니라 못 읽는 값**이었다.
       *
       * ⚠ 내가 앞서 이 티켓에 «gui 몫 — 고칠 것이 0 줄» 이라 적었다. 데이터가 흐르는 것은
       *   맞았지만 **읽히느냐는 안 쟀다.** 그 회차에 스스로 *«읽히는 줄로 세우려면 작은 고침이
       *   필요하다»* 라고 적어 두고 **안 지었다** — 「산문의 약속은 아무 걸쇠도 안 문다」.
       *
       * ⚠ 날 JSON 은 **그대로 둔다**(engine 이 «줄이지 말고 다 보내라» 는 자리다). 위에 얹는다.
       */
      /**
       * **그 회차의 결론을 맨 앞에 놓는다** (지시156, gui 결정9463).
       *
       * 〔실측 2026-09-07 19:2x UTC · `run_93c29ffb…`〕 8/8 초록으로 끝난 회차인데 화면은
       * **1,136,192자** 의 날 JSON 이었고(`t1` 893KB + `say` 237KB 가 base64 소리다),
       * 그 안에 **이 회차의 결론**이 묻혀 있었다:
       * > *「전사문은 «서식2» 에 더 맞는데 사슬은 «서식3» 이다 — 사람이 사슬을 잘못 고른 것으로 보인다」*
       *
       * ⚠ **판정이 아니다.** 화면은 «틀렸다» 고 말하지 않는다 — 모델이 낸 말을 **그대로**
       *   앞으로 옮기고, 근거(전사문이 어느 서식에 몇 개 맞았나)를 옆에 둔다. 사람이 판단한다.
       * ⚠ 이견이 **없을 때는 아무 말도 안 한다** — 늘 붙는 안내는 안 읽히고, 안 읽히는 안내는 해롭다.
       */
      formCheckHeading: 'Before you read this: the chain may be the wrong one',
      /** 「몇 개 중 몇 개가 맞았나」 — 사람이 스스로 판단할 근거다. 판정을 대신하지 않는다. */
      formCheckEvidence: (rows: { id: string; matched: number; of: number }[]) =>
        `Transcript match — ${rows.map((r) => `${r.id}: ${r.matched}/${r.of}`).join(' · ')}`,
      /**
       * ⚠ **못 읽은 것과 «이견이 없다» 는 다른 말**이다. 접으면 «괜찮다» 로 읽힌다.
       *   (모델이 코드펜스를 붙이면 이 칸이 JSON 으로 안 읽힌다 — 실제로 겪은 자리다.)
       */
      formCheckUnreadable:
        'This run carries a clinical extraction, but the console could not read it as JSON — so it cannot tell you whether the chain matches the transcript. The raw output below is unchanged.',
      /**
       * **판정이 안 난 구역과 그 까닭** (지시156, gui 결정9464).
       *
       * 〔실측 `run_93c29ffb…`〕 구역 넷이 **다** `judgement: null` 인데 화면은 아무 말도
       * 안 했다 — 사람은 «아직 안 돌았나» 로 읽는다. **까닭은 이미 옆 칸에 있었다.**
       * ⚠ 「판정 없음」이 **결함이 아닐 수 있다** — 이 사슬은 «못 읽었으면 지어내지 않는다» 가
       *   규율이다(지시119). 그래서 문구가 «빠졌다» 가 아니라 **«안 냈다 + 까닭»** 이다.
       */
      unjudgedHeading: 'Sections with no judgement',
      /** 까닭이 **없을 때** — 지어내지 않는다. 「모른다」와 「이런 까닭이다」는 다른 말이다. */
      unjudgedNoReason: 'no reason recorded',
      /** 날 JSON 을 **지우지 않는다.** 접어서 뒤로 보낼 뿐이다 — 「가리는 순서를 바꾼다」. */
      rawOutputSummary: 'Raw output (JSON)',
      statusReportHeading: 'Report',
      statusReportBreached: 'Thresholds exceeded — this report is not the usual one.',
      statusReportOk: 'No thresholds exceeded.',
      /* ⚠ 사슬이 산출 모양을 바꾸면 이것이 뜬다. «이상 없음» 이 아니다. */
      statusReportMissing:
        'This is a status-check run, but its output carries no report in the declared shape — the console cannot say whether thresholds were exceeded.',
      noRunOutput: 'This run recorded no output.',
      /** 못 읽었다. 위와 **다른 말**이어야 한다. */
      runOutputError: 'Could not read this run\u2019s output',
      /**
       * **낸 것이 JSON 이 아니다** (지시111 걸쇠, 결정3442).
       *
       * 지시111 규칙9 가 *"코드펜스를 붙이지 마라"* 인데 **모델은 종종 붙인다.**
       * 그러면 이 칸이 객체가 아니라 **문자열**로 온다. 〔실측〕 화면은 그것을
       * `JSON.stringify` 로 감싸 `"```json\n{...}```"` 라는 **한 줄짜리 따옴표 덩이**로
       * 그렸다 — 옆의 제대로 된 JSON 세 덩이와 나란히 있으면 **그냥 값처럼 보인다.**
       *
       * 브리프의 말: *"요약 에이전트가 그 JSON 을 받으니 **앞이 깨지면 뒤가 지어낸다**."*
       * 그러니 사람이 **여기서** 알아야 한다.
       *
       * ⚠ **«틀렸다» 고 단정하지 않는다.** 문자열을 내는 것이 정상인 단계도 있다.
       *   말하는 것은 «이 값은 JSON 으로 안 읽힌다» 라는 **사실 하나**다.
       */
      outputNotJson: 'Not JSON — this value did not parse. A next step expecting JSON will not read it.',
      stepOutput: 'Output',
      /**
       * ★★ **답이 «있는데 비었다»** (gui 결정9441, llm 실측이 부른 자리).
       *
       * ## 왜 — 세 상태가 화면에서 **두 낯**이었다
       *
       * `step.output` 갈래를 그려 봤다 〔실측 2026-09-07 KST〕:
       * ```
       * output 없음(null·undefined)   화면에 **아무것도 안 뜬다**            ← 「안 냈다」
       * output === ''                 «Output» 만 뜬다. 펼치면 **빈칸**      ← 「펼쳐 보라」로 읽힌다
       * output === {}                 «Output» + `{}`                        ← 값이 보이긴 한다
       * ```
       * 가운데가 문제다. **펼치기 전까지 「답이 없다」와 「값이 있다」가 똑같이 생겼다** —
       * 그리고 펼쳐도 빈칸이라 «화면이 못 그린 건가» 로 읽힌다.
       *
       * ## 이 갈래가 **실물에서 난다**
       *
       * llm 이 지시110 의 모델을 재서 알려 줬다 〔llm 실측 13:1x KST〕: 그 모델은 답 전에
       * **«생각» 을 길게 낸다.** `max_tokens 128·512` 에서 `content` 가 **`""`**(finish=length)
       * 이고 `reasoning` 만 가득 찼다. 곧 **예산이 생각에 다 쓰이면 이 상태가 그대로 온다.**
       * ⚠ 목에는 이 갈래가 **0건**이었다 — 화면이 한 번도 이 상태로 안 그려졌다.
       *
       * ## ⚠ 까닭은 **말하지 않는다**
       *
       * «예산이 모자랐다» 라고 쓰고 싶지만 **계약에 그 칸이 없다**〔실측: `Step` 에
       * `finishReason`·`reasoningTokens` 0건, `AgentIo.truncated` 는 256KiB 저장 이야기라 **다른 것**〕.
       * 지어 쓰면 다른 까닭(모델이 정말 빈 답을 냈다·앞 단계가 빈 값을 넘겼다)일 때 **틀린 곳을 고치게 한다.**
       * ▶ 그래서 **사실 하나만** 말한다 — `outputNotJson` 이 «틀렸다» 대신 «안 읽힌다» 만 말하는 것과 같은 손.
       */
      stepOutputEmpty: 'Empty — the step finished and recorded an output, but there is nothing in it.',
      /**
       * **그 단계가 돈 기계** (gui 결정8475). `providerId`(추론이 간 곳)와 **다른 물음**이라
       * 문구가 그것을 갈라 말해야 한다 — 둘이 나란히 뜨므로 안 가르면 같은 것으로 읽힌다.
       */
      stepNodeHint: 'the machine this step ran on — not the same as where inference went',
      providerHint: 'The provider that actually served this step — it can be a different machine than the one the step ran on.',
      /*
       * **설계와 실제를 견준 결과** (결정1451). 계약이 이 몫을 화면에 맡겼다 —
       * *"화면이 이것을 설계 쪽과 견줘 어긋남을 말한다."*
       *
       * ⚠ 갈래를 셋으로 둔 이유: `unpinned` 을 어긋남으로 그리면 **거짓 경보**이고
       * (설계를 어긴 것이 없다), 같음으로 그리면 **거짓 안심**이다(아무도 의도한 적이 없다).
       */
      providerDrift: (designed: string) => `≠ designed for ${designed}`,
      /*
       * **불렀는데 어디로 갔는지 기록이 없다** (결정1454). 경보가 아니다 — 오늘은
       * 이게 모든 단계라 붉히면 폭주한다. 다만 **없음으로 뭉개지도 않는다.**
       */
      providerUnrecorded: '⇢ not recorded',
      providerUnrecordedHint:
        'This step called a model, but the run did not record which provider served it. Steps produced by an older worker build do not carry that field — it is missing history, not a step that skipped the model.',
      providerDriftHint:
        'This step did not go where the graph said it should. The model name alone cannot tell you this — the same name can exist on several providers.',
      providerUnpinnedHint:
        'The graph does not pin a provider for this step, so nothing was overridden — but nothing was intended either. This is how a run can silently land on the wrong machine.',
      loading: 'Loading…',
      notFound: 'Run not found',
      /*
       * ★ **«못 읽었다» 를 «없다» 로 말하지 않는다** (결정5451 과 같은 잣대 → 결정5461).
       *
       * 종전에는 `runQuery.isError || !runQuery.data` 를 한 문구(«Run not found»)로
       * 뭉갰다. 그런데 그 둘은 **사람이 할 일이 정반대**다:
       * ```
       * 없다      그 런은 지워졌거나 id 가 틀렸다 — 목록으로 돌아가면 된다
       * 못 읽었다  런은 있을 수 있다 — 권한·프록시·서버 문제고 **다시 시도할 일**이다
       * ```
       * «없다» 로 말하면 사람이 **있는 런을 없는 것으로 알고 포기한다.**
       */
      loadFailed: (message?: string) =>
        `Could not read this run${message ? ` — ${message}` : ''}. It may still exist — this is a read failure, not a missing run.`,
      back: '← Back to list',
      tokensLine: (tokens: string, cost: string, duration: string) => `Tokens ${tokens} · Cost ${cost} · Duration ${duration}`,
      timeLine: (started: string, ended: string) => `Started ${started} · Ended ${ended}`,
      openTrace: 'Open SigNoz trace ↗',
      /**
       * **이미 밖으로 나간 것이 있다** (지시111 위험, 결정3468).
       *
       * 실행이 `failed` 여도 **일부 갈래는 이미 나갔을 수 있다.** 비LLM 이 지시111 에서
       * 그 모양을 짚었다 — *"①은 이미 채널에 올라가 있다 … 올라간 것은 못 되돌린다."*
       * 그 옆에서 «같은 입력으로 다시» 를 누르면 **나갔던 것이 한 번 더 나간다.**
       *
       * ⚠ **막지 않는다.** 다시 돌릴지는 사람이 정한다 — 콘솔이 서버 판정을 새로
       * 만들지 않는다는 잣대(결정423)와 같은 자리다. **사실만 대고 단추는 그대로 둔다.**
       */
      /**
       * ⚠ **못 봤으면 «없다» 고 말하지 않는다** (결정3471).
       *
       * 위 경고는 질의 셋(그 실행이 쓴 판·정의·종류 선언)을 이어서 만든다. 하나라도
       * 실패하면 **결과가 빈 배열**이 되고, 그러면 화면은 «이미 나간 것이 없다» 와
       * 똑같이 **아무 말도 안 한다.** 안전 문구가 조용히 사라지는 것이 가장 나쁘다 —
       * 사람은 «확인됐고 괜찮다» 로 읽는다. 「빈 것과 없는 것은 다르다」.
       */
      irreversibleUnknown:
        'Could not check whether any part of this run already went out — some of that information failed to load. Reload before rerunning.',
      alreadyIrreversible: (steps: string) =>
        `Heads up: this run already finished ${steps}, which cannot be undone. Running it again will do it again.`,
      rerun: 'Rerun with same input',
      rerunning: 'Submitting rerun…',
      rerunSubmitted: (runId: string) => `Rerun submitted: ${runId}`,
      /**
       * 전체 중단(안전장치4)이 켜져 있으면 실행 기록만 남고 디스패치되지 않는다.
       * "submitted" 만 보여 주면 사람은 돌고 있다고 믿고 기다린다(결정151).
       *
       * **이 문구는 진작 있었는데 화면이 안 썼다**(결정435). 같은 라우트를 쓰는
       * `AgentIo` 는 읽고 있었고 여기만 빠져 있었다 — 문구를 적어 둔 것과 그것이
       * 화면에 닿는 것은 다른 일이다.
       */
      rerunHeldByGlobalStop: (runId: string) =>
        `Recorded as ${runId}, but not dispatched — the global stop is on. Turn it off under Settings and rerun.`,
      stepsHeading: 'Step timeline (run_steps)',
      noSteps: 'No recorded steps',
      /*
       * **못 읽은 것과 없는 것은 다르다** (결정1401). 종전에는 조회가 실패하면 단계
       * 자리가 **그냥 비었다** — `noSteps`("기록된 단계가 없다")와 구별이 안 됐다.
       * 실행이 실패했는데 단계가 안 보이는 것과, 단계 조회가 실패한 것은 **할 일이 다르다.**
       */
      stepsError: 'Could not load the steps of this run',
    },
  },

  agentIo: {
    heading: 'Agent I/O',
    runIdLabel: 'Run ID',
    lookUp: 'Look up',
    loading: 'Loading…',
    loadError: 'Could not load I/O history',
    rerun: 'Rerun with same input',
    rerunning: 'Submitting rerun…',
    rerunSubmitted: (runId: string) => `Rerun submitted: ${runId}`,
    /**
     * 전체 중단(안전장치4)이 켜져 있으면 실행 기록만 남고 디스패치되지 않는다.
     * "submitted" 만 보여 주면 사람은 돌고 있다고 믿고 기다린다(결정151).
     */
    rerunHeldByGlobalStop: (runId: string) =>
      `Recorded as ${runId}, but not dispatched — the global stop is on. Turn it off under Settings and rerun.`,
    noIo: 'No recorded I/O',
    inlineLimitExceeded: (bytes: string) => `Inline limit exceeded — content stored externally on the server (${bytes} bytes)`,
    runNotLoadedYet: 'Run details must be loaded first',
    /*
     * ★ **왜 못 누르는지 말한다** (결정5402).
     *
     * 「다시 실행」은 `runQuery.data` 가 있어야 눌린다 — 그 조회가 실패하면 단추가
     * **영영 회색인데 화면은 아무 말도 안 했다.** 읽기 실패는 위에서 말하면서
     * 이 실패만 삼키고 있었다. 「가려진 것은 없는 것이다」.
     *
     * 팔레트에서 이미 같은 판단을 했다 — 못 누르는 것에는 **까닭을 붙인다**(결정4469).
     */
    rerunNeedsRunDetails: 'Cannot rerun — the run details did not load',
    /** 아직 오는 중. 실패와 **다른 말**이다 — 기다리면 되는 것과 안 되는 것은 다르다. */
    rerunWaitingForRunDetails: 'Cannot rerun yet — still loading the run details',
    // 결정132: 기본값이 목 픽스처의 `run-001` 이라 화면에 들어서는 순간 운영에
    // 없는 ID 로 404 를 냈다. 비워 두되 **무엇을 해야 하는지는 말한다** —
    // 빈 화면은 고장난 것처럼 보인다.
    enterRunId: 'Enter a run ID above to see its agent I/O. You can copy one from the Runs screen.',
  },

  approvals: {
    /* 지시184 — 메모 칸의 보이는 이름. */
    notesLabel: 'Notes',
    /** 승인·거절이 실패했을 때. 어느 조작인지 말하고 서버 원문을 붙인다. */
    /*
     * **끝난 결재도 보인다** (결정491). 종전에는 대기만 그렸고, 결재가 끝나는 순간
     * 그 줄이 사라졌다 — *"누가 무엇을 승인했나"* 를 콘솔에서 볼 길이 없었다.
     * 결정465 가 전체 중단에 대해 세운 물음과 같다: 제일 먼저 묻는 것이 "누가" 다.
     */
    historyHeading: 'Decided',
    historyNone: 'Nothing has been decided yet.',
    /** 서버가 실은 결정값을 그대로 옮긴다 — 화면이 이름을 짓지 않는다. */
    decidedLine: (decision: string, at: string) => `${decision} · ${at}`,
    /** 누가 눌렀는가. 없으면 이 줄 대신 아래 문구가 나온다 — 지어내지 않는다(결정465). */
    decidedBy: (who: string) => `by ${who}`,
    /**
     * **아무도 안 누른 결정.** 시간 초과는 `decidedBy` 가 없다 — 사람이 한 것이
     * 아니라는 뜻이고, 그 사실 자체가 정보다.
     */
    decidedByNobody: 'no one pressed this — it timed out',
    decideFailed: 'Could not record that decision',
    heading: 'Approvals',
    description:
      'In the early MVP, Slack thread replies remain a parallel channel while the console matures into the primary one (06 §2-④).',
    loading: 'Loading…',
    loadError: 'Could not load pending approvals',
    none: 'No approvals pending',
    requested: (when: string) => `Requested ${when}`,
    notesPlaceholder: 'Notes (optional)',
    approve: 'Approve',
    reject: 'Reject',
  },

  /* Database 화면 (지시220) — 플릿의 DB 를 종류별로, 서버 → 데이터베이스 → 표 → 내용. 읽기만. */
  databases: {
    title: 'Database',
    intro: 'Databases on the fleet, grouped by kind. Read-only: nothing here can change a row.',
    loading: 'Checking each machine for a database…',
    none: 'No registered machine was found.',
    engineHeading: (engine: string, n: number) => `${engine} · ${String(n)}`,
    engineName: (engine: string) => (engine === 'postgres' ? 'PostgreSQL' : engine),
    open: (serverId: string) => `Open ${serverId}`,
    version: (v: string) => v,
    listening: (addrs: string) => `listening on ${addrs}`,
    unreachable: (why: string) => `Could not reach this machine, so it is unknown whether it has a database: ${why}`,
    /* 서버 목록의 기다림 (지시222) — 처음은 2초, 다시 훑기는 멈추라고 할 때까지(길어야 1분). */
    notConnected: (seconds: number) => `Not connected — no answer within ${String(seconds)} s. Rescan to keep looking.`,
    rescan: 'Rescan',
    stop: 'Stop',
    scanning: (elapsed: number, max: number, missing: number) => `Scanning… ${String(elapsed)} s of ${String(max)} s — ${String(missing)} machine${missing === 1 ? '' : 's'} not answering yet`,
    scanDone: (missing: number) => (missing === 0 ? 'Every machine answered.' : `Scan ended — ${String(missing)} machine${missing === 1 ? '' : 's'} still not answering.`),
    presence: {
      ours_running: 'running',
      ours_stopped: 'installed but stopped',
      foreign_listening: 'another server is listening here — not installed by the platform, so it cannot be opened',
      absent: 'no database on this machine',
    } as Record<string, string>,
    back: '← All databases',
    serverTitle: (serverId: string) => `Database · ${serverId}`,
    catalogs: 'Databases',
    tables: 'Tables',
    pickCatalog: 'Pick a database.',
    pickTable: 'Pick a table to see its columns and rows.',
    noTables: 'This database has no tables.',
    size: (bytes: string) => bytes,
    estimatedRows: (n: number) => (n < 0 ? 'rows not counted yet' : `~${String(n)} rows`),
    tableKind: { table: 'table', view: 'view', materialized_view: 'materialized view', partitioned_table: 'partitioned table' } as Record<string, string>,
    metadata: 'Metadata',
    metaLine: (kind: string, rows: number, size: string) => `${kind} · ${String(rows)} rows · ${size}`,
    columns: 'Columns',
    colName: 'Column',
    colType: 'Type',
    colNullable: 'Nullable',
    colDefault: 'Default',
    colKey: 'Key',
    primaryKey: 'primary key',
    yes: 'yes',
    no: 'no',
    indexes: 'Indexes',
    noIndexes: 'No indexes.',
    contents: 'Contents',
    sortBy: (column: string, dir: string) => `Sort by ${column}, ${dir}`,
    sorted: (dir: string) => (dir === 'asc' ? '▲' : '▼'),
    ascending: 'ascending',
    descending: 'descending',
    filterLabel: 'Filter',
    valueLabel: 'Value',
    filterOp: (column: string) => `How to filter ${column}`,
    filterValue: (column: string) => `Value to filter ${column} by`,
    ops: {
      '': 'no filter',
      contains: 'contains',
      starts: 'starts with',
      eq: '=',
      ne: '≠',
      gt: '>',
      gte: '≥',
      lt: '<',
      lte: '≤',
      isnull: 'is empty (null)',
      notnull: 'is not empty',
    } as Record<string, string>,
    apply: 'Apply filters',
    clear: 'Clear filters',
    showing: (from: number, to: number, total: number) => (total === 0 ? 'No rows match.' : `Rows ${String(from)}–${String(to)} of ${String(total)}`),
    previous: 'Previous',
    next: 'Next',
    pageSize: 'Rows per page',
    nullCell: 'null',
    truncatedNote: 'Long values are cut at 300 characters and binary columns show their size only.',
    /* 엑셀로 내려받기 (지시221) — 지금 건 정렬·거르개 그대로, 쪽이 아니라 **맞는 줄 전부**. */
    download: 'Download .xlsx',
    /* 내려받을 열을 고르는 작은 창 (지시221). 이진 열은 처음에 꺼져 있다 — 사람이 일부러 켤 때만 싣는다. */
    exportTitle: (table: string) => `Download ${table} as .xlsx`,
    exportPick: 'Columns to include',
    exportColumn: (name: string, type: string) => `${name} (${type})`,
    exportBinaryHint: 'binary — off by default',
    exportBinaryNote: 'A binary column is written as base64 only where the value fits in one cell (24 KB). Larger values carry their size instead.',
    exportAll: 'Select all',
    exportNone: 'Select none',
    exportRemembered: 'Your last choice for this table is restored.',
    exportNeedOne: 'Pick at least one column.',
    exportGo: 'Download',
    exportCancel: 'Cancel',
    downloading: (done: number, total: number) => `Downloading… ${String(done)} / ${String(total)}`,
    downloadNote: (max: number) => `All matching rows in the current order and filters, up to ${String(max)} rows. Binary columns carry their size only.`,
    downloadCapped: (max: number, total: number) => `Only the first ${String(max)} of ${String(total)} rows were written — narrow the filters to get the rest.`,
    sheetInfo: 'export info',
    infoField: 'Field',
    infoValue: 'Value',
    infoServer: 'server',
    infoDatabase: 'database',
    infoTable: 'table',
    infoColumns: 'columns',
    infoSort: 'sort',
    infoFilters: 'filters',
    infoRows: 'rows written',
    infoTotal: 'rows matching',
    infoAt: 'exported at',
    infoNone: '(none)',
    failed: (doing: string, why: string) => `${doing} failed: ${why}`,
    doingList: 'Listing databases',
    doingCatalogs: 'Reading the database list',
    doingTables: 'Reading the table list',
    doingMeta: 'Reading the table metadata',
    doingRows: 'Reading the rows',
  },
  fleet: {
    /**
     * **주소 판정의 문구는 콘솔이 갖는다** (결정3421).
     *
     * 〔실측 08-30〕 서버는 `addressNote` 에 **한국어 산문**을 실어 준다
     * (`provisioning/tailnet-address.ts` 의 `tailnetAddressReason` — CLI·로그와 공용).
     * `FleetScreen` 은 그것을 `title` 로 **그대로 그리고 있었다** — 영어 화면에
     * 한국어 한 줄이 뜨는 자리이고, 잔여 한글 관문은 목이 그 값을 안 실어서
     * **한 번도 안 걸렸다**(목이 그 칸을 0건 채웠다 — 결정3417 이 세운 관문이
     * 이 자리를 짚어 줬다).
     *
     * **판정은 서버 것, 문구는 콘솔 것**으로 가른다. 판정을 다시 계산하지 않으므로
     * 규칙이 두 벌이 되지 않는다(결정469 가 경계한 그것) — 콘솔은 서버가 내린
     * 낱말 하나를 사람 말로 옮길 뿐이다.
     */
    /*
     * ★ **눈에 보이는 낱말도 콘솔이 쓴다** (결정5416).
     *
     * 바로 아래 `addressVerdictNote` 주석이 *"판정은 서버 것, **문구는 콘솔 것**"* 이라고
     * 적어 뒀는데, 그 잣대를 **툴팁에만** 댔다. 칸에는 서버 낱말이 그대로 떴다 —
     * 〔글로 읽어 알았다〕 상태 칸이 이렇게 읽혔다:
     *
     * ```
     * ec2-cpu-1 · CPU · x86_64 · online **missing** · 2026-08-24 …
     *                            ↑ 노드가 «없다» 는 뜻으로 읽힌다. 실제 뜻은
     *                              «tailnet 주소가 없다» 다
     * ```
     *
     * ⚠ 판정을 **다시 계산하지는 않는다** — 서버가 준 낱말을 사람 말로 옮길 뿐이다
     * (결정469 가 경계한 «규칙 두 벌» 이 안 생긴다).
     * ⚠ 모르는 판정은 **그 낱말 그대로** 낸다 — 서버가 갈래를 늘려도 칸이 빈칸이
     * 되지 않는다(지시107).
     */
    addressVerdictLabel: (verdict: string): string =>
      ({
        missing: 'no tailnet address',
        not_ipv4: 'not IPv4',
        ipv4_mapped: 'IPv4-mapped',
        outside_tailnet: 'outside tailnet',
      })[verdict] ?? verdict,
    addressVerdictNote: (verdict: string): string =>
      ({
        missing:
          'No tailnet address on this node — there is no key to match it against a discovered machine.',
        not_ipv4: 'Not an IPv4 address. A tailnet address is IPv4 inside 100.64.0.0/10.',
        ipv4_mapped:
          'IPv4-mapped IPv6 notation. The address is right but the spelling differs — record it as plain IPv4.',
        outside_tailnet:
          'This IPv4 address is outside the tailnet range. The console will treat it as a different machine.',
      })[verdict] ??
      /* 모르는 판정이 오면 **지어내지 않는다** — 낱말만 보여 준다(§0-2-2). */
      verdict,
    /**
     * **큐 적체** (서버 결정715, 결정2423).
     *
     * ⚠ 이건 오류가 아니다. 잡이 **실패하지 않은 채 앉아 있는** 것이다 —
     * 그래서 문구에 fail/error 를 안 쓴다. 사람이 고칠 것은 **듣는 쪽**이다.
     */
    queuesHeading: 'Queues',
    queuesLoadError: 'Could not read queue health',
    /** 다 멀쩡할 때. **아무 말도 안 하면 "안 봤다" 와 같아 보인다.** */
    /**
     * **큐가 막힌 사유를 콘솔 말로 쓴다** (결정3429).
     *
     * 〔실측 08-31, 실물 브라우저〕 `FleetScreen` 이 서버의 `problem` 을 **그대로** 그렸고
     * 그 값은 **한국어 문단**이다(`workers/node-agent/queue-health.ts`). 영어 화면에
     * 한국어 두 줄이 떠 있었다 — 아홉 화면의 글자를 장부·목과 맞대 보다 나왔다.
     *
     * ⚠ `nodeIdDerivedMismatch`(gui-canvas 결정3485) 와 **다른 자리다.** 그쪽은 유도된
     * 노드 id 가 산문 안에만 있어 다시 쓰면 **정보가 줄었다.** 여기는 아니다 —
     * 계약이 `waiting`·`oldestWaitingSeconds`·`expectedListeners` 를 **값으로 다 준다.**
     * 그래서 콘솔이 써도 **한 글자도 안 잃는다.** 서버의 두 갈래를 그대로 옮긴다.
     *
     * 갈래를 지어내지 않는다 — 서버가 가른 그대로다:
     * 들을 노드가 **없다** / 있는데 **안 집어간다**(이때는 하트비트를 보라고 가리킨다).
     */
    /**
     * ⚠ **나이를 «0분» 으로 접지 않는다** (env 결정4707 의 짝, gui 결정8416).
     *
     * 계약에서 `oldestWaitingSeconds` 는 **선택 칸**이다. 종전에 화면이 `?? 0` 으로 접었는데,
     * 그러면 **문제가 있다고 말하는 바로 그 줄이 «0분째 기다린다»** 고 한다 — 사람은
     * 그것을 «방금 생긴 일» 로 읽고 넘긴다. 「없는 것과 0 은 다르다」.
     *
     * ⚠ env 가 서버 쪽에서 재 보니 **`problem` 이 있으면 그 값은 반드시 있다**(그쪽 `stalled`
     * 조건이 함께 요구한다). 곧 지금은 닿지 않는 갈래다 — 그런데도 고치는 까닭이 그쪽 주석에
     * 적힌 것과 같다: **`?? 0` 은 읽는 사람에게 «0분이 나올 수 있다» 고 가르치고**, 그 불변식을
     * 모른 채 위쪽을 고치면 그때 진짜로 «밀렸는데 0분» 이 나온다.
     * ▶ 서버는 시끄럽게 죽는 쪽을 골랐다. **화면은 죽으면 안 되므로 «모른다» 를 말한다.**
     */
    queueWaitedUnknown: 'an unknown time',
    queueProblemNoListener: (waiting: number, minutes: number | undefined): string =>
      `${waiting} ${waiting === 1 ? 'job has' : 'jobs have'} waited ${minutes === undefined ? strings.fleet.queueWaitedUnknown : `${minutes} min`} and no online node ` +
      'can listen to this queue — bring up a node with that capability, or send the jobs back.',
    queueProblemNotPicking: (waiting: number, minutes: number | undefined, listeners: readonly string[]): string =>
      `${waiting} ${waiting === 1 ? 'job has' : 'jobs have'} waited ${minutes === undefined ? strings.fleet.queueWaitedUnknown : `${minutes} min`}. ` +
      `${listeners.join(' · ')} ${listeners.length === 1 ? 'is' : 'are'} listed online in the fleet table ` +
      'but not picking up — either that process does not subscribe to this queue, or the table is stale ' +
      'and the machine is actually off. Check that node’s heartbeat first; that is what tells the two apart.',

    queuesAllHealthy: (n: number) => `${n} ${n === 1 ? 'queue' : 'queues'}, none stalled.`,
    /**
     * **아무도 안 듣는 큐** — 적체가 없어도 말한다 (지시153 곁, gui 결정9470).
     *
     * 〔실측 2026-09-08 07:4x KST〕 서버의 판정은 **적체만** 본다:
     * `stalled = waiting > 0 && oldestWaitingSeconds > staleAfter` — `expectedListeners` 가
     * **안 들어간다**(`workers/node-agent/queue-health.ts` 의 `const stalled =` 로 시작하는 줄). 그래서 청취자가 0인 큐도
     * 아직 안 밀렸으면 `stalled: false` 이고, 화면은 **«none stalled»** 만 말했다.
     * 그 사실(`queueNoListener`)은 **적체 가지 안에서만** 그려져 **영영 안 보였다.**
     *
     * ⚠ **«건강하지 않다» 고 판정하지 않는다**(결정423 — 판정은 서버 것이다).
     *   서버가 준 **사실**을 그대로 옮길 뿐이다: 몇 개가 청취자를 안 가졌나.
     *   잡을 일이 있는지는 사람이 정한다 — 안 쓰는 큐면 정상일 수도 있다.
     * ★ 이 절의 제 주석이 이미 잣대를 적어 뒀다: *「다 멀쩡해도 **말은 한다.**
     *   침묵은 «안 봤다» 와 같아 보인다」* — 한 겹이 모자랐던 자리다.
     */
    queuesNoListener: (n: number) =>
      `${n} of them ${n === 1 ? 'has' : 'have'} no listener — nothing is set up to pick their jobs up. Not stalled yet; jobs would pile up first.`,
    /** 막힌 것이 있을 때의 머리줄. 서버가 센 수를 그대로 쓴다. */
    /**
     * 서버가 쓴 **적체 기준**. 판정을 설명하는 값이라 판정 옆에 둔다 (결정2428).
     *
     * ⚠ 화면이 이 값으로 **다시 판정하지 않는다**(결정423). 서버가 이미 `stalled` 를
     *   정했고, 이건 *"무엇을 기준으로 그렇게 봤나"* 를 사람에게 보이는 것뿐이다.
     */
    queuesThreshold: (text: string) => `stale after ${text}`,
    queuesStalled: (n: number) => `${n} ${n === 1 ? 'queue is' : 'queues are'} stalled`,
    /** 아무도 안 듣는다 — 이 줄이 이 화면의 요점이다. */
    queueNoListener: 'no listener',
    queueWaiting: (n: number) => `${n} waiting`,
    /** 제일 오래 기다린 잡. 서버가 못 재면 이 칸이 아예 없다(0 이 아니다). */
    queueOldest: (text: string) => `oldest ${text}`,
    heading: 'Fleet',
    loading: 'Loading…',
    /*
     * ★ **까닭을 붙일 자리를 만든다** (결정4462 의 잣대 → 결정5461).
     * «못 읽었다» 만 말하면 권한·프록시·서버 중 무엇인지 모르고, 셋은 사람이 할 일이
     * 서로 다르다. 서버가 준 까닭을 그대로 잇는다(`describeError`).
     */
    loadError: (message?: string) => `Could not load fleet status${message ? ` — ${message}` : ''}`,
    /**
     * **줄에서 바로 하는 일** (지시161).
     *
     * 🔴 운영자 실측 화면: 노드 줄과 «Update the bundle on …» 상자가 **화면 아래위로
     *   갈라져** 있어 어느 줄에 대한 것인지 눈으로 이어야 했다.
     * ⚠ **흩어진 것을 모으는 일이지 값을 새로 만드는 일이 아니다** — 여기 있는 것은
     *   전부 **이미 있는 손**을 이 자리로 데려온 것이다.
     */
    actions: {
      update: 'Update…',
      /** ⚠ 우클릭도 그대로 둔다 — 익힌 사람의 손을 뺏지 않는다(지시137 ②). */
      updateHint: 'Right-click the row does the same thing.',
      /**
       * ⚠ **«판을 모르는 줄» 에서는 못 누른다.** 무엇으로 올릴지 모르는데 창을 열면
       * 사람이 빈 창을 보고 «고장» 으로 읽는다(지시137 ② 가 그은 그 선).
       */
      updateUnknown: 'This row has no build verdict yet, so there is nothing to update to.',
      /**
       * ★ **다시 읽기.** 이 화면이 보여 주는 것을 서버에서 새로 읽는다.
       * ⚠ 이것은 **기계를 두드리지 않는다** — tailnet 을 훑는 것은 값이 비싸고, 그 손은
       *   «Add a node…» 창 안에 이미 있다(«다시 훑기»). 둘을 뭉치면 표를 새로 보고 싶을
       *   때마다 함대 전체를 두드리게 된다.
       */
      rescan: 'Rescan',
      /**
       * **함대 전체 올리기** (지시163).
       *
       * 🔴 종전에는 노드마다 우클릭→창→올리기를 되풀이해야 했다 — 오늘 넷을 올리는 데
       *   운영자가 **스크립트를 썼다.**
       * ⚠ 되돌릴 수 없는 원격 조작이 **여러 대**에 간다. 그래서 누르기 전에 **몇 대에
       *   무엇이 일어나는지** 이름을 대어 말한다(지시137 ② 의 잣대를 대수만큼 늘린 것).
       */
      updateAll: 'Update All…',
      /**
       * **Tailscale 을 화면에서 다룬다** (지시164).
       *
       * 🔴 종전에는 tailscale 관련 설정이 화면에 **없었다** — 스크립트와 자격 보관소로만
       *   만졌다. 그래서 지시70·141 이 이 자리에서 막혀 있었다: 둘이 기다리는 것이
       *   «재사용 가능한 auth key 를 넣는 길» 이고, 실측으로 등록된 `tailscale_auth_key` 가
       *   **0건**이었다.
       */
      tailscale: 'Tailscale Management…',
      /**
       * **줄에서 치운다** (지시173).
       *
       * 🔴 «지운다» 가 아니라 «치운다» 인 것은 **값이 정했다**: `runs`·`deployments` 가
       *   NO ACTION 이라 이력 있는 노드는 삭제가 막히고, 안 막히는 노드에서는
       *   abilities·credentials 가 CASCADE 로 사라지고 배선(`agent_defs.execution_node_id`)이
       *   널이 된다.
       * ⚠ 그래서 문구가 **무엇이 사라지지 «않는지»** 를 말한다. 안 말하면 사람은
       *   되돌릴 수 없는 일을 한다고 믿고 못 누르거나, 반대로 지운 줄 안다.
       */
      remove: 'Remove…',
      removeTitle: (node: string) => `Remove ${node} from the fleet list`,
      /** ⚠ **사라지는 것은 줄 하나뿐이다.** 그 사실을 먼저 말한다. */
      removeWhatGoes: 'It disappears from this list.',
      /** ⚠ **안 사라지는 것**을 이름 대어 말한다 — 이것이 이 창의 핵심이다. */
      removeWhatStays: [
        'The machine itself is not touched — nothing is uninstalled or stopped.',
        'Past runs and their steps stay, and you can still open them.',
        'Pipelines that point at this node keep pointing at it.',
      ],
      /**
       * ⚠ **되살아난다.** 안 말하면 «지웠는데 돌아왔다» 가 되고, 사람은 제품이 고장 난
       * 줄 안다. 그리고 그 되살아남이 **옳다** — 함대는 보고하는 것이다.
       */
      removeComesBack:
        'If that machine reports in again, it comes back to this list. The fleet is what reports, not what we remember.',
      /*
       * ★★★ **무엇이 딸려 죽는지 먼저 센다** (지시179 ①).
       *
       * 🔴 운영자가 노드 하나를 없애려 했을 때 화면은 **한 글자도** 말하지 않았다.
       *    그래서 「없는 것 같다」로 지우면 `slack.post ← 슬랙 봇 토큰` 이 유령으로 남는다.
       * ⚠ 표 이름을 **그대로** 보인다 — 화면이 예쁜 이름을 지어내면 사람이 그것을
       *   어디서 찾아야 할지 모른다(서버가 세고 화면은 옮긴다).
       */
      boundTitle: 'Still wired to this node:',
      boundNothing: 'Nothing is wired to this node.',
      boundCounting: 'Counting what is wired to this node…',
      boundUnreadable: 'Could not read what is wired to this node — this is not the same as “nothing”.',
      /** ⚠ **남는 것**을 수로 말한다. 이력은 끊을 것이 아니다. */
      historyTitle: 'Stays, and is not deleted:',
      /*
       * ★★★ **순서를 화면이 지킨다** (지시179 ①).
       *
       * 🔴 살아 있는 노드를 치우면 하트비트가 `removed_at` 을 지워 **곧 돌아온다**
       *    (지시173 이 일부러 그렇게 했다). 그것을 모르면 사람은 제품이 고장 난 줄 안다.
       * ⚠ 그래서 **막지 않고 말한다** — 「그래도 치운다」는 정당한 선택이다(잠시 감추기).
       */
      stillReporting:
        'This node is reporting right now, so removing it from the list only hides it until the next heartbeat. Turn the machine (or its agent) off first if you mean it to stay gone.',
      /** ⚠ 능력은 **장부에서만** 거둔다 — 그 기계의 값은 그대로다. */
      revokeAbility: 'Revoke',
      revokeAbilityDone: (note: string) => note,
      removeConfirm: 'Remove from the list',
      removeRunning: 'Removing…',
      removeFailed: 'Could not remove it: ',
      removeCancel: 'Cancel',
      /* 지시192 — 치우면 그 기계에만 묶인 자격을 함께 폐기한다. 폐기는 되돌릴 수 없어 누르기 전에 이름으로 보인다. */
      credentialsRevokeTitle: 'Revoked with it — only this machine uses these credentials:',
      credentialsKeptTitle: 'Kept — this machine uses these, but something else does too:',
      credentialsNone: 'No credential is tied only to this machine.',
      credentialsNotYours: (n: number) => `${n} more tied to this machine belong to someone else and are left alone.`,
      credentialsUnchecked: 'Credentials could not be checked here, so none will be revoked.',
      credentialLine: (name: string, kind: string, boundBy: string) => `${name} (${kind}) · ${boundBy}`,
      credentialKeptLine: (name: string, kind: string, where: string) => `${name} (${kind}) · also used by ${where}`,
      revokeIsFinal:
        'Revoking cannot be undone. If this machine reports again it comes back to the list, but these credentials do not.',
      removeConfirmRevoking: (n: number) => `Remove and revoke ${n} credential${n === 1 ? '' : 's'}`,
      removedResult: (node: string, n: number) => `Removed ${node}. Revoked ${n} credential${n === 1 ? '' : 's'}.`,
      removedKept: (n: number) => `Kept ${n} that something else still uses.`,
      removedFailed: (name: string, error: string) => `Could not revoke ${name}: ${error}`,
      removedUnchecked: 'Credentials were not checked, so none were revoked.',
      removedClose: 'Close',
      tailscaleTitle: 'Tailscale',
      /** ① 지금 누가 붙어 있나 — **우리 장부가 아는 것**만 말한다. 기계를 두드리지 않는다. */
      tsWhoTitle: 'Nodes and their tailnet addresses',
      tsNoAddress: 'no address recorded',
      /** ⚠ 「주소가 없다」와 「탐지를 안 했다」는 다른 말이다 — 여기서는 앞엣것만 안다. */
      tsWhoNote:
        'This is what the platform has recorded, not a live scan. A node with no address can never be matched to a scan result.',
      /** ② 가입 키 */
      tsKeysTitle: 'Join keys',
      tsKeysNone:
        'No tailnet join key is registered yet. Without one the platform cannot put a new machine on the tailnet — that is what blocks joining from the console today.',
      tsAddKey: 'Register a join key',
      tsKeyName: 'Name',
      tsKeySecret: 'Auth key (tskey-auth-…)',
      tsKeySave: 'Register',
      tsKeySaving: 'Registering…',
      tsKeySaved: 'Registered.',
      tsKeyFailed: 'Could not register the key: ',
      /** ③ 가입 안 된 기계 붙이기 */
      tsJoinTitle: 'Put a machine on the tailnet',
      tsJoinTarget: 'Machine (registered SSH target)',
      tsJoinKey: 'Join key',
      tsJoinAck:
        'I understand: tailscale is installed on that machine and left running there.',
      tsJoin: 'Join the tailnet',
      tsJoining: 'Joining…',
      tsJoined: 'The join command finished. Rescan to see it on the tailnet.',
      tsJoinFailed: 'The join did not finish: ',
      /** ⚠ 고를 것이 없으면 **왜 없는지** 말한다 — 빈 드롭다운은 「고장」으로 읽힌다. */
      tsNoTargets: 'No SSH target is registered, so there is no machine to put on the tailnet yet.',
      /**
       * ⚠ **«못 읽음» 을 «없음» 과 가른다** (결정3480). 「키가 0건」과 「목록을 못 읽었다」는
       * 사람이 할 일이 정반대다 — 앞엣것은 키를 넣는 일이고 뒤엣것은 **넣으면 안 되는** 일이다
       * (이미 있는데 안 보이는 것일 수 있다).
       */
      tsKeysUnreadable: 'Could not read the credential list — this does not mean no key is registered. ',
      tsTargetsUnreadable: 'Could not read the target list — this does not mean none are registered. ',
      updateAllTitle: 'Update every node that is behind',
      updateAllNone: 'Every node already runs the reference build — there is nothing to update.',
      /**
       * ⚠ **굽기 직후에는 시작하지 않는다** (결정7752). 굽기와 겹쳐 죽으면
       * `provision_runs` 에 빈 행이 남아 다음 시도가 409 로 튕기고 그 노드가 **30분 잠긴다.**
       * 쓸기 자가 «가라앉기» 를 최대 20초까지 기다린다 — 여기서는 같은 사실(굽기 도장)을
       * 읽어 그 창 안이면 **막고 까닭을 말한다.** 손을 다시 짓지 않는다.
       */
      updateAllTooFresh:
        'The reference build was baked moments ago. Wait for it to settle, then try again — starting now can collide with the bake and lock those nodes out for 30 minutes.',
      updateAllConfirm: (n: number) => (n === 1 ? 'Update 1 node' : `Update ${n} nodes`),
      updateAllRunning: (done: number, total: number) => `Updating ${done} of ${total}…`,
      /** ⚠ **«전 → 후» 를 대당 적는다** — 「몇 대 성공」만으로는 무엇이 움직였는지 모른다. */
      updateAllRow: (display: string, before: string, after: string) =>
        `${display}: ${before} → ${after}`,
      updateAllRowFailed: (display: string, why: string) => `${display}: did not finish — ${why}`,
      updateAllDone: (ok: number, total: number) => `${ok} of ${total} nodes updated.`,
      /** 아직 안 잰 판은 «없다» 가 아니라 «모른다» 다(결정518 의 잣대). */
      updateAllUnknownBuild: 'unknown',
      rescanning: 'Rescanning…',
      create: 'Add a node…',
    },
    columns: {
      node: 'Node',
      /** 지시161 — 줄에서 바로 할 수 있는 일. */
      actions: 'Actions',
      capabilities: 'Capabilities',
      status: 'Status',
      lastHeartbeat: 'Last heartbeat',
      /*
       * **이 노드가 돌리는 번들 판** (결정1402).
       *
       * 오늘 이것이 안 보여서 데모가 막혔다 — `s-ai-pro6000-37` 이 **23.7시간 전
       * 번들**을 돌고 있었고 그 판에는 발행한 파이프라인이 아예 없었다. 발행은
       * 성공했고 `drift` 도 clean 이라 **화면에는 아무 이상이 없었다.**
       */
      agentBuild: 'Bundle',
    },
    noNodes: 'No registered nodes',
    cpuOnly: 'CPU',
    /*
     * **안 잰 것을 "CPU" 라고 말하지 않는다** (지시77 gui 몫, 결정518).
     *
     * 종전에는 역량이 하나도 없으면 `cpuOnly` 를 냈다. 그런데 그것은 **잰 결과**를
     * 말하는 문장이고, 실제로 그 자리에 오는 것은 **아직 안 다녀온 기계**다
     * 〔실측: `icmoon-office-desktop` 은 `capabilities.arch` 조차 없다〕.
     *
     * 이 저장소가 여러 번 적은 그것이다 — *"빈 것과 없는 것은 다르다"*,
     * *"0 은 공짜가 아니라 모름"*. 안 잰 것을 잰 것처럼 적으면 **GPU 를 붙여 둔
     * 기계를 CPU 뿐이라고 읽고** 배치에서 건너뛴다.
     */
    /**
     * 번들 판을 **아직 보고한 적이 없다.** *"판이 없다"* 가 아니라 *"모른다"* 다 —
     * 서버는 값이 없으면 칸을 생략한다. 빈칸으로 두면 이 둘이 같아 보인다.
     */
    agentBuildUnknown: 'Never reported',
    /**
     * **닿을 수 없는 기계의 판** (지시137 ①, 결정6455).
     *
     * 지시 원문: *«offline 노드의 버전은 «뒤처짐» 이 아니라 «닿을 수 없음» 이다.
     * 섞어 보이지 마라»*. 닿을 수 없는 기계의 판이 옛것인 것은 **당연**하고,
     * 그것을 뒤처짐과 같은 무게로 그리면 **고칠 수 있는 줄이 묻힌다.**
     *
     * ⚠ 색으로만 가르지 않는다 — 낱말이 뜻을 나른다(색을 못 보는 사람이 있다).
     */
    agentBuildUnreachable: 'unreachable',
    /**
     * **판 대조** (지시137 ①, 결정6465).
     *
     * ⚠ **여섯 갈래를 낱말로 가른다.** 지시137 원문: *«색만으로 가르지 마라 — 색을 못
     * 보는 사람이 있다»*. 그래서 아래는 전부 **글자**이고, 색은 거들 뿐이다.
     *
     * ⚠ `ahead` 를 «뒤처짐» 과 같은 통에 넣으면 **다운그레이드를 «올리기» 라고 부르게 된다.**
     * 커밋에는 순서가 없다 — 플랫폼이 되돌려졌으면 노드가 더 새것일 수 있다.
     */
    builds: {
      title: 'Bundle versions',
      /** 기준을 **이름으로** 박는다 — «뒤처짐» 을 재는 값은 재는 순간 낡는다(integrate 지적). */
      baseline: (commit: string) => `Compared against ${commit}`,
      baselineAt: (when: string) => `built ${when}`,
      /** ⚠ `process` 는 «번들을 못 읽어 도는 판으로 쟀다» 는 뜻이다 — 그러면 «올리면 최신» 이 참이 아닐 수 있다. */
      baselineFromProcess:
        'The bundle could not be read, so this compares against the version this server is running. Updating a node may not reach that version.',
      /** ⚠ 기준이 없으면 **전부 «모른다»** 다. «전부 최신» 으로 그리면 거짓말이다. */
      noBaseline:
        'No reference build is available, so none of these can be compared. This does not mean they are up to date.',
      verdict: {
        same: 'up to date',
        behind: 'older than the reference',
        /** ⚠ 「올리기」가 아니라 **「내리기」**인 자리다. */
        ahead: 'NEWER than the reference — updating would move it backwards',
        differs: 'different, and we cannot tell which is newer',
        unknown: 'version not known',
        unreachable: 'cannot be reached',
      },
      /** 나이는 **`behindSeconds`** 로 만든다 — 브라우저 시계로 재면 어긋난 판을 «최신» 으로 읽는다. */
      behindBy: (age: string) => `behind by ${age}`,
      /**
       * **간격에도 방향이 있다** (gui 결정9414).
       *
       * 〔실측 2026-09-07 KST, 화면을 그려서 눈으로 읽었다〕 한 줄이 이렇게 떴다:
       * ```
       * ws-h200-1 … NEWER than the reference — updating would move it backwards (**behind by 22 minutes**)
       * ```
       * **앞뒤가 서로를 부정한다.** `behindBy` 를 방향과 무관하게 붙이고 있었다.
       * ⚠ 하필 `ahead` 다 — 이 갈래에서 «뒤처졌다» 로 읽은 사람은 **올리기를 누르고,
       *   그것은 그 노드를 되돌린다.** 판정 문구가 막으려던 바로 그 동작을 간격 문구가 부른다.
       * ▶ `differs` 는 **어느 쪽인지 모른다** — 그래서 방향 없는 말을 쓴다(«벌어져 있다»).
       */
      aheadBy: (age: string) => `ahead by ${age}`,
      /** 방향을 모를 때. «behind» 도 «ahead» 도 쓰면 안 되는 자리다(`differs`). */
      apartBy: (age: string) => `apart by ${age}`,
      /** ⚠ 없는 것은 0 이 아니다. */
      behindUnknown: 'gap not known',
      loadError: 'Could not read the bundle versions',
      /**
       * **우클릭 → 올리기** (지시137 ②, 결정6466).
       *
       * ⚠ 되돌릴 수 없는 **원격 조작**이다. 지시 원문: *«무엇이 일어나는지 먼저 보이고
       * 확인을 받아라 — **그 노드에서 돌던 잡이 끊긴다**는 사실 포함»*.
       * 그 사실은 지어낸 것이 아니다: 계획이 `install-service` 에서 유닛을 다시 쓰고
       * 재기동한다(env 가 값으로 확인해 줬다, axis-api 전달).
       */
      update: {
        menu: 'Update bundle on this node…',
        title: (node: string) => `Update the bundle on ${node}`,
        /** 무엇이 일어나는가 — **먼저 보인다**. */
        facts: [
          'The node downloads the reference bundle and its service is rewritten and restarted.',
          'Anything that node was running is interrupted.',
          'Its .env is not touched — credentials on that machine stay as they are.',
        ],
        /** ⚠ 어디까지 가는지 **이름으로** 말한다 — «최신» 이라고만 하면 재는 순간 낡는다. */
        goesTo: (commit: string) => `It will move to ${commit}.`,
        credentialLabel: 'Platform API key credential',
        /**
         * ★ **더 이상 묻지 않는다** (지시162㉠). 되묻던 자리가 사람을 죽였다 —
         * 목록에서 `ssh_password` 를 고를 수 있었고 노드에서 `curl 401` 이 났다.
         * ⚠ 「안 묻는다」를 **말은 한다.** 아무 설명 없이 칸이 사라지면 사람은
         *   «어디로 갔지» 를 찾으러 간다.
         */
        credentialNotAsked:
          'The bundle key is not asked for — the platform uses the credential designated for pulling node bundles.',
        /** ⚠ **고르게 하지 않되 숨기지도 않는다** — 무엇으로 붙었는지는 결과에 적는다. */
        credentialUsed: (name: string) => `Pulled with: ${name}`,
        confirm: 'Update now',
        running: 'Updating…',
        /**
         * 🔴 **«시작했다» 라고 말하고 있었다 — 이 부름은 이미 끝난 뒤에 돌아온다.**
         *
         * 〔운영자 2026-09-09 09:3x KST〕 *「update now 누르고 나서 성공했으면 성공했다고
         * 다이얼로그 나왔으면 좋겠음」*. 값으로도 그렇다: `POST …/apply` 는 **끝난 결과**를
         * (`status: success|failed|reverted`) 실어 돌려준다 — 실물 `prv_25ce188e` 는 12초 만에
         * `success` 로 끝났다. 그런데 화면은 *"Update started."* 만 말했다.
         * ⚠ «시작했다» 는 **끝을 안 말하는 말**이라, 실패해도 같은 문장이 뜬다.
         */
        succeeded: (node: string, commit: string) => `${node} now runs ${commit}.`,
        /**
         * ⚠ **실패를 «안 끝났다» 로 뭉개지 않는다.** 서버가 준 낱말을 그대로 댄다 —
         * `failed` 와 `reverted` 는 그 기계에 남은 것이 다르다(되돌렸나 아닌가).
         */
        didNotFinish: (status: string) => `The update did not finish — the server reported "${status}".`,
        /**
         * ⚠ **설치가 됐다 ≠ 노드가 살아 있다**(지시42 완료 정의). 유닛이 뜬 것과
         * 하트비트가 올라오는 것은 다른 사실이라 따로 적는다.
         */
        nodeSeenAs: (status: string) => `The control plane now sees this node as ${status}.`,
        failed: 'The update did not finish',
        /** 이미 최신이면 **못 누르게** 한다(지시137 ②). */
        alreadySame: 'This node already runs the reference build — there is nothing to update.',
        /** ★ `ahead` 는 **올리기가 아니라 내리기**다. */
        wouldDowngrade:
          'WARNING: this node runs a NEWER build than the reference. Updating would move it backwards.',
        /** ⚠ 대상이 없으면 **어느 기계가 갈릴지 모른다** — 막는다. */
        noTarget:
          'This node has no unambiguous SSH target, so the console cannot tell which machine would be changed. Register one (or resolve the duplicate) first.',
        /** ⚠ 기준을 모르면 **어디로 가는지도 모른다.** */
        noBaseline: 'There is no reference build to move to.',
        cancel: 'Cancel',
        /** ⚠ 못 읽은 것은 «등록된 것이 없다» 가 아니다(결정3480). */
        /**
         * ⚠ **쓰임이 바뀌었다** (지시162㉠). 종전에는 «고를 목록을 못 읽었다» 였다.
         * 이제 고르지 않으므로, 이 목록은 **결과의 자격 id 를 이름으로 바꾸는 데만** 쓴다.
         * 못 읽어도 «무엇으로 붙었나» 는 id 로 그대로 나온다 — 그 사실을 말한다.
         */
        credentialsUnreadable:
          'Could not load the credential list, so the key below is named by its id.',
      },
    },
    notMeasured: 'Not measured yet',
    notMeasuredHint:
      'This node has not reported its hardware. It is not a claim that the machine has no GPU.',
  },

  sessions: {
    /* 지시184 — 입력 칸의 보이는 이름. */
    promptLabel: 'Prompt',
    heading: 'Sessions',
    loading: 'Loading…',
    loadError: 'Could not load session list',
    /*
     * **빈 것과 못 읽은 것은 다르다** (결정1446). 종전에는 세션이 0 이면 목록 자리가
     * **그냥 비어 있었다** — 붙은 데몬에 세션이 없는 것인지, 조용히 실패한 것인지
     * 화면만 보고 가를 수 없었다.
     */
    empty: 'No sessions are running on the connected daemon.',
    selectPrompt: 'Select a session on the left.',
    summaryLine: (model: string, status: string, tokens: string, cost: string) =>
      `Model ${model} · Status ${status} · Tokens ${tokens} · Cost ${cost}`,

    pendingPermissions: (count: number) => `Pending permission requests (${count})`,
    /*
     * **소유자를 모르는 세션** (지시56 §2-1, engine 결정740 / 결정1450).
     *
     * 서버가 `inspect`·`logs` 를 소유자에게만 준다. 플랫폼 밖에서 뜬 세션은 소유자를
     * 몰라 409 로 막히는데, **콘솔에는 그 사실을 말할 자리도 밝힐 길도 없었다** —
     * 고르면 오른쪽이 **그냥 비었다.** 안전장치가 기능을 막는 것으로만 남아 있었다.
     *
     * ⚠ 문구가 *"권한이 없다"* 가 되면 안 된다. 남의 것이라는 판정이 아니라
     * **아직 아무에게도 안 묶였다**는 뜻이다 — 사람이 할 일이 다르다.
     */
    ownerUnknown: 'This session was not started by the platform, so nobody owns it yet. Prompts and reasoning stay hidden until someone claims it.',
    claim: 'This session is mine',
    /*
     * ⚠ `claimed` 를 **지웠다** (결정2421).
     *
     * 밝히기 단추는 `session_owner_unknown` 갈래 **안에만** 있다. 성공하면 inspect 가
     * 다시 돌아 그 갈래가 통째로 사라지고 내용이 열린다 — 그 자리에 "Claimed." 를
     * 그리면 **뜨자마자 언마운트되어 깜빡이고 만다.**
     *
     * 성공의 신호는 **막힌 화면이 열리는 것** 자체다. 선언만 해 두고 안 쓰면
     * `strings.tsx` 를 읽는 사람이 **있지도 않은 문구를 있다고 읽는다.**
     */
    inspectError: 'Could not open this session',
    promptPlaceholder: 'Prompt to send to the session',
    send: 'Send',
    stop: 'Stop',
    viewLogs: 'View logs',
  },

  resources: {
    /**
     * **설계 저장소** (지시65 §15, 결정3460).
     *
     * ⚠ 지우기는 **폐기(archive)** 다 — 서버가 `archived_at` 을 세울 뿐 행을 안 지운다.
     * 그리고 **같은 내용을 다시 등록하면 되살아난다**(결정387). 그러니 문구가
     * *"영영 사라진다"* 라고 말하면 **거짓말**이다. 있는 그대로 적는다.
     */
    /**
     * **프로비저닝 대상 — 사람이 치울 수 있게 한다** (결정720, gui 결정6464).
     *
     * 설계 저장소가 겪은 것과 **같은 부류**다(결정3460): 서버는 지울 수 있는데
     * 사람은 못 지웠다. 그래서 등록한 대상이 **영영 쌓였다** — 목록·중복 판정에 계속 낀다.
     *
     * ⚠ **폐기이지 «영영 사라짐» 이 아니다.** 계약이 못박았다 —
     * *«목록·중복 판정·설치에서 빠진다. **실행 기록은 남는다**(안전장치4)»*.
     * «영영 사라진다» 라고 적으면 거짓말이고, 사람은 되돌릴 수 있는 일 앞에서 겁낸다.
     */
    /**
     * **LLM 공급자 판** (03 §13.3, gui 결정6469).
     *
     * 결정6431 ④ 가 «Resources 에 공급자 판 자체가 없다» 로 남겨 둔 자리다. 그 하나가
     * 없어서 **여섯 라우트가 화면에 안 닿았다** — 등록·제거·자격증명·노드 선언, 그리고
     * 모델 카탈로그 둘. 「판 하나가 없으면 그 판이 쓸 라우트가 전부 죽는다」.
     */
    /**
     * **모델 카탈로그 판** (03 §13.3, gui 결정6470).
     *
     * 공급자 판(결정6469)이 서면서 마지막 두 라우트가 남았다 — 등록·수정과 제거.
     *
     * ⚠ **단가 0 은 «말하고» 쓴다**(결정194). 온프렘은 토큰 단가가 0 이라
     * `runs.cost_usd` 합이 영원히 0 이고 **일일 상한이 그 경로를 한 번도 못 본다.**
     * 금지가 아니라 **침묵을 막는 것**이라 `unmeteredAck` 를 함께 켜게 한다.
     */
    models: {
      /* 지시184 — 칸마다 보이는 이름. */
      fieldModel: 'Model id',
      fieldDisplay: 'Shown as',
      fieldInput: 'Input price per Mtok',
      fieldOutput: 'Output price per Mtok',
      title: 'Model catalog',
      intro:
        'Models this platform knows about, per provider. Prices are what the daily cost cap counts — a model with no price is invisible to it.',
      loadError: 'Could not load the model catalog',
      empty: 'No models in the catalog yet.',
      columns: { model: 'Model', provider: 'Provider', display: 'Shown as', input: 'Input /Mtok', output: 'Output /Mtok', state: 'Pricing' },
      /** ⚠ 서버 판정을 그대로 읽는다 — 화면이 다시 셈하지 않는다(결정441). */
      pricingKnown: 'confirmed',
      pricingUnknown: 'not stated',
      remove: 'Remove',
      confirmRemove: (model: string) => `Remove ${model} from the catalog? Agent definitions that use it will stop working.`,
      removeFailed: 'Could not remove that model',
      /** ⚠ «실패» 가 아니라 «지금은 안 된다» — 쓰는 정의를 먼저 옮겨야 한다. */
      removeInUse: 'An agent definition still uses that model, so it cannot be removed yet.',
      /* 등록·수정 폼 */
      addHeading: 'Add or update a model',
      providerLabel: 'Provider',
      modelPlaceholder: 'Model id (e.g. claude-sonnet-4)',
      displayPlaceholder: 'Shown as',
      inputPlaceholder: 'Input price per Mtok',
      outputPlaceholder: 'Output price per Mtok',
      /**
       * ★ **0 을 쓰려면 말해야 한다** (결정194).
       * 온프렘 모델은 단가가 0 이라 상한이 그 경로를 못 본다 — 기본값에 기대어
       * **조용히** 상한 없는 경로가 서는 것만 막는다.
       */
      unmeteredAck: 'I accept that a price of 0 makes this model invisible to the daily cost cap',
      unmeteredRequired:
        'A price of 0 means runs with this model are never billed, so the daily cost cap cannot see them. Tick the box above to say that is deliberate.',
      save: 'Save',
      saveFailed: 'Could not save that model',
      needProvider: 'Register a provider first — a model belongs to one.',
    },
    providers: {
      /* 지시184 — 칸마다 보이는 이름. */
      fieldName: 'Name',
      fieldBaseUrl: 'Base URL',
      title: 'LLM providers',
      intro:
        'Model providers this platform can call. Removing one is refused while an agent definition still uses it.',
      loadError: 'Could not load the provider list',
      empty: 'No providers registered yet.',
      columns: { name: 'Name', kind: 'Kind', models: 'Models', node: 'Runs on', credential: 'Credential' },
      /** ⚠ **모르는 것과 없는 것을 가른다** — 서버가 노드를 못 풀었을 때가 있다. */
      nodeUnresolved: 'not resolved',
      nodeNone: '—',
      hasCredential: 'attached',
      noCredential: 'none',

      /** ⚠ **모델 수를 못 세었을 수 있다** — 0 과 다르다. */
      modelsUnknown: 'not counted',
      remove: 'Remove',
      confirmRemove: (name: string) => `Remove ${name}? Agent definitions that use it will stop working.`,
      removeFailed: 'Could not remove that provider',
      /** ⚠ «실패» 가 아니라 «지금은 안 된다» — 쓰는 정의를 먼저 옮겨야 한다. */
      removeInUse: 'An agent definition still uses that provider, so it cannot be removed yet.',
      attach: 'Attach credential',
      /**
       * **고르는 자리에는 «고르라» 고 적는다** (gui 결정9426).
       *
       * 〔실측 2026-09-07 KST, `Resources` 를 그려서 읽었다〕 한 줄이 이렇게 나왔다:
       * ```
       * … attached **Attach credential** **Attach credential** Detach Remove
       * ```
       * 빈 `<option>` 과 제출 `<button>` 이 **같은 문구**(`attach`)를 쓰고 있었다.
       * ```
       * 눈으로   말을 더듬는다 — 같은 낱말이 잇달아 두 번
       * 낭독기   `select` 의 이름이 «Attach credential <공급자>», 단추가 «Attach credential» —
       *          **고르는 것과 하는 것을 이름으로 못 가른다**
       * ```
       * ▶ 빈 칸은 **동작**이 아니라 **무엇을 고를지**를 말해야 한다.
       * ⚠ 단추 쪽(`attach`)은 **안 바꿨다** — 그 낱말은 동작 이름으로 옳다.
       */
      attachPick: 'Choose a credential…',
      /**
       * **고르는 칸과 그 단추가 «접근 이름» 에서도 갈려야 한다** (gui 결정9428).
       *
       * 결정9426 은 **보이는 글자**의 겹침을 고쳤다. 그런데 줄마다 임자를 붙이면서
       * `aria-label` 을 «동작 + 임자» 로 통일하자 **같은 겹침이 aria 에서 되살아났다**
       * 〔실측: `2×«Set node On-prem 4090»` — 입력칸과 단추가 같은 이름〕.
       * ▶ 고르는 칸은 **«무엇을 적/고르나»**, 단추는 **«무엇을 하나»** 다. 임자는 둘 다 붙인다.
       */
      setNodeField: (provider: string) => `Node id for ${provider}`,
      attachField: (provider: string) => `Choose a credential for ${provider}`,
      detach: 'Detach',
      attachFailed: 'Could not change the credential',
      /**
       * ⚠ **기계 낱말을 사람 앞에 그대로 두지 않는다** (결정5433).
       * 이름표는 사람 말로, 기계 이름은 괄호에 — 그래야 사람이 읽고도 서버와 말이 통한다.
       */
      kindLabels: {
        anthropic: 'Anthropic (anthropic)',
        openai_compatible: 'OpenAI-compatible endpoint (openai_compatible)',
        google: 'Google (google)',
      } as Record<string, string>,
      nodePlaceholder: 'Node it runs on',
      setNode: 'Set node',
      setNodeFailed: 'Could not set the node',
      /* 등록 폼 — 계약 필수는 이름·종류 둘뿐이다. */
      addHeading: 'Register a provider',
      namePlaceholder: 'Name',
      kindLabel: 'Kind',
      baseUrlPlaceholder: 'Base URL (optional)',
      add: 'Register',
      addFailed: 'Could not register that provider',
      /** ⚠ 같은 이름·주소가 이미 있을 때. 「실패」가 아니라 「이미 있다」다. */
      addConflict: 'A provider with that identity already exists.',
      /**
       * **그 서버에 실제로 무엇이 올라와 있나** (지시110, gui 결정9437).
       *
       * ## 왜 — 계약이 답을 갖고 있는데 **아무 화면도 안 읽는다**
       *
       * `GET /llm-providers/{id}/server-models` 가 `drift` 를 셋으로 갈라 준다
       * (`onlyOnServer` · `onlyInCatalog` · `both`). 그중 **`onlyOnServer` 를 읽는 콘솔 코드가
       * 0곳**이었다〔실측 2026-09-07 KST, 전수〕.
       * ★ 지금 지시110 의 기계가 **정확히 그 상태**다: `s-ai-pro6000-43` 의 `/api/tags` 에
       *   `qwen3.5:122b-a10b-q4_K_M`(81.37GB)이 **올라와 있고**, `llm_models` 행은 **0** 이다
       *   〔env 실측, 대장 110 줄〕. 곧 «받았는데 등록 안 됨» 을 **화면이 한 마디도 안 한다.**
       *
       * ## ⚠ 왜 여기(공급자 줄)인가 — 기존 자리는 **이 갈래에 안 닿는다**
       *
       * `AgentDefsPanel` 이 같은 라우트를 부르지만 **모델을 고른 뒤에만** 부른다.
       * 모델이 0개인 공급자에서는 **아예 안 돈다** — 곧 110 의 그 화면에서는 못 쓴다.
       * ▶ 그래서 «펼 때만 묻는» 손(`ContainersOnTarget` 의 그것)을 공급자 줄에 붙인다:
       *   목록이 열릴 때마다 공급자 여럿을 두드리지 않는다.
       *
       * ## ⚠ 판정은 **서버 것**이다
       *
       * 화면이 카탈로그와 서버 목록을 **견주지 않는다** — `drift` 를 그대로 그린다.
       * 결정423 이 막은 자리이고, 지시110 줄이 그 금지를 «남은 일» 처럼 적어 둔 것을
       * 오늘 값으로 바로잡았다.
       */
      serverModels: 'Show models on that server',
      serverModelsLoading: 'Asking that server…',
      /** ⚠ «없다» 가 아니라 «그 서버가 하나도 안 내놨다» 다. 못 물어본 것과 다르다. */
      serverModelsEmpty: 'That server reported no models.',
      /**
       * ★ **지시110 이 물은 그 상태.** «등록만 하면 쓸 수 있다» 를 사람이 알아야 한다 —
       * 이 줄이 없으면 «기계엔 있는데 왜 안 보이나» 로 남는다.
       * ⚠ 경고가 아니다. 그리고 **우리가 대신 등록하지 않는다**(그건 llm 대역이다).
       */
      serverOnly: (models: readonly string[]) =>
        `On that server but not in the catalog: ${models.join(', ')}`,
      /** 반대쪽 — 카탈로그엔 있는데 그 서버엔 없다. 이미 다른 자리가 말하지만 여기서도 한 줄. */
      catalogOnly: (models: readonly string[]) =>
        `In the catalog but not on that server: ${models.join(', ')}`,
      /** 둘 다 있는 것 — «맞다» 를 말해야 위 두 줄이 «다르다» 로 읽힌다(분모다). */
      serverBoth: (n: number) => `${n} models match the catalog.`,
      /** ⚠ 못 물어본 것은 **없는 것과 다르다** — 서버가 까닭을 준다(`failure`·`detail`). */
      serverModelsUnreachable: (why: string) => `Could not ask that server: ${why}`,
    },
    targets: {
      title: 'SSH targets',
      intro:
        'Machines registered for provisioning. Removing one takes it out of the list, the duplicate check, and future installs — the run history it already produced stays.',
      loadError: 'Could not load the target list',
      /**
       * ⚠ **빈 목록이 막다른 길이면 안 된다** (gui 결정8436).
       *
       * 종전은 `'No targets registered yet.'` 로 끝났다. 그런데 **여기서는 등록을 못 한다** —
       * 등록 창(`AddComputeNodeDialog`)은 **파이프라인 편집기의 팔레트**에서 열린다〔실측:
       * `NodePalette` 의 `Compute nodes` 절 머리 `+`〕. 곧 이 화면만 본 사람은
       * **목록이 비었는데 무엇을 할지 모른다.**
       *
       * ★ 같은 파일에 이미 옳은 형제가 있다: 설계 판의 빈 문구가
       * *«…Right-click a node or a machine box on the canvas to save one.»* 로 **자리를 말한다.**
       * 그 잣대를 여기에도 쓴다 — 내가 tailnet 키에 쓴 것과도 같은 잣대다(결정8412:
       * «키가 없으면 무엇을 등록해야 하는지 말한다»).
       *
       * ⚠ 자리 이름을 지어내지 마라. 그 길이 바뀌면 이 문장이 **거짓말이 된다** —
       *   `emptyHint` 를 지우거나 고칠 때 그 자리를 다시 재라.
       */
      empty: 'No targets registered yet.',
      emptyHint:
        'Machines are registered from the pipeline builder: open the palette and use + on the Compute nodes section.',
      columns: { host: 'Host', user: 'User', arch: 'Architecture', allowed: 'Allowed', created: 'Registered' },
      /** ⚠ «안 쟀다» 를 «x86_64» 로 접지 않는다 — 같은 창의 다른 자리가 이미 그 잣대를 쓴다. */
      archUnknown: 'not measured',
      allowedYes: 'yes',
      allowedNo: 'no',
      remove: 'Remove',
      /** 되돌릴 수 있어도 **묻는다** — 그리고 무엇이 남는지 말한다. */
      confirmRemove: (host: string) =>
        `Remove ${host} from the target list? Future installs will not offer it. The runs it already produced stay in the history.`,
      removeFailed: 'Could not remove that target',
      /** ⚠ **«실패» 가 아니라 «지금은 안 된다»** 다 — 사람이 할 일이 다르다. */
      removeBusy:
        'That target has a provisioning run in progress, so it cannot be removed yet. Wait for the run to finish and try again.',
      /** ⚠ 501 은 서버를 고칠 일이지 다시 눌러 볼 일이 아니다. */
      removeNotWired: 'This server does not have target removal wired.',
      /**
       * **그 기계에서 우리가 올려 둔 컨테이너** (지시59, 결정6472).
       *
       * ⚠ 계약이 못박았다 — *«남의 컨테이너는 이 응답에 들어가지 않는다»*.
       * 그래서 **빈 목록이 «그 기계가 논다» 가 아니다.** 그 사실을 화면이 말해야
       * 사람이 «아무것도 안 돈다» 로 안 읽는다.
       */
      /**
       * **tailnet 에 넣기** (지시70 ②③④⑤ · 지시141 ㉡, gui 결정8412).
       *
       * ⚠ ㉡ 은 «임시·우회» 가 아니라 `oauth_client` 와 **대등한 1급 경로**다(운영자 명시).
       *   그래서 문구 어디에도 그 뜻이 배지 않게 한다 — 그냥 «가입시킨다» 라고만 말한다.
       */
      join: 'Join our tailnet…',
      joinTitle: (host: string) => `Join ${host} to our tailnet`,
      /** ★ **무엇이 없어서 못 하는지** 말한다 — 빈 드롭다운만 두면 사람이 그것을 모른다. */
      joinNeedsKey:
        'No usable tailnet auth key is registered. Register one under Credentials first — kind “tailscale_auth_key”.',
      /**
       * ★★ **못 읽은 것은 없는 것이 아니다.**
       *
       * 목록을 못 읽었는데 «키를 등록하라» 고 말하면 **거짓**이다 — 이미 등록해 둔 사람이
       * 똑같은 키를 하나 더 만들게 된다. 내 자(결정3480)가 이 자리를 바로 물었다.
       */
      joinKeysUnreadable: 'Could not read the credential list, so we do not know which keys are registered.',
      joinKeyLabel: 'Tailnet auth key',
      joinHostnameLabel: 'Name on the tailnet (optional)',
      /*
       * ⚠ **목에 있는 이름을 자리표로 쓰지 마라** (integrate 실측 → gui 결정8415).
       * 종전에 `lab-4090-1` 을 썼는데 그것이 **목 픽스처의 식별자**와 같은 글자라,
       * 「목에만 있어야 할 값이 운영 번들에 있다」 걸쇠(`check-console-bundle`)가 물었다.
       * 걸쇠 입장에서는 **자리표인지 샌 값인지 구분할 수 없다** — 글자가 같으니까.
       * ▶ 자리표는 값이 있으니 **지우지 않고 글자만** 바꾼다.
       */
      joinHostnamePlaceholder: 'e.g. my-workstation',
      /** ⚠ 계약의 pattern 을 **누르기 전에** 건다 — 눌러 보고 400 을 받는 것과 다르다. */
      joinHostnameBad:
        'Letters, digits and hyphens only, starting with a letter or digit, up to 63 characters.',
      joinSudoLabel: 'Sudo password credential (optional — only used for a system-wide install)',
      /**
       * ★★ 계약이 `acknowledgeInstall: true` 를 **필수**로 뒀다 — 곧 «기본값으로 조용히
       * 참이 되는 것» 이 아니라 **사람이 읽고 켠 것**이어야 한다는 뜻이다.
       * 그래서 이 확인란은 단추와 **다른 말**을 한다: 무엇에 동의하는지 적는다.
       */
      joinAcknowledge: 'I understand this installs tailscale on that machine and keeps it running there.',
      joinStart: 'Join the tailnet',
      joinRunning: 'Joining…',
      joinDone: 'That machine joined the tailnet.',
      /** ★ 409 는 «실패» 가 아니라 «지금은 안 된다» — 다시 눌러도 같다. */
      /* ── 그 기계의 `.env` 키 이름들 (결정8441) ── */
      envKeys: 'Show .env keys',
      envKeysHide: 'Hide .env keys',
      envKeysLoading: 'Reading the keys on that machine…',
      /** ★ **이름뿐이라는 것을 말한다** — 안 적으면 「값이 가려졌나」로 읽힌다. */
      envKeysNote: (path: string) => `Key names in ${path} on that machine. Values are never read.`,
      /** ⚠ **「키 0개」가 아니다** — 파일 자체가 없다. 사람이 할 일이 다르다. */
      envKeysNoFile: (path: string) => `There is no ${path} on that machine yet.`,
      envKeysEmpty: (path: string) => `${path} exists on that machine but has no keys in it.`,
      /** ⚠ 못 읽은 것은 **「없다」가 아니다**. */
      envKeysUnreachable: 'Could not reach that machine, so we do not know which keys it has',
      envKeysNotWired: 'This server cannot read keys from a machine yet',
      envKeysFailed: 'Could not read the keys on that machine',

      /* ── tailnet 판정 (지시70 ①, 결정8429) ── */
      /** ⚠ 「못 읽었다」와 「없다」를 안 뭉친다 — 앞엣것은 연결 탓이고 뒤엣것은 기계 상태다. */
      stateChecking: 'Checking whether this machine is already on the tailnet…',
      stateUnreadable: 'Could not read the tailnet state of this machine',
      /** ★ 이미 붙어 있으면 **넣을 것이 없다** — 창이 그것을 먼저 말한다. */
      stateReady: (address: string | undefined) =>
        address === undefined
          ? 'This machine is already on the tailnet.'
          : `This machine is already on the tailnet at ${address}.`,
      /**
       * ⚠ 네 갈래가 **사람이 할 일이 다르다** — 색이 아니라 이 문장이 그것을 말한다.
       *   `absent` 만 «깐다» 이고, 나머지 둘은 이미 깔린 것을 **깨우거나 붙이는** 일이다.
       */
      statePresence: {
        joined: 'Tailscale is installed and this machine is on the tailnet.',
        needs_login: 'Tailscale is installed but this machine has not signed in to a tailnet yet.',
        installed_not_running: 'Tailscale is installed but not running on this machine.',
        absent: 'Tailscale is not installed on this machine.',
      } as Record<string, string>,
      /** ⚠ 서버가 «못 넣는다» 고 판정한 경우 — 눌러 볼 일이 아니다. */
      stateNotInstallable: 'This machine cannot be added to the tailnet.',
      /** ★ 판정은 서버가, 문구는 화면이 — 그런데 **다음 할 일은 서버 말을 그대로** 싣는다. */
      stateNextStep: (nextStep: string) => nextStep,
      stateNeedsAdmin: 'Adding it needs an administrator password on that machine.',
      joinRefused: 'That machine cannot be joined right now',
      joinUnreachable: 'Could not reach that machine, so nothing was changed there.',
      joinNotWired: 'This server does not have tailnet joining wired.',
      joinFailed: 'Could not join that machine to the tailnet',
      containers: 'Show what we run there',
      containersHide: 'Hide',
      containersLoading: 'Asking that machine…',
      /** ⚠ «없다» 가 아니라 «우리 것이 없다» 다. */
      containersEmpty:
        'Nothing that this platform started is running there. Containers started by other people are not listed here.',
      containersNote: 'Only containers this platform started are listed.',
      containersColumns: { name: 'Name', image: 'Image', state: 'State', status: 'Status' },
      /**
       * `state` 를 사람 말로 (gui 결정6474).
       *
       * ⚠ 계약이 이 칸을 **열린 문자열**로 둬서 목 덮기 자(결정472)가 **이 칸을 아예 못 본다** —
       * 서버가 낼 수 있는 상태를 셀 방법이 없다. 그래서 화면이 아는 것만 옮기고,
       * **모르는 것은 지어내지 않고 온 말 그대로** 보여 준다(`containerStateUnknown`).
       */
      containerState: {
        running: 'Running',
        exited: 'Stopped',
        created: 'Created, never started',
        paused: 'Paused',
        restarting: 'Restarting',
        removing: 'Being removed',
        dead: 'Dead — Docker could not remove it',
      },
      /** ★ 모르는 상태는 **온 말 그대로** 보인다 — 우리 어휘로 뭉개면 사람이 원문을 못 본다. */
      containerStateUnknown: (raw: string) => `${raw} (we do not have wording for this state)`,
      /** ⚠ 502 는 «못 물어봤다» 다 — 계약이 «빈 목록으로 돌려주지 않는다» 고 못박았다. */
      containersUnreachable:
        'Could not reach that machine, so we do not know what is running there. This is not the same as nothing running.',
      /** ★ **재시도가 아니라 사람이 지문을 대조해야 한다**(안전장치2). */
      containersFingerprint:
        'The host key does not match what was registered. Do not retry — compare the fingerprint first; something else may be answering at that address.',
      containersNotWired: 'This server does not have remote container listing wired.',
      containersFailed: 'Could not list containers',
    },
    designs: {
      title: 'Saved designs',
      intro:
        'Designs you saved from the canvas. They show up in the palette as presets. Removing one takes it out of the palette — it is not erased, and saving the same contents again brings it back.',
      empty: 'No designs saved yet. Right-click a node or a machine box on the canvas to save one.',
      loadError: 'Could not load saved designs',
      columns: {
        name: 'Name',
        scope: 'Kind',
        contents: 'Contents',
        created: 'Saved',
      },
      /** ⑤ 는 N=1 인 ⑥ 이다 — 사람 말로는 «꼭짓점 하나» 와 «상자 하나» 다. */
      scopeNode: 'One module',
      scopeModule: 'A machine box',
      contents: (modules: number, edges: number) =>
        `${modules} module${modules === 1 ? '' : 's'}, ${edges} connection${edges === 1 ? '' : 's'}`,
      remove: 'Remove',
      /** 되돌릴 수 있다는 것을 **확인 문구 안에서** 말한다 — 눌러 놓고 겁내지 않게. */
      confirmRemove: (name: string) =>
        `Remove “${name}” from the palette? It is not erased — saving the same contents again brings it back.`,
      removeFailed: 'Could not remove that design',
    },
    heading: 'Resources',
    intro:
      'Register the agent definitions, datasources and credentials that pipeline nodes point at.',
    created: 'Registered.',
    submit: 'Register',
    /**
     * ⚠ **「아직 안 왔다」와 「없다」는 다르다** (gui 결정8449).
     *
     * 네 판(자격·자료원·정의·도구)이 `(data ?? []).length === 0` 으로 세고 있어서
     * **읽는 중에도 「하나도 없다」고 말했다**〔실측: 응답을 200ms 늦추니 그 줄이 떴다〕.
     * 그러면 사람은 **이미 등록해 둔 것을 하나 더 만든다** — 내가 tailnet 키 창에서
     * 막아 둔 바로 그 해(결정8412)이고, `LoadVersionDialog` 이 이미 값을 치른 자리다
     * (*"열자마자 그렇게 읽고 닫으면 있는 버전을 못 본다"*).
     */
    loading: 'Loading…',
    none: 'Nothing registered yet.',
    notWired: 'Not wired on the server.',
    /**
     * **서비스 키** (지시228) — ztro 로 만든 서비스가 ztro 의 web API 를 부를 때 쓰는 id/key 쌍.
     * 자격증명(ztro 가 밖을 부를 때 쓰는 것)과 방향이 반대라 같은 화면에 나란히 둔다.
     */
    serviceKeys: {
      title: 'Service keys',
      note: 'Keys that services built on ZTRO use to call its web APIs (the Open API modules). A service sends the key together with your user id; a call without the matching pair is rejected. These keys cannot open the console or any other API. Keys ZTRO itself uses to reach other systems are the credentials above.',
      howToUse: (userId: string) => `Set these two values in the service: ztro.userId=${userId} and ztro.apiKey=<the key>. It sends them as the headers X-Ztro-User-Id and Authorization: Bearer.`,
      fieldName: 'Key name',
      namePlaceholder: 'Which service uses it (e.g. carewell-android)',
      issue: 'Issue key',
      issuedTitle: 'Copy the key now — it is shown only once.',
      issuedUserId: 'User id',
      issuedKey: 'API key',
      gotItClose: 'Got it, close',
      loadError: 'Could not load the service keys',
      loading: 'Reading the service keys…',
      none: 'No service keys yet. Issue one for each service that calls a web API.',
      columns: { name: 'Name', userId: 'User id', key: 'Key (masked)', lastUsed: 'Last used', status: 'Status', actions: 'Actions' },
      neverUsed: 'Never',
      active: 'Active',
      /* 만료를 둔 키는 그 날짜를 말한다 — 지난 키가 «Active» 로 보이면 401 의 까닭을 못 찾는다. */
      activeUntil: (when: string) => `Active until ${when}`,
      expired: (when: string) => `Expired (${when})`,
      revoked: (when: string) => `Revoked (${when})`,
      revoke: 'Revoke',
      revokeConfirm: (name: string) => `Revoke ${name}? Calls using it fail at once.`,
      revokeYes: 'Yes, revoke',
      revokeNo: 'Keep it',
      createFailed: 'Could not issue the key',
      revokeFailed: 'Could not revoke the key — it is still active',
    },
    credentials: {
      /**
       * **자격이 안 받아들여졌다 — 무엇이 어떻게 안 된 것인가** (지시141 보완, gui 결정8406).
       *
       * ## ⚠ 넷을 한 문장으로 뭉치지 않는다
       *
       * 지시 예시문은 「자격이 만료되었습니다. 새로 발급해 등록해 주십시오」인데,
       * §2-1(d) 가 가른 넷 중 **둘은 만료가 아니다**:
       * ```
       * 회수됨       **우리 장부가 안다** → 안 불러도 아는 갈래다
       * 거절됨(401)  자격이 죽었다        → 「새로 발급하라」가 맞다
       * 권한 모자람  자격은 **살아 있다** → 새로 발급해도 그대로다. 권한을 넓혀야 한다
       * 못 물어봤다  네트워크             → 자격이 **멀쩡할 수 있다**
       * 상대 오류    상대 API 가 실패     → 역시 자격을 **모른다**
       * ```
       * 뒤 셋에 「만료됐으니 새로 발급하라」고 쓰면 **멀쩡한 키를 버리고 다시 만들게 하고**,
       * 그래도 안 되니 사람이 두 번 헤맨다. 「못 읽은 것은 없는 것이 아니다」와 같은 자리다.
       *
       * ## ⚠ 두 경로를 **대등하게** 쓴다
       *
       * 이 문구는 OAuth client 로 발급한 자격에도, 운영자가 등록한 auth key 에도 **같이** 쓴다.
       * 어느 낱말도 한쪽을 «임시·우회» 로 읽히게 하지 않는다 — 리드가 못박은 자리다.
       * OAuth client secret 도 회수된다. 한쪽만 만료를 말하면 다른 쪽이 조용히 죽는다.
       */
      expiry: {
        /** ★ 불러 보지 않고도 아는 갈래다 — 만료와 폐기를 core 가 한 낱말로 묶었다. */
        expired_or_revoked: 'That credential has been revoked here, so it will not be used again. Issue a new one.',
        unauthorized: 'That credential was rejected. It has expired or been revoked, so a new one has to be issued.',
        /* ★ 살아 있는 자격이다 — 다시 발급하면 **같은 자리로 돌아온다**. */
        insufficient_scope:
          'That credential is still valid, but it does not carry the permissions this needs. Issuing a new one the same way will not help — widen the permissions instead.',
        /* ★★ 여기서 «만료» 라고 말하면 멀쩡한 키를 버리게 만든다. */
        /** ★ **새 갈래.** 「가리키는 자격이 아예 없다」는 폐기와 **다른 사실**이다. */
        not_found: 'The credential this points at no longer exists here. Pick another one, or register it again.',
        upstream:
          'The service did not give us an answer, so we do not know whether that credential is still good. This is not the same as it being expired.',
        /**
         * ★ `cause` 가 오면 **덧붙인다** — `timeout`·`server_error`·`bad_response` 는
         * 사람이 할 일이 다르다(기다린다 / 상대를 본다 / 우리 쪽을 본다).
         * ⚠ 모르는 `cause` 도 **그대로 보인다** — 뭉개면 접혔던 그 자리로 돌아간다.
         */
        upstreamCause: (cause: string) => `The service reported “${cause}”.`,
        /** ★ **어떤 자격인지** — 이름만으로는 종류가 갈리지 않는다(tailscale 둘이 그렇다). */
        which: (name: string, kind: string) => `Credential: ${name} (${kind})`,
        /**
         * ★★ **어디서 갱신하나 — 서버가 그 자리를 줄 때만 적는다.**
         *
         * `renewAt` 의 **있고 없음 자체가 신호**다(api 결정): 있으면 「사람이 콘솔에서 할 일이
         * 있다」, 없으면 「자격을 건드릴 일이 아니다」. 없는데 링크를 지어 넣으면
         * **멀쩡한 키를 버리게 하는 갈래에 갱신 단추가 붙는다.**
         */
        where: (at: string) => `Renew it in ${at}.`,
        /** ⚠ 서버가 우리가 모르는 낱말을 보내면 **지어내지 않는다** — 온 말을 그대로 보인다. */
        unknown: (raw: string) => `The service reported “${raw}”, which we do not have wording for.`,
      },
      /*
       * ★ **슬랙 설치를 콘솔에서 시작한다** (지시87 계열, 결정6460).
       *
       * 이 판은 `slack_bot_token` 을 «OAuth 로 만들어진다 — 손으로 붙여 넣는 것이 아니다» 라고
       * **알고만 있었다.** 그런데 그 흐름을 **시작할 길이 없었다** — 그래서 지시87 은 리드가
       * 손으로 깔아 닫혔고, 다음 워크스페이스에서 같은 손이 또 필요했다.
       *
       * ⚠ 단추가 **설치하지 않는다.** 슬랙으로 가는 문을 열 뿐이고, 승인은 사람이 거기서 한다.
       *   그래서 «Install» 이 아니라 «Start…» 이고, 다음에 무슨 일이 일어나는지 적는다.
       */
      slackStart: 'Start Slack app installation…',
      slackStarting: 'Asking the server for an install link…',
      slackStartHint:
        'This does not install anything. It asks the server for a one-time install link; you approve the app in Slack, and Slack sends you back here.',
      slackOpen: 'Open the Slack install page',
      /** ⚠ 501 은 «실패» 가 아니라 «안 배선됨» 이다 — 사람이 할 일이 다르다. */
      slackNotWired:
        'This server does not have the Slack install route wired, so the console cannot start the flow here.',
      title: 'Credentials',
      note: 'The secret is sent once and never returned — only the last 4 characters are shown.',
      namePlaceholder: 'e.g. lab-4090-postgres',
      hostPlaceholder: 'e.g. 100.64.0.12',
      usernamePlaceholder: 'e.g. platform',
      secretPlaceholder: 'paste the value — it is never shown again',
      kindLabel: 'Credential kind',
      /*
       * ★★★ **칸마다 앞에 이름을 붙인다** (지시176, 운영자 실물 2026-09-09).
       *
       * 종전에는 `placeholder` 뿐이었다 — **값을 넣는 순간 그 칸이 무엇이었는지 사라진다.**
       * 운영자 화면에 여섯 칸이 이름 없이 늘어서 있었고, 스크린리더는 `aria-label` 이
       * 붙은 둘만 읽었다. `placeholder` 는 **보기**이지 이름이 아니다 — 둘 다 둔다.
       */
      fieldName: 'Name',
      fieldHost: 'Host',
      fieldUsername: 'Username',
      fieldSecret: 'Secret',
      /** ⚠ 무엇을 넣는 칸인지 종류마다 다르다 — 종류를 고르면 이 곁말이 바뀐다. */
      hostMeaning:
        'The machine or endpoint this credential is used against — for an OAuth client it is the token endpoint, not a host.',
      /*
       * ★ **사람이 읽을 이름** (결정5413). 종전엔 드롭다운이 `gcp_service_account`
       * 처럼 **기계 이름을 그대로** 보여 줬다 — 글로 읽어 보고 알았다.
       *
       * ⚠ **괄호 안에 기계 이름을 남긴다.** 운영자는 에이전트 선언의 `secretKinds`
       * 에서 그 낱말을 그대로 보게 되고, 화면이 그것을 감추면 **둘을 잇지 못한다.**
       *
       * ⚠ `Partial` 이다 — **일부러다.** 빠짐없이 요구하면 계약에 종류가 늘 때
       * 컴파일이 깨져 «화면을 안 고쳐도 그 상자가 제 칸을 갖는다»(지시107)를 어긴다.
       * 이름이 없으면 기계 이름 그대로 뜬다(고침 전과 같다 — 나빠지지 않는다).
       */
      kindNames: {
        git_pat: 'Git access token (git_pat)',
        git_ssh_key: 'Git SSH key (git_ssh_key)',
        ssh_key: 'SSH key (ssh_key)',
        ssh_password: 'SSH password (ssh_password)',
        llm_api_key: 'LLM API key (llm_api_key)',
        db_password: 'Database password (db_password)',
        /*
         * ⚠ **넷이 나란히 서면 사람이 헷갈린다**(결정8408·8409).
         * 특히 tailscale 둘은 **이름이 한 낱말 차이고 비밀 접두사까지 `tskey-` 로 같다** —
         * 그것이 종류를 나눈 까닭이라, 이름이 아니라 **하는 일**을 적어야 갈린다:
         * ```
         * tailscale_api_key   기기 목록을 **읽기만** 한다 (tskey-api-)
         * tailscale_auth_key  기기를 tailnet 에 **넣는다** (tskey-auth-)
         * oauth_client        **단명 토큰**을 받아 온다
         * ```
         * ⚠ `tailscale_auth_key` 를 «임시·우회» 로 읽히게 적지 마라 — `oauth_client` 와
         *   **대등한 1급 경로**다(운영자가 못박았다).
         */
        tailscale_api_key: 'Tailscale API key — reads the device list only (tailscale_api_key)',
        tailscale_auth_key: 'Tailscale auth key — joins a machine to the tailnet (tailscale_auth_key)',
        oauth_client: 'OAuth client — exchanges for short-lived tokens (oauth_client)',
        gcp_service_account: 'GCP service account JSON (gcp_service_account)',
        slack_bot_token: 'Slack bot token (slack_bot_token)',
      } satisfies Partial<Record<CredentialKind, string>>,
      hostRequired: 'This credential kind connects somewhere, so a host is required.',
      /**
       * **여러 줄인 비밀은 한 줄 칸에 안 들어간다** (2026-09-09 실물).
       *
       * 🔴 비밀 칸이 `<input type="password">` — **한 줄짜리**였다. 그래서 PEM 을 붙여 넣으면
       *   줄바꿈이 사라지고, 서버가 `Cannot parse privateKey: Unsupported key format` 을 낸다.
       *   곧 **`ssh_key` 는 콘솔로 등록이 아예 불가능했다** 〔운영자가 오늘 그 자리에서 막혔다〕.
       * ⚠ 여러 줄 칸은 **가릴 수가 없다**(textarea 에는 `type="password"` 가 없다). 감추는
       *   대신 **보인다고 말한다** — 조용히 드러내는 것보다 낫다.
       */
      secretVisible: 'This kind needs several lines, so it cannot be masked — the value is visible while you paste it.',
      /*
       * ★★★ **머리글·꼬리글까지 넣으라고 말한다** (지시176 보완, 운영자 실물 2026-09-09).
       *
       * 🔴 운영자가 «그 줄 사이만» 넣었다 — 316자, `-----BEGIN` 이 없는 값이었다.
       *    그럴 만하다: 화면은 「Secret」 이라고만 했고, 사람이 보기에 **비밀은 가운데
       *    그 덩어리**다. 머리글은 «형식» 처럼 보이지 «비밀» 로 안 보인다.
       * ⚠ 그런데 개인키는 **머리글이 형식의 일부**다 — 없으면 어떤 도구도 못 읽는다.
       *   등록에서 400 으로 무는 것(지시177)만으로는 부족하다. **넣기 전에** 말해야 한다.
       */
      secretWholeFile:
        'Paste the whole file, including the “-----BEGIN …-----” and “-----END …-----” lines — they are part of the format, not decoration.',
      /**
       * **어디에 매였나** (지시168).
       *
       * 🔴 종전에는 이 목록이 이름·종류·꼬리 넷만 그렸다 — 그리고 **이름이 거짓말한다**:
       *   실물 `lab-4090-1 bundle key` 는 노드와 무관한 **플랫폼 열쇠**다. 사람이 이름으로
       *   짐작하다 틀린 것을 골랐고 노드에서 `curl 401` 이 났다(지시162).
       * ⚠ **«안 매임» 과 «못 읽음» 을 가른다** — 서버가 칸을 늘 싣기 때문에 빈 것은
       *   «찾아봤고 없다» 다. 그렇게 말한다.
       */
      boundNothing: 'not bound to anything yet',
      boundUse: (use: string, isDefault: boolean) =>
        isDefault ? `${use} (default)` : use,
      boundTarget: (target: string) => `SSH target ${target}`,
      boundNode: (node: string) => `node ${node}`,
      /** ★ 지시178 곁값 — 능력에 물린 자격. 「어느 노드의 무슨 능력」 둘 다 있어야 뜻이 선다. */
      boundAbility: (nodeId: string, ability: string) => `${ability} on ${nodeId}`,
      /*
       * ★★★ **폐기 — 지움이 아니다** (지시178).
       *
       * ⚠ 되돌릴 수 없다 → 확인 창(지시137 ②). ⚠ «지운다» 고 쓰지 않는다 — 행은 남고
       *   그 자격으로 무엇을 했는지는 계속 물을 수 있다. 낱말이 그것을 말해야 한다.
       */
      revoke: 'Revoke…',
      revokeTitle: (name: string) => `Revoke “${name}”?`,
      revokeWhatHappens:
        'The credential stops working immediately. The record stays, so what it was used for can still be looked up — this is not a delete.',
      revokeCannotUndo: 'This cannot be undone. A new credential has to be registered to replace it.',
      revokeBound: 'It is still bound to:',
      revokeConfirm: 'Revoke',
      revokeCancel: 'Cancel',
      revokeWorking: 'Revoking…',
      /** ⚠ 서버가 «어디에 쓰이는지» 를 말한다 — 화면은 그 말을 그대로 보인다. */
      revokeInUse: 'The server refused because something still uses it:',
      /** ⚠ 사유 없이 여는 길은 없다 — 빈 칸이면 강행 단추가 잠긴다. */
      revokeForceLabel: 'Revoke anyway — say why',
      revokeForcePlaceholder: 'e.g. the machine was terminated',
      revokeForceConfirm: 'Revoke anyway',
      revokeFailed: 'Could not revoke: ',
      /** ⚠ 지정은 **누르면 바로** 간다 — 되돌릴 수 있고(다시 지정), 기계를 안 만진다. */
      designate: 'Use as…',
      designating: 'Setting…',
      designateFailed: 'Could not set the use: ',
      last4: 'Last 4',
      /*
       * ★★ **아래를 표로 바꾼다** (지시176). 종전에는 `<ul>` 한 줄에 이름·종류·꼬리넷·
       * 매인 곳·쓰임이 ` · ` 로 이어져 있었다 — **세로로 견줄 수가 없었다.**
       * 자격은 «어느 것이 어디에 매였나» 를 훑는 것이 일이라, 훑기가 안 되면 판이 안 된다.
       */
      columnName: 'Name',
      columnKind: 'Kind',
      columnBound: 'Bound to',
      columnUse: 'Use',
      columnActions: 'Actions',
    },
    datasources: {
      /* 지시184 — 칸마다 보이는 이름. */
      fieldName: 'Name',
      fieldTable: 'Table',
      fieldColumns: 'Columns',
      /*
       * ★ **기계 이름만 보이던 것을 고친다** (결정5430) — 자격증명(결정5413)과 같은 자리.
       * 〔글로 읽어 알았다〕 드롭다운이 `postgres`·`http`·`internal_runs` 로만 떴다.
       * ⚠ 괄호 안에 기계 이름을 남긴다 — 계약·선언이 그 낱말을 쓴다.
       * ⚠ `Record<string,string>` 이라 **모르는 종류는 그대로** 뜬다(지시107).
       */
      /**
       * **고치기·치우기** (gui 결정8413). 종전에는 **만들 수만 있었다** —
       * `DELETE /provision/targets/{}` 가 「치울 길이 없다」로 데인 것과 같은 자리다(결정6464).
       */
      rename: 'Rename',
      renamePrompt: (name: string) => `New name for ${name}`,
      remove: 'Remove',
      /** ⚠ 되돌릴 수 없다 — **무엇이 없어지는지** 말하고 묻는다. */
      confirmRemove: (name: string) =>
        `Remove the datasource “${name}”? Anything that reads through it stops working. This cannot be undone.`,
      /** ★ 409 는 «실패» 가 아니라 «지금은 안 된다» — 쓰는 데를 먼저 옮겨야 한다. */
      removeInUse: 'Something still reads through that datasource, so it cannot be removed yet.',
      removeFailed: 'Could not remove that datasource',
      renameFailed: 'Could not rename that datasource',
      kindNames: {
        postgres: 'PostgreSQL (postgres)',
        http: 'HTTP endpoint (http)',
        internal_runs: 'This platform’s own runs (internal_runs)',
      } as Record<string, string>,
      title: 'Datasources',
      namePlaceholder: 'e.g. runs-history',
      kindLabel: 'Datasource kind',
      credentialLabel: 'Credential',
      credentialRequired: 'A postgres datasource requires a credential.',
      credentialNone: '— none —',
      tablePlaceholder: 'Table, e.g. notes',
      columnsPlaceholder: 'Columns, comma separated, e.g. title,body',
    },
    toolDefs: {
      /* 지시184 — 칸마다 보이는 이름. */
      fieldName: 'Name',
      fieldSourceDir: 'Source directory',
      fieldEntrypoint: 'Entrypoint',
      title: 'Tools (MCP)',
      note:
        'Only directories the server allows can be registered. Registration runs the server once to read its tool list.',
      disabled:
        'Tool registration is off on this server. An operator must set MCP_TOOL_ROOTS to the directories that may be registered.',
      sourceDirPlaceholder: 'Source directory, e.g. /srv/tools/wordcount',
      entrypointPlaceholder: 'Entrypoint, e.g. server.py',
      namePlaceholder: 'e.g. wordcount',
      rejected: 'Rejected',
      verified: 'Verified',
    },
    agentDefs: {
      /* 지시184 — 칸마다 **보이는** 이름. placeholder 는 보기일 뿐 이름이 아니다. */
      fieldName: 'Name',
      fieldDescription: 'Description',
      fieldInstruction: 'Instruction',
      title: 'Agent definitions',
      namePlaceholder: 'e.g. summarizer',
      descriptionPlaceholder: 'e.g. Summarises an incoming email thread',
      instructionPlaceholder: 'e.g. Summarise the input in three bullet points.',
      providerLabel: 'Model',
      /*
       * **저장되기 전에 말한다** (LLM 결정2564·2574 실측 → 결정414).
       *
       * 음성 데모의 정의가 `{{input.text}}` 없이 저장됐고, codegen 은 틀이 없으면
       * **입력을 버리도록 굽는다**(`_input`). 오류가 안 난다 — 실행은 성공하고
       * TTS 가 *"원문을 제공해 주시면…"* 을 소리 내어 읽었다.
       *
       * ⚠ **막지 않는다.** 입력을 안 쓰는 정의도 정당하다. 알리기만 한다.
       *
       * ⚠ **되는 방향으로만 단정한다**(LLM 이 못 박은 제약). 칸 이름까지는 못 본다 —
       * `{{input.없는칸}}` 도 빈 값이 되지만 그건 실행 때 값이다. 그래서 문구가
       * *"안 된다"* 가 아니라 **"이 자리표는 풀리지 않는다"** 다.
       */
      noInputTemplate:
        'This prompt does not read the input — without {{input.…}} the incoming text is dropped.',
      unresolvedTemplate: (paths: string) =>
        `These placeholders will not resolve and become empty: ${paths}`,
      /** ⚠ 위와 같은 부류다(결정3475) — 못 읽은 것에 «없다 + 등록해라» 를 말하지 않는다. */
      providerUnreadable: 'Could not load the model catalog — try reloading. This does not mean no provider is registered.',
      providerNone: 'No models available — register an LLM provider first.',
      /*
       * ★ **«그 기계에 아직 안 깔렸다»** (지시110, 결정6458).
       *
       * 이 물음은 «부를 수 있나»(`runnable`)와 **다른 것**이다 — 공급자는 멀쩡히
       * 부를 수 있는데 그 서버에 **그 모델만** 없을 수 있다. 지시110 의 실제 상황이
       * 그것이었다(`Qwen3.5-122B-A10B` 가 카탈로그엔 있고 기계엔 없었다).
       *
       * ⚠ **판정은 서버가 한다** — `drift.onlyInCatalog` 를 그대로 읽는다.
       *   콘솔이 카탈로그와 서버 목록을 견줘 다시 셈하면 판정이 두 벌이 된다(결정423).
       */
      modelNotOnServer: (model: string) =>
        `“${model}” is in the catalog but not on that server yet — a run would fail until it is pulled.`,
      /** ⚠ **못 물어본 것은 «다 있다» 가 아니다**(결정3480). 사유를 함께 말한다. */
      serverModelsUnknown: 'Could not check what that server actually has — this does not mean the model is missing.',
      /*
       * **스키마를 적을 수 있게 한다** (결정6453).
       *
       * 이 폼은 `inputSchema`/`outputSchema` 를 `{ "type": "object" }` 로 **박아서**
       * 보내고 있었다 — 사람은 아무것도 못 적었고, 계약이 받는 자연어 사양문
       * (`inputSpecText`·`outputSpecText`)도 안 실렸다. `AgentEditor` 는 그 사양문을
       * 그리는데, **콘솔로 등록한 정의에서는 영원히 비어 있었다**〔실측〕.
       */
      inputSpecLabel: 'What this agent receives (plain words)',
      inputSpecPlaceholder: 'e.g. The full text of one email thread, plus the sender name.',
      outputSpecLabel: 'What this agent returns (plain words)',
      outputSpecPlaceholder: 'e.g. Three bullet points and a one-line subject.',
      inputSchemaLabel: 'Input schema (JSON)',
      outputSchemaLabel: 'Output schema (JSON)',
      /** 왜 기본이 이것인지 **말해 준다** — 안 그러면 «비워도 되는 칸» 으로 읽는다. */
      schemaHint:
        '{"type":"object"} accepts anything. Narrow it so a caller finds out at registration time, not at run time.',
      /** 초안 단추. **모델이 짓는 것**이라 시간과 돈이 든다는 것을 이름이 말한다. */
      draftSchemas: 'Draft both schemas with this model',
      drafting: 'Asking the model…',
      /** ⚠ 「저장되지 않는다」를 **먼저** 말한다 — 눌러도 아무것도 안 굳는다. */
      draftHint:
        'The model writes a first draft from the two descriptions above. Nothing is saved — read it and change what is wrong before you register.',
      draftNeedsModel: 'Pick a model first — the draft is written by that model.',
      draftNeedsSpec: 'Describe the input or the output above first; there is nothing to draft from.',
      /** 못 쓴 JSON 은 **등록을 막는다** — 서버가 400 을 주기 전에 여기서 말한다. */
      schemaNotJson: (which: string) => `The ${which} schema is not valid JSON, so this cannot be registered yet.`,
    },
  },

  settings: {
    /**
     * **지금 도는 판** (지시37, 결정8457).
     *
     * ⚠ 문구가 «서버» 라고 **못박는다** — 콘솔 번들은 그 서버가 제 나무에서 내주는 것이라
     *   대개 같은 나무지만 **구운 시각은 다를 수 있다.** 「이것이 곧 화면의 판이다」로 읽히면
     *   그게 또 하나의 조용한 거짓이 된다.
     * ⚠ `unknown` 은 **「모른다」**다 — 「없다」가 아니다(계약이 `nullable` 로 뒀다).
     */
    runningBuild: {
      heading: 'Running server build',
      intro:
        'What the API server behind this console is running. Use it when a screen or a field seems missing: the build may simply be older than the change.',
      commit: 'Commit',
      builtAt: 'Built at',
      startedAt: 'Started at',
      /* 계약이 이 라우트를 «나이와 커밋» 이라 적었다 — 시각과 나이는 다른 물음이다(결정8466). */
      uptime: 'Up for',
      nodeVersion: 'Node',
      unknown: 'not known to the server',
      loadError: 'Could not read the running build',
    },
    // 지시48 ④ — 편집기 작업 취향. 그래프가 아니라 사람의 것이다(결정185).
    editorPrefs: {
      heading: 'Pipeline editor',
      autosaveEnabled: 'Auto-save definitions and structure',
      autosaveEnabledHint:
        'Periodically saves the pipeline the same way the Save button does. It overwrites the current version — history only grows when you use Save As.',
      autosaveInterval: 'Auto-save every',
      seconds: (n: number) => `${n}s`,
      layoutImmediate: 'Save layout immediately',
      layoutImmediateHint:
        'Card positions and box sizes are saved as you move them, without pressing Save. Turn this off to make layout wait for Save too.',
      savedNote:
        'Stored in this browser only — these are working preferences, not part of the pipeline.',
    },
    tailscale: {
      /* 지시184 — 칸의 보이는 이름. */
      fieldTailnet: 'Tailnet',
      fieldApiKey: 'API key',
      title: 'Tailscale',
      note:
        'The console reads the tailnet device list from the tailscale CLI on this host. An API key does not replace it — probing a node still needs this host to be on the tailnet — but if the CLI is missing or logged out, a stored key lets the console still list devices instead of failing outright.',
      tailnetPlaceholder: 'Tailnet, e.g. example.com (or - for the key’s default)',
      apiKeyPlaceholder: 'API key (write-only)',
      save: 'Save and re-scan',
      savedRediscovering: 'Saved. Re-scanning nodes…',
      last4: 'Last 4',
      /**
       * ⚠ **못 읽은 것을 «저장된 것이 없다» 로 말하지 않는다** (결정3478).
       *
       * 아래는 **저장 상태에 대한 단정**이다. 질의가 실패했을 때 그 문장이 뜨면 운영자는
       * 열쇠를 **다시 넣는다** — 이미 있는 자격증명이 하나 더 생긴다. 지시58 사실3
       * (같은 기계를 두 번 등록)과 같은 부류인데 **여기서 되풀이되는 것은 비밀**이다.
       */
      unreadable: 'Could not load stored credentials — try reloading. This does not mean no key is stored.',
      none: 'No Tailscale API key stored. The CLI is the only source right now.',
    },
    heading: 'Settings',
    apiKeys: {
      /* 지시184 — 칸의 보이는 이름. */
      fieldName: 'Key name',
      /* 목록이 실패해도 **빈 표**만 보였다 — 열쇠가 없는 것과 못 읽은 것은 다르다. */
      loadError: 'Could not load API keys',
      /* 지시183 — 불러오는 동안과 0개를 갈라 말한다. 머리만 남은 표는 둘 중 무엇인지 모른다. */
      loading: 'Reading the keys…',
      none: 'No API keys have been issued yet.',
      title: '(a) API key management',
      namePlaceholder: 'Key name (e.g. ops-laptop)',
      issue: 'Issue',
      copyNowWarning: 'You will not be able to see this again once you leave this screen — copy it now.',
      gotItClose: 'Got it, close',
      /* **이 열쇠가 무엇을 할 수 있나** (결정1438). 폐기를 고르는 화면이라 필요하다. */
      columns: { name: 'Name', key: 'Key (masked)', scope: 'Scope', lastUsed: 'Last used', status: 'Status' },
      revoked: (when: string) => `Revoked (${when})`,
      active: 'Active',
      revokeNow: 'Revoke now',
      createFailed: 'Key issuance failed: ',
      revokeFailedPrefix: 'Key revocation failed — ',
      revokeFailedStillActive: 'that key is still active.',
    },
    deployTargets: {
      title: '(b) Deploy target whitelist',
      columns: { target: 'Target', kind: 'Kind', ref: 'Ref', allowed: 'Allowed' },
      /*
       * ★ **종류마다 이름을 둔다** (결정5403). 종전엔 이름이 둘뿐이라 화면이
       * `repo` 냐 아니냐로 갈랐고, `robot`·`robot_sim` 이 **«Node» 로 적혔다.**
       *
       * ⚠ `satisfies Record<DeployTargetKind, string>` 이라 **계약에 종류가 늘면
       * 여기서 컴파일이 깨진다** — 조용히 틀린 이름을 붙이는 것보다 낫다(지시107).
       */
      /** 못 읽었을 때. **빈 표는 «허용된 것이 없다» 로 읽힌다** — 그건 다른 말이다. */
      loadError: 'Could not load the deploy-target whitelist',
      /*
       * ★ 지시183 — **이 표는 안전장치3 의 화이트리스트다.** 머리만 남은 표는
       *   «허용된 것이 없다» 로 읽힌다. 그래서 불러오는 중과 **정말 비었음**을 갈라 말하고,
       *   정말 비었으면 그 뜻을 **글로** 적는다(빈칸에 맡기지 않는다).
       */
      loading: 'Reading the whitelist…',
      none: 'Nothing is on the whitelist — every deploy target is refused.',
      kindLabel: {
        repo: 'Repo (path A)',
        node: 'Node (path B)',
        robot: 'Robot',
        robot_sim: 'Robot (simulator)',
      } satisfies Record<DeployTargetKind, string>,
      allowed: 'Allowed',
      blocked: 'Blocked',
      toggleFailedPrefix: 'Allow/block change failed — ',
      toggleFailedNoChange: 'the state did not change.',
    },
    globalStop: {
      /* 지시184 — 칸의 보이는 이름. 무엇을 쳐야 하는지는 옆 글이 말한다. */
      fieldConfirm: 'Type to confirm',
      title: '(d) Global stop switch',
      currentStatus: 'Current status: ',
      stopped: 'Stopped',
      runningNormally: 'Running normally',
      /*
       * **모르는 것을 정상이라고 말하지 않는다** (결정489). 이 판은 플랫폼의 비상
       * 제동이고, 틀리는 방향이 한쪽으로 치우쳐 있다 — 멈췄는데 "정상" 이라고 하면
       * 사고 중에 사람이 다른 데를 본다.
       *
       * 조회 중과 못 읽음을 가른다: 앞은 기다리면 되고, 뒤는 왜 못 읽는지를 봐야 한다.
       */
      statusLoading: 'Checking…',
      statusUnreadable: 'Cannot read the switch — this is not "running normally"',
      lastChanged: (when: string) => ` · Last changed ${when}`,
      /**
       * **누가 걸었는가** (결정465).
       *
       * 전체 중단은 이 플랫폼에서 권한이 제일 센 스위치다. 무인 운행에서 그것이 걸린
       * 것을 보면 제일 먼저 묻는 것이 *"누가 멈췄나"* 이고 — 모르면 **풀어도 되는지
       * 판단할 수 없다.** 남이 무언가를 막으려고 건 것을 모르고 풀면 그 사람이 막으려던
       * 일이 그대로 일어난다.
       *
       * 시각만 보이면 *"언제"* 는 알고 *"누가"* 는 모른다. 둘 다 있어야 판단이 선다.
       */
      changedBy: (who: string) => `Changed by ${who}.`,
      confirmPrompt: 'Type the confirmation phrase to immediately stop all unattended runs: ',
      confirmWord: 'STOP',
      stopEverything: 'Stop everything',
      resume: 'Resume',
      failedPrefix: 'Global stop change failed — ',
      failedNoChange: 'the state did not change.',
    },
    pipelineLimits: {
      /* 지시184 — 칸의 보이는 이름. */
      fieldPipeline: 'Pipeline',
      title: '(c) Pipeline cost & retry limits',
      /* 조회가 실패하면 **폼이 통째로 사라졌다** — 사용자는 그런 파이프라인이 없는 줄 안다. */
      loadError: 'Could not load the limits for this pipeline',
      pipelineNamePlaceholder: 'Pipeline name',
      lookUp: 'Look up',
      /*
       * **하나일 때는 이름을 외워 쳐도 됐다** (지시120, 결정6454).
       *
       * 이 칸은 `useState('code-task')` 로 **이름 하나를 박아** 두고 나머지는 사람이
       * 외워 치게 했다. 파이프라인이 하나이던 때는 맞았다 — 지금은 여럿이고,
       * 그 하나는 이 설치에 **아예 없을 수도** 있다. 그러면 화면이 조회 실패로 열린다.
       *
       * ⚠ 자유 입력을 **없애지 않는다.** 예약 이름(`status-check`·`code-task`·
       * `paseo-agents`)은 `pipelines` 표에 행이 없어 아래 목록에 **안 뜬다**(결정377).
       * 고를 수 있게만 더하고, 치는 길은 남긴다.
       */
      pipelineHint:
        'Pick a registered pipeline, or type a name. Built-in names (status-check, code-task, paseo-agents) are not in the list — they have no pipeline row.',
      /** ⚠ 목록을 **못 읽은 것**은 «등록된 것이 없다» 가 아니다(결정3480). 치는 길은 살아 있다. */
      knownPipelinesUnreadable: 'Could not load the list of registered pipelines — you can still type a name.',
      /*
       * ⚠ **문구가 낡아 있었다** (지시134, 결정6456). «0 not allowed» 만으로는
       * «그럼 상한을 없애려면 뭘 넣나» 에 답하지 못한다 — 답은 **비우는 것**이다.
       */
      dailyCostCap: 'Daily cost cap (USD) — leave blank for no cap. 0 is not allowed.',
      /** 빈 칸의 뜻을 칸 안에서도 말한다 — 라벨을 안 읽고 지나가는 사람이 있다. */
      dailyCostCapNone: 'no cap',
      maxRetries: 'Max retries',
      maxLoops: 'Max loops',
      /*
       * **폭주 방어**(결정471). 온프렘은 단가가 0 이라 일일 비용 상한이 안 걸린다 —
       * 그때 실행 하나의 호출 수를 막는 것은 이 칸뿐이다(결정574·578).
       */
      maxCallsPerRun: 'Max calls per run (runaway guard)',
      paused: 'Paused',
      /*
       * **왜 멈췄는지** (결정444). 킬스위치(`tripKillSwitch`, 안전장치 R5)가 파이프라인을
       * 멈출 때 사유를 함께 저장한다 — 일일 비용 상한을 넘었다든지. 그런데 화면은
       * `paused` 체크상자만 그려서, 운영자는 **왜 멈췄는지 모른 채 체크를 풀 수 있었다.**
       * 플랫폼이 스스로 멈춘 것을 사람이 모르고 되돌리는 자리라 조용하면 안 된다.
       */
      pausedReason: (reason: string) => `Paused by the platform — ${reason}`,
      save: 'Save',
      killNow: 'Kill now',
      killReason: 'Manual kill switch triggered from console',
      saveFailed: 'Save failed: ',
      saved: 'Saved',
      /*
       * **내가 안 건드린 칸이 같이 바뀌었다** (결정470). 요청 본문이 `maxCallsPerRun`
       * 을 못 받는데 서버 upsert 는 그 열을 덮어쓴다 — 비용 상한 한 줄을 고치러 온
       * 사람이 폭주 방지 상한을 같이 잃는다(결정469).
       *
       * 숫자를 문장에 박지 않는다. **서버가 응답으로 말한 값**을 그대로 옮긴다.
       */
      callCapReset: (from: number, to: number) =>
        `Heads up — the per-run call cap changed from ${from} to ${to}. You did not set this: ` +
        `the save request has no field for it, so the server rewrote it to its default. ` +
        `It cannot be restored from this screen.`,
      killFailed: 'Kill switch failed: ',
      killed: 'Kill switch triggered',
    },
  },
  /** 구성원·초대 (지시54). */
  access: {
    /*
     * ★ **권한 축이 무엇을 여는지 말한다** (결정5422).
     *
     * 〔글로 읽어 알았다〕 초대 폼이 `pipeline.read`·`access.manage`·`admin` 을
     * **이름만** 체크상자로 늘어놓는다. 사람이 권한을 **주는** 자리인데
     * 무엇을 주는지 화면이 말하지 않는다 — 모르고 주면 과하게 준다.
     *
     * 뜻은 **내가 지어내지 않았다.** `docs/07-org-authz.md` §1 의 표를 옮겼다.
     *
     * ⚠ **`admin` 은 수로 재면 안 된다.** 라우트 표를 세면 `access.manage` 44 ·
     * `admin` **1** 이 나와 admin 이 약해 보인다. 그 문서가 그 함정을 적어 뒀다 —
     * *"`admin` 은 **다른 것을 포함한다.** 검사에서 `admin` 이면 무조건 통과시킨다"*.
     * 그래서 수를 안 보이고 **포함한다는 사실**을 말한다.
     *
     * ⚠ 이름표가 없는 축도 체크상자는 **그대로 뜬다**(이름으로 물러남) — 서버가 축을
     * 늘려도 화면이 죽지 않는다. 대신 검사가 «뜻 없는 축» 을 붉힌다(지시107 의 결).
     */
    axisMeaning: {
      'pipeline.read': 'View pipelines and graphs',
      'pipeline.create': 'Create new pipelines',
      'pipeline.update': 'Edit them — save, publish, roll back',
      'pipeline.delete': 'Delete them',
      'access.manage': 'Set up credentials, node access and deploy targets',
      'member.manage': 'Invite, remove and set permissions',
      admin: 'All of the above, plus organisation settings — admin passes every check',
    } as Record<string, string>,
    /**
     * 제거된 구성원 되살리기 (engine 결정1307, 화면은 결정459).
     *
     * **계약 한 줄이 화면에 그대로 나와야 한다:** 되살리기는 사람을 조직에 돌려놓을
     * 뿐이고 **권한은 다시 줘야 한다.** 안 말하면 *"되살렸는데 왜 안 되나"* 가 그대로
     * 문의가 된다(engine 이 미리 짚었다).
     */
    /**
     * 감사 기록 (지시56 §4-2-1, engine 결정1309, 화면은 결정461).
     *
     * 콘솔이 두 자리에서 *"감사에 남는다"* 라고 **약속만 하고 있었다** — 웹 폼 제출
     * 문구와 구성원 제거 확인 문구다. 볼 길이 없으면 그 약속은 확인할 수 없다.
     */
    audit: {
      title: 'Audit trail',
      hint: 'Who did what here, newest first. Denied attempts are recorded too — an audit that only kept what succeeded would not be one.',
      /**
       * **잘렸을 수 있다** (gui 결정8496). 실행 목록의 `runs.maybeTruncated` 와 **같은 손**이다 —
       * 두 표가 같은 사실을 다른 말로 하면 사람이 다른 것으로 읽는다.
       *
       * ⚠ «N개가 더 있다» 라고 **안 말한다** — 그 수를 우리는 모른다. 모르는 수를 지어내면
       * 그것이 또 하나의 조용한 거짓이 된다.
       * ★ 이 줄이 필요한 까닭: 지시103·140 을 볼 때 이 표가 «봉투가 왔나» 의 **유일한 답**이라,
       *   잘린 줄 모르면 사람이 «그 일은 안 일어났다» 로 읽는다.
       */
      maybeTruncated: (shown: number) =>
        `Showing the ${shown} most recent entries. There may be older ones that are not listed.`,
      /*
       * **빈 것과 안 배선된 것은 다르다.** 서버가 그 둘을 501 로 갈라 준다
       * (`routes/audit.ts`: *"빈 목록으로 답하지 않는다"*). 화면이 접으면 그 구분이 사라진다.
       */
      empty: 'Nothing has been recorded yet.',
      notWired: 'This deployment does not expose the audit trail (the server answered 501). Nothing is wrong with your permissions — the read side is not wired here.',
      colWhen: 'When',
      colWho: 'Who',
      colAction: 'Action',
      colTarget: 'Target',
      colOutcome: 'Outcome',
      /*
       * **누구인지 모를 수 있다.** 인증 전에 거부된 것은 사용자가 없다 — 화면이
       * 이름을 지어내면 안 된다(`userId` 가 선택인 이유).
       */
      unknownWho: '—',
      /** 상세는 접어 둔다. 한 줄에 다 펴면 표가 못 읽게 된다. */
      showDetail: 'Details',
      /**
       * **거부가 몇 건인지** (티켓56 gui 확인, 결정484).
       *
       * 거부 줄은 붉게 그린다. 다만 색은 줄을 하나씩 볼 때 드러나는 신호이고, 표를
       * 훑는 사람에게는 *"거부가 있었나"* 가 여전히 눈으로 세는 일이었다.
       * 지시88 ④ 의 잣대가 여기에도 걸린다 — **평상시와 같은 모양이면 아무도 안 본다.**
       *
       * 분모를 함께 말한다. "3건 거부" 만으로는 그것이 스무 건 중 셋인지 넷 중 셋인지
       * 모르고, 그 차이가 사람이 놀랄지 말지를 가른다.
       */
      deniedCount: (denied: number, total: number) =>
        `${denied} of the ${total} entries shown were denied.`,
      /*
       * **어디서 했는지도 감사다** (결정463). 같은 계정이라도 낯선 곳에서 온 것인지가
       * 감사에서 묻는 것이다. 표에 두면 칸이 넘쳐 못 읽으니 접힌 자리에 둔다.
       */
      fromIp: (ip: string) => `From ${ip}`,
      fromAgent: (agent: string) => `Client: ${agent}`,
      denied: 'denied',
    },

    restore: {
      title: 'Removed members',
      /*
       * **살아 있는 목록과 섞지 않는 이유**를 여기서 한 번 말한다. 별도 구역인 것이
       * 눈에 보이지만, 왜 별도인지는 문장이 있어야 안다.
       */
      hint: 'People who were removed from this organisation. They are not members right now and cannot sign in to it.',
      empty: 'Nobody has been removed.',
      removedAt: (when: string) => `Removed ${when}`,
      restoreLabel: (name: string) => `Restore ${name}`,
      restoring: 'Restoring…',
      /*
       * **되살린 직후 그 사람은 아무것도 못 한다.** 이 문장이 이 화면의 존재 이유에
       * 가깝다 — `permissions: []` 를 숨기면 계약이 화면에서 사라진다.
       */
      restoredNoPermissions: (name: string) =>
        `${name} is back in the organisation with no permissions at all — restoring returns the person, not what they could do. Grant permissions in the table above before they can do anything.`,
      /*
       * 409 — 나간 사이에 다른 사람이 그 이름을 가져갔다. **그때만 이름을 묻는다.**
       * 처음부터 요구하면 되살리기와 개명이 한 문에 섞인다.
       */
      nameTakenTitle: 'That username is taken now',
      nameTakenHint:
        'Someone else took this username while they were removed. Pick a different one to restore them under.',
      newNameLabel: 'New username',
      confirmRestore: 'Restore',
      cancel: 'Cancel',
    },
    title: 'Access',
    noPermission:
      'You do not have permission to manage members. Ask an admin if you need it — the server is what refuses, so this page would not work even if it were shown.',
    membersTitle: 'Members',
    membersHint:
      'Every axis is sent on save — this is not a partial update. Hiding a control here is convenience, not security: the server is what actually refuses.',
    colMember: 'Member',
    colActions: '',
    you: ' (you)',
    save: 'Save',
    saving: 'Saving…',
    remove: 'Remove',
    /*
     * **"for good" 이 아니다** (결정366 고침). 소프트 삭제라 행은 남는다 — 그렇게
     * 적어 두면 사용자가 감사 기록에서 제 이름을 보고 "안 지워졌다" 고 읽는다.
     */
    removeYes: 'Remove member',
    removing: 'Removing…',
    /*
     * 확인 문구가 **실제로 일어나는 일**을 말한다 (통합 브리프
     * `integ-to-gui-member-remove-is-fixed.md`).
     *
     *   접근이 끊긴다        로그인도, 이 조직의 자원도
     *   되돌리는 길이 없다    권한까지 거둬지므로 다시 부르고 다시 줘야 한다
     *   이름은 풀린다        부분 유일 인덱스로 바뀌어 그 이름을 다시 쓸 수 있다
     *
     * 되돌릴 수 없다는 것을 **묻기 전에** 말한다 — 누른 뒤에 알리면 늦다.
     */
    removeConfirm: (name: string) =>
      `Remove ${name}? They can no longer sign in or reach anything here, and there is no undo from this screen — you would have to add them again and re-grant every permission. Past audit entries keep their name, and the username becomes free to reuse.`,
    cancel: 'Cancel',
    axisHint: (axis: string) => `Permission axis '${axis}'. Admin always passes.`,
    /*
     * 거절 둘 (통합 브리프 ②). 둘 다 409 라 **`error.code` 로 갈랐다** — 사람이 할 일이
     * 다르므로 다음 걸음까지 말한다.
     */
    rejectLastAdmin:
      'This is the last admin. Make someone else an admin first, then come back — otherwise nobody could grant permissions again.',
    rejectSelfElevation:
      'You cannot change your own permissions. Ask another admin to do it.',

    invitesTitle: 'Invitations',
    invitesHint: 'People join by invitation. There is no self-signup yet.',
    inviteEmail: 'Email',
    inviteCreate: 'Send invitation',
    inviteCreating: 'Creating…',
    /*
     * 토큰은 **한 번만** 나간다 (통합 브리프 ①). 목록에는 없고 다시 볼 수 없으므로
     * 그 자리에서 복사시키고, 새로 고치면 사라진다는 것도 말해야 한다.
     */
    inviteTokenTitle: 'Copy this link now',
    inviteTokenOnce:
      'This is the only time the token is shown. Refresh or close this and it is gone — you would have to issue a new invitation.',
    inviteCopy: 'Copy',
    inviteCopied: 'Copied',
    inviteDone: 'Done',
    inviteRevoke: 'Revoke',
    inviteRevoking: 'Revoking…',
    colEmail: 'Email',
    colState: 'State',
    colExpires: 'Expires',
    /*
     * 네 갈래를 한 배지로 뭉치지 않는다 — "내가 취소한 것" 과 "시간이 지난 것" 은
     * 사람이 할 일이 다르다(다시 보내기 vs 왜 안 썼는지 묻기).
     */
    inviteStateLabel: {
      live: 'waiting',
      accepted: 'accepted',
      revoked: 'revoked',
      expired: 'expired',
    },
    invitesEmpty: 'No invitations yet.',

    /** 수락 화면 — 로그인 밖이다(통합 브리프 ④). */
    acceptTitle: 'Accept your invitation',
    acceptUsername: 'Choose a username',
    acceptPassword: 'Choose a password',
    acceptSubmit: 'Create account',
    acceptSubmitting: 'Creating…',
    acceptDone: 'Your account is ready. Sign in to continue.',
    acceptNoToken:
      'This link is missing its invitation token. Use the full link you were sent.',
    /*
     * **사유를 가르지 않는다.** "없는 토큰" 과 "만료된 토큰" 이 같은 문구로 온다 —
     * 일부러 그렇게 한 것이므로 화면도 갈라 추측하지 않는다.
     */
    acceptRejected: 'This invitation cannot be used. Ask whoever invited you for a new link.',
    acceptRateLimited: (seconds: number) =>
      `Too many attempts. Try again in ${seconds} seconds.`,
    acceptPasswordShort: 'Use at least 8 characters.',
    acceptGoSignIn: 'Go to sign in',
  },

};
